import {
  getLessonPrice,
  LESSON_PRICING,
  LESSON_LOCATIONS,
  parseLessonCatalog,
  setLessonCatalog,
  lessonLocationLabel,
  lessonPriceMatrixForPeople,
  lessonStartTimesForLocation,
  lessonEndTimesForLocation,
} from '../../../js/lessonPricing.js';
import { resolveCoursePackages, findCoursePackage, normalizeMealOptions } from '../../../js/coursePackages.js';
import {
  upsertDraft,
  getDraftForPage,
  removeDraft,
  hydrateContact,
  setNamedField,
  applyUrlOccupancyToTravelDetails,
} from '../../../js/bookingDraft.js';
import { readChildrenAgesFromParams } from '../../../js/offerSearchParams.js';
import {
  destroyPolishDateMask,
} from '../../../js/polishDateMask.js';
import {
  createBookingPhoneMask,
  isCompleteBookingPhone,
  bookingPhoneUnmasked,
  setBookingPhoneCountry,
  bookingPhoneValidityMessage,
  getBookingPhoneRule,
} from '../../../js/bookingPhoneMask.js';
import {
  attachBirthDatePicker,
  destroyBirthDatePicker,
  validateBirthDatePicker,
} from '../../../js/birthDatePicker.js';
import { initStickySummaryBar } from '../../../js/stickySummaryBar.js';
import { getTurnstileConfig, mountTurnstile, revealTurnstile } from '../../../js/turnstile.js';

const LESSON_MAX_CHILD_AGE = 18;

const getLessonChildMinBirthDate = () => {
  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);
  minDate.setFullYear(minDate.getFullYear() - LESSON_MAX_CHILD_AGE);
  return minDate;
};

const polishBirthToIso = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parts = raw.split('.');
  if (parts.length !== 3 || parts[2].length !== 4) return raw;
  const day = parts[0].padStart(2, '0');
  const month = parts[1].padStart(2, '0');
  return `${parts[2]}-${month}-${day}`;
};

const isoBirthToPolish = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) return raw;
  const parts = raw.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return raw;
};

const COURSE_LOCATIONS = {
  ...LESSON_LOCATIONS,
  pellegrino: 'San Pellegrino, Włochy',
};

const parseSchoolCourseCatalog = (root) => {
  const raw = root?.dataset?.schoolCourseCatalog || '';
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const findCatalogSlope = (root, locationKey) => {
  const catalog = parseSchoolCourseCatalog(root);
  const loc = String(locationKey || '');
  return catalog.find((slope) => slope.id === loc || slope.destination === loc) || catalog[0] || null;
};

const courseLocationLabel = (root, locationKey) => {
  const slope = findCatalogSlope(root, locationKey);
  if (slope?.label) return slope.label;
  const fallback = COURSE_LOCATIONS[locationKey] || locationKey || '';
  return locationKey === 'harenda' && fallback && !String(fallback).includes('Polska')
    ? `${fallback}, Polska`
    : fallback;
};

const formatCourseMoney = (value) => Number(value).toLocaleString('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCourseMealRateDays = (pricePerDay, days, { withPlus = false } = {}) => {
  const rate = Math.max(0, Number(pricePerDay) || 0);
  const n = Math.max(0, Number(days) || 0);
  if (!rate || !n) return '';
  const prefix = withPlus ? '+' : '';
  const dayWord = n === 1 ? ui('dzień') : ui('dni');
  return `${prefix}${rate} ${ui('zł / dzień')} × ${n} ${dayWord}`;
};

const formatCourseMealLineDetail = (meals = []) => {
  const list = Array.isArray(meals) ? meals : [];
  const amount = list.reduce((sum, meal) => sum + (Number(meal.price) || 0), 0);
  if (amount <= 0) return '';
  const rate = list.reduce((max, meal) => Math.max(max, Number(meal.price_per_day) || 0), 0);
  const days = list.reduce((sum, meal) => sum + (Number(meal.days) || 0), 0);
  const name = (() => {
    for (const meal of list) {
      const label = String(meal.label || '').trim();
      if (label && label.toLowerCase() !== 'tak') return label;
    }
    return ui('Obiad');
  })();
  const ratePart = formatCourseMealRateDays(rate, days);
  return ratePart ? `${name}, ${ratePart}` : name;
};

const formatCourseMealChoicePrice = (meal) => {
  const rate = Number(meal?.price_per_day) || 0;
  const days = Number(meal?.days) || 0;
  const ratePart = formatCourseMealRateDays(rate, days, { withPlus: true });
  if (ratePart) return ratePart;
  const price = Number(meal?.price) || 0;
  return price > 0 ? `+${formatCourseMoney(price)} zł` : '';
};

const formatPrice = (n) => `${Number(n).toLocaleString('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} zł`;

const i18nUi = (key, fallback) => {
  const bag = typeof window !== 'undefined' ? window.hskiI18n : null;
  const value = bag && typeof bag[key] === 'string' ? bag[key] : '';
  return value || fallback;
};

/** Polish msgid → catalog label. Unknown or already-translated text is returned as-is. */
const ui = (msgid) => {
  const text = String(msgid ?? '');
  if (!text) return '';
  const bag = typeof window !== 'undefined' ? window.hskiI18n?.msg : null;
  const value = bag && typeof bag[text] === 'string' ? bag[text] : '';
  return value || text;
};

const uiFmt = (msgid, ...vars) => {
  let index = 0;
  return ui(msgid).replace(/%s/g, () => String(vars[index++] ?? ''));
};

/** Display only. Hours and headcount stay numeric; the sentence is the msgid. */
const lessonWithInstructorDetail = (hours, people) => {
  const n = Number(people) || 0;
  if (n === 1) {
    return uiFmt('%sh lekcji dla %s osoby u jednego instruktora', hours, n);
  }
  return uiFmt('%sh lekcji dla %s osób u jednego instruktora', hours, n);
};

const lessonPeopleNominative = (people) => {
  const n = Number(people) || 0;
  if (n === 1) return ui('1 osoba');
  return uiFmt('%s osoby', n);
};

const formatGuestsLabel = (adults, children = 0) => {
  const adultPart = `${adults} ${adults === 1 ? i18nUi('adult_one', 'dorosły') : i18nUi('adult_many', 'dorosłych')}`;
  if (!children) return adultPart;
  const childPart = `${children} ${children === 1 ? i18nUi('child_one', 'dziecko') : i18nUi('child_many', 'dzieci')}`;
  return `${adultPart}, ${childPart}`;
};

/** Prod travel sidebar / summary: "Dorośli: N, Dzieci: M" */
const formatTravelGuestsLabel = (adults, children = 0) => {
  const adultN = Number(adults) || 0;
  const childN = Number(children) || 0;
  const adultWord = adultN === 1 ? i18nUi('adult_one', 'dorosły') : i18nUi('adult_many', 'dorosłych');
  const childWord = childN === 1 ? i18nUi('child_one', 'dziecko') : i18nUi('child_many', 'dzieci');
  const parts = [];
  if (adultN > 0) parts.push(`${adultN} ${adultWord}`);
  if (childN > 0) parts.push(`${childN} ${childWord}`);
  return parts.join(', ') || '—';
};

const ORDERER_MIN_AGE = 18;
const ORDERER_AGE_MESSAGE = () => i18nUi('orderer_age', 'Musisz mieć ukończone 18 lat — sprawdzamy do umowy');

const isAdultBirthDate = (value) => {
  const iso = polishBirthToIso(value);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const birth = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return false;
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() - ORDERER_MIN_AGE);
  limit.setHours(0, 0, 0, 0);
  return birth <= limit;
};

const validateOrdererBirthDate = (inputOrPicker, { required = true } = {}) => validateBirthDatePicker(inputOrPicker, {
  required,
  maxDate: new Date(),
  minAgeYears: ORDERER_MIN_AGE,
  minAgeMessage: ORDERER_AGE_MESSAGE(),
  fieldLabel: ui('Data urodzenia'),
});

const setOrdererBirthError = (scope, message = '') => {
  const errorEl = scope?.querySelector?.('.js-travel-orderer-birth-error');
  const hintEl = scope?.querySelector?.('.js-travel-orderer-birth-hint');
  if (!errorEl) return;
  const text = String(message || '').trim();
  errorEl.textContent = text;
  errorEl.hidden = !text;
  if (hintEl) hintEl.hidden = Boolean(text);
};

const readTravelParticipantBirthValue = (card) => {
  if (!card) return '';
  const index = card.dataset.participantIndex || '1';
  const birth = card.querySelector(`[name="participant-birth-${index}"]`);
  return String(card._birthMask?.value || birth?.value || '').trim();
};

const canBeTravelOrderer = (card) => {
  const birth = readTravelParticipantBirthValue(card);
  if (!birth) return false;
  return isAdultBirthDate(birth);
};

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const parseBookingCopy = (el) => {
  const rootEl = el?.closest?.('.js-booking') || el;
  try {
    const parsed = JSON.parse(rootEl?.dataset?.bookingCopy || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const fillCopy = (template, vars = {}) => {
  let text = String(template ?? '');
  Object.keys(vars).forEach((key) => {
    text = text.split(`{${key}}`).join(String(vars[key] ?? ''));
  });
  return text;
};

const bookingTx = (el, key, fallback = '', vars = {}) => fillCopy(parseBookingCopy(el)[key] ?? fallback, vars);

/** Przybliżona odmiana imienia przez dopełniacz („dla ___”) — Specyfikacja/hski_cennik.html */
const genitiveImie = (name) => {
  if (!name) return name;
  const n = String(name).trim();
  if (!n) return n;
  const exceptions = {
    maria: 'Marii',
    julia: 'Julii',
    amelia: 'Amelii',
    natalia: 'Natalii',
    zofia: 'Zofii',
    lidia: 'Lidii',
    klaudia: 'Klaudii',
    sylwia: 'Sylwii',
    wiktoria: 'Wiktorii',
    oliwia: 'Oliwii',
    eugenia: 'Eugenii',
    aurelia: 'Aurelii',
    kornelia: 'Kornelii',
    patrycja: 'Patrycji',
  };
  const exception = exceptions[n.toLowerCase()];
  if (exception) return n[0] === n[0].toUpperCase() ? exception : exception.toLowerCase();
  if (/a$/i.test(n)) {
    const stem = n.slice(0, -1);
    if (/(sia|cia|dzia|nia)$/i.test(n)) return stem;
    const lastCh = stem.slice(-1).toLowerCase();
    if (lastCh === 'i') return `${stem}i`;
    if (/[kglj]/.test(lastCh)) return `${stem}i`;
    return `${stem}y`;
  }
  if (/ek$/i.test(n)) return `${n.slice(0, -2)}ka`;
  if (/eł$/i.test(n)) return `${n.slice(0, -2)}ła`;
  if (/[bcćdfghjklłmnńprstwzźż]$/i.test(n)) return `${n}a`;
  return n;
};

const formatCourseDate = (iso) => {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const courseDaysInclusive = (fromIso, toIso) => {
  const from = new Date(`${fromIso}T12:00:00`);
  const to = new Date(`${toIso}T12:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 0;
  return Math.round((to - from) / 86400000) + 1;
};

const formatCourseDaysWord = (days) => {
  const n = Number(days) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return '1 dzień';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} dni`;
  return `${n} dni`;
};

/** np. 18.12.2026 — 21.12.2026 (4 dni) */
const formatCourseDateRange = (fromIso, toIso, daysHint = 0) => {
  if (!fromIso || !toIso) return '—';
  const range = `${formatCourseDate(fromIso)} — ${formatCourseDate(toIso)}`;
  const days = Number(daysHint) > 0 ? Number(daysHint) : courseDaysInclusive(fromIso, toIso);
  return days > 0 ? `${range} (${formatCourseDaysWord(days)})` : range;
};

/** Normalizuje zakres dat z sidebara (whitespace / myślniki). */
const normalizeTravelTermLabel = (label) => String(label || '')
  .replace(/\s+/g, ' ')
  .replace(/\s*[-–—]\s*/g, ' — ')
  .trim();

/**
 * Pod etykietą „Pokój” pokazuje samą nazwę.
 * Usuwa też zdublowany rodzaj: „Pokój Pokój Classic” → „Classic”.
 */
const normalizeTravelRoomForReview = (raw) => {
  let s = String(raw || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  s = s.replace(/^(pokój|apartament)\s+\1\b/i, (_, kind) => (
    `${kind.charAt(0).toUpperCase()}${kind.slice(1).toLowerCase()}`
  ));
  const withoutKind = s.replace(/^(pokój|apartament)\s+/i, '').trim();
  return withoutKind || s;
};

const getLessonDatesLabel = (rootEl) => {
  const slotDates = [...rootEl.querySelectorAll('.lesson-schedule__slot')]
    .map((slot) => slot.dataset.date)
    .filter(Boolean);
  const unique = [...new Set(slotDates)].sort();
  if (unique.length) {
    return unique.map(formatCourseDate).join(', ');
  }
  const config = getLessonConfig(rootEl);
  return config.lessonDate ? formatCourseDate(config.lessonDate) : '—';
};

const parseCourseTermOptions = (root) => {
  const raw = root?.dataset.courseTermOptions || '[]';
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getSelectedCourseTermOption = (root) => {
  const options = parseCourseTermOptions(root);
  const params = new URLSearchParams(window.location.search);
  const dateFrom = params.get('date_from') || root?.dataset.dateFrom || '';
  const dateTo = params.get('date_to') || root?.dataset.dateTo || '';
  const course = params.get('course') || root?.dataset.course || '';

  if (dateFrom && dateTo) {
    const byDates = options.find(
      (option) => option.date_from === dateFrom && option.date_to === dateTo,
    );
    if (byDates) return byDates;
  }

  if (course) {
    const byCourse = options.find((option) => option.value === course);
    if (byCourse) return byCourse;
  }

  const select = root?.querySelector('.js-booking-course-type');
  if (select) {
    const selected = options.find((option) => option.value === select.value);
    if (selected) return selected;

    if (select.selectedOptions?.[0]) {
      const optionEl = select.selectedOptions[0];
      return {
        value: optionEl.value,
        label: optionEl.textContent?.trim() || '',
        title: optionEl.dataset.title || optionEl.textContent?.trim() || '',
        date_from: optionEl.dataset.dateFrom || root.dataset.dateFrom || '',
        date_to: optionEl.dataset.dateTo || root.dataset.dateTo || '',
      };
    }
  }

  return options[0] || {};
};

const getCourseConfig = (root) => {
  const params = new URLSearchParams(window.location.search);
  const term = getSelectedCourseTermOption(root);
  const packages = resolveCoursePackages(root);
  const courseLabel = term.title || term.label || root?.dataset.courseLabel || params.get('course_label') || 'Kurs';
  const dateFrom = term.date_from || params.get('date_from') || root?.dataset.dateFrom || '';
  const dateTo = term.date_to || params.get('date_to') || root?.dataset.dateTo || '';
  const packageId = params.get('package') || root?.dataset.package || '';
  const selectedPackage = packages.find((pkg) => pkg.id === packageId) || packages[0];
  const packageLabel = selectedPackage?.label
    || params.get('package_label')
    || root?.dataset.packageLabel
    || 'Pakiet';
  const price = Number(
    selectedPackage?.price
    || params.get('price')
    || root?.dataset.price
    || 950,
  );

  if (dateFrom) root.dataset.dateFrom = dateFrom;
  if (dateTo) root.dataset.dateTo = dateTo;
  if (courseLabel) root.dataset.courseLabel = courseLabel;
  if (selectedPackage?.id) root.dataset.package = selectedPackage.id;
  if (packageLabel) root.dataset.packageLabel = packageLabel;
  root.dataset.price = String(price);

  const location = params.get('location') || root?.dataset.location || 'harenda';
  const offerId = params.get('offer') || root?.dataset.offerId || '';
  const productUrl = root?.dataset.courseProductUrl || '';
  const audience = params.get('audience')
    || root?.dataset.audience
    || ((params.get('course') || root?.dataset.course || productUrl).match(/dorosl|adult/) ? 'adults' : 'children');

  return {
    course: params.get('course') || root?.dataset.course || '',
    course_label: courseLabel,
    course_type: term.value || '',
    package: selectedPackage?.id || packageId,
    package_label: packageLabel,
    price,
    discipline: params.get('discipline') || root?.dataset.discipline || 'ski',
    location,
    location_label: courseLocationLabel(root, location),
    offer_id: offerId,
    audience,
    date_from: dateFrom,
    date_to: dateTo,
    days: Number(term.days) > 0
      ? Number(term.days)
      : courseDaysInclusive(dateFrom, dateTo),
  };
};

const getLessonConfig = (root) => {
  const params = new URLSearchParams(window.location.search);

  return {
    lessonType: root?.querySelector('.js-booking-lesson-type:checked')?.value
      || root?.querySelector('input.js-booking-lesson-type[type="hidden"]')?.value
      || params.get('lesson_type')
      || root?.dataset.lessonType
      || 'single',
    participants: Number(
      root?.querySelector('.js-booking-lesson-participants')?.value
      || root?.dataset.lessonParticipants
      || params.get('participants')
      || 2,
    ),
    hours: Number(
      root?.querySelector('.js-booking-lesson-hours')?.value
      || params.get('hours')
      || root?.dataset.lessonHours
      || 3,
    ),
    discipline: root?.querySelector('[name="discipline"]')?.value
      || params.get('discipline')
      || root?.dataset.lessonDiscipline
      || 'ski',
    location: root?.querySelector('[name="location"]')?.value
      || params.get('location')
      || root?.dataset.lessonLocation
      || 'harenda',
    lessonDate: readFieldValue(root, 'lesson-date') || '',
    lessonTime: readFieldValue(root, 'lesson-time') || '',
  };
};

const getBookingApi = () => ({
  restUrl: window.hskiBooking?.restUrl || '/wp-json/hski/v1/booking',
  restNonce: window.hskiBooking?.restNonce || '',
});

/** Włącz: ?crm_debug=1, localStorage.hski_crm_debug=1, lub window.hskiBooking.crmDebug = true */
const isCrmDebug = () => {
  try {
    if (window.hskiBooking?.crmDebug) return true;
    if (new URLSearchParams(window.location.search).get('crm_debug') === '1') return true;
    if (localStorage.getItem('hski_crm_debug') === '1') return true;
  } catch {
    /* ignore */
  }
  return false;
};

const logCrmDebug = (...args) => {
  if (!isCrmDebug()) return;
  console.log('[HSKI CRM]', ...args);
};

const COURSE_STUDENTS_STEP = 1;
const COURSE_ORDERER_STEP = 2;
const LESSON_STUDENTS_STEP = 1;
const LESSON_ORDERER_STEP = 2;
const TRAVEL_PARTICIPANTS_STEP = 1;
const TRAVEL_ORDERER_STEP = 2;
const TRAVEL_REVIEW_STEP = 3;

const newSubmissionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const readFieldValue = (root, name) => root.querySelector(`[name="${name}"]`)?.value?.trim() || '';

const readCheckbox = (root, name) => Boolean(root.querySelector(`[name="${name}"]`)?.checked);

const PHONE_PREFIX_DEFAULT = '+48';

const normalizePhonePrefix = (value) => {
  const prefix = String(value || '').trim();
  return /^\+\d{1,4}$/.test(prefix) ? prefix : PHONE_PREFIX_DEFAULT;
};

const readPhonePrefix = (scope) => normalizePhonePrefix(
  scope?.querySelector('.js-phone-prefix, [name="phone-prefix"]')?.value,
);

const readPhoneCountry = (scope) => {
  const select = scope?.querySelector('.js-phone-prefix, [name="phone-prefix"]');
  const code = String(select?.selectedOptions?.[0]?.dataset?.country || '').toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : 'PL';
};

const formatPhoneWithPrefix = (phone, prefix = PHONE_PREFIX_DEFAULT) => {
  const raw = String(phone || '').trim();
  if (!raw) return '';

  const normalizedPrefix = normalizePhonePrefix(prefix);
  const prefixDigits = normalizedPrefix.replace(/\D/g, '');
  let digits = raw.replace(/\D/g, '');

  if (prefixDigits && digits.startsWith(prefixDigits) && digits.length > prefixDigits.length) {
    digits = digits.slice(prefixDigits.length);
  }

  // Numer krajowy w grupach 3 (jak maska w polu): 669 105 704
  const national = digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  return national ? `${normalizedPrefix} ${national}` : normalizedPrefix;
};

const nationalPhoneDigits = (value) => String(value || '').replace(/\D/g, '');

/** Walidacja numeru krajowego — długość zależna od wybranego kraju (PL 9, GB 10, …). */
const isValidBookingPhone = (field) => {
  if (!field) return false;
  if (field._bookingPhoneMask || field.classList.contains('js-booking-phone')) {
    return isCompleteBookingPhone(field);
  }
  let digits = nationalPhoneDigits(field.value);
  if (!digits) return false;

  const control = field.closest('.booking-form__control--phone') || field.closest('.booking-form__field');
  const prefixDigits = String(readPhonePrefix(control)).replace(/\D/g, '');
  if (prefixDigits && digits.startsWith(prefixDigits) && digits.length > prefixDigits.length) {
    digits = digits.slice(prefixDigits.length);
  }
  digits = digits.replace(/^0+/, '') || digits;
  const rule = getBookingPhoneRule(readPhoneCountry(control));
  const min = rule.min != null ? rule.min : rule.digits;
  return digits.length >= min && digits.length <= rule.digits && /^\d+$/.test(digits);
};

const syncPhoneFieldValidity = (field) => {
  if (!field || field.type !== 'tel') return;
  const ok = isValidBookingPhone(field);
  field.setCustomValidity(ok || !field.value?.trim()
    ? ''
    : bookingPhoneValidityMessage(field));
};

const PHONE_PREFIX_CHEVRON = '<svg class="booking-form__phone-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const parsePhonePrefixOption = (option) => {
  if (!option) {
    return { dial: PHONE_PREFIX_DEFAULT, country: 'PL', flag: '🇵🇱', name: ui('Polska'), search: 'polska poland +48 pl' };
  }
  const flag = option.dataset.flag || '';
  const sourceName = option.dataset.searchName
    || String(option.textContent || '').replace(flag, '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  const name = option.dataset.name || sourceName;
  const dial = option.value || PHONE_PREFIX_DEFAULT;
  const country = String(option.dataset.country || 'PL').toUpperCase();
  return {
    dial,
    country,
    flag,
    name,
    search: `${name} ${sourceName} ${dial} ${country}`.toLowerCase(),
  };
};

const selectedPhonePrefixOption = (select) => select?.selectedOptions?.[0] || select?.options?.[0] || null;

const closePhonePrefixPanels = (except = null) => {
  document.querySelectorAll('.booking-form__control--phone.is-prefix-open').forEach((control) => {
    if (control === except) return;
    control.classList.remove('is-prefix-open');
    const panel = control.querySelector('.booking-form__phone-panel');
    const trigger = control.querySelector('.js-phone-prefix-trigger');
    if (panel) panel.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
  });
};

const enhancePhonePrefixSelect = (select) => {
  if (!select || select.dataset.enhanced === '1') return;
  const wrap = select.closest('.booking-form__phone-prefix');
  const control = select.closest('.booking-form__control--phone');
  if (!wrap || !control) return;

  select.dataset.enhanced = '1';
  select.classList.add('visually-hidden');
  select.setAttribute('tabindex', '-1');
  wrap.classList.add('is-enhanced');
  wrap.querySelector('img')?.remove();

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'booking-form__phone-trigger js-phone-prefix-trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', select.getAttribute('aria-label') || 'Numer kierunkowy');

  const flagEl = document.createElement('span');
  flagEl.className = 'booking-form__flag js-phone-flag';
  flagEl.setAttribute('aria-hidden', 'true');

  const dialEl = document.createElement('span');
  dialEl.className = 'booking-form__phone-dial js-phone-dial';

  trigger.append(flagEl, dialEl);
  trigger.insertAdjacentHTML('beforeend', PHONE_PREFIX_CHEVRON);
  wrap.insertBefore(trigger, select);

  const panel = document.createElement('div');
  panel.className = 'booking-form__phone-panel';
  panel.hidden = true;
  panel.innerHTML = `
    <input type="search" class="booking-form__phone-search js-phone-prefix-search" placeholder="${escapeHtml(ui('Szukaj kraju lub numeru'))}" autocomplete="off" enterkeyhint="search">
    <div class="booking-form__phone-list js-phone-prefix-list" role="listbox"></div>
  `;
  control.appendChild(panel);

  const listEl = panel.querySelector('.js-phone-prefix-list');
  const searchEl = panel.querySelector('.js-phone-prefix-search');
  const options = [...select.options].map(parsePhonePrefixOption);

  const syncTrigger = () => {
    const meta = parsePhonePrefixOption(selectedPhonePrefixOption(select));
    flagEl.textContent = meta.flag;
    dialEl.textContent = meta.dial;
    listEl?.querySelectorAll('.booking-form__phone-option').forEach((btn) => {
      btn.classList.toggle('is-selected', btn.dataset.country === meta.country);
    });
  };

  const renderList = (query = '') => {
    const needle = String(query || '').trim().toLowerCase();
    const matches = needle
      ? options.filter((item) => item.search.includes(needle))
      : options;
    const selected = parsePhonePrefixOption(selectedPhonePrefixOption(select)).country;
    if (!matches.length) {
      listEl.innerHTML = `<p class="booking-form__phone-empty">${escapeHtml(ui('Brak wyników'))}</p>`;
      return;
    }
    listEl.innerHTML = matches.map((item) => `
      <button
        type="button"
        class="booking-form__phone-option${item.country === selected ? ' is-selected' : ''}"
        role="option"
        data-country="${escapeHtml(item.country)}"
        data-dial="${escapeHtml(item.dial)}"
        aria-selected="${item.country === selected ? 'true' : 'false'}"
      >
        <span class="booking-form__phone-option-flag" aria-hidden="true">${escapeHtml(item.flag)}</span>
        <span class="booking-form__phone-option-name">${escapeHtml(item.name)}</span>
        <span class="booking-form__phone-option-dial">${escapeHtml(item.dial)}</span>
      </button>
    `).join('');
  };

  const openPanel = () => {
    closePhonePrefixPanels(control);
    control.classList.add('is-prefix-open');
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    renderList(searchEl.value);
    window.requestAnimationFrame(() => searchEl.focus());
  };

  const closePanel = () => {
    control.classList.remove('is-prefix-open');
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    searchEl.value = '';
  };

  const chooseOption = (country) => {
    const option = [...select.options].find((item) => String(item.dataset.country || '').toUpperCase() === country);
    if (!option) return;
    option.selected = true;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncTrigger();
    closePanel();
  };

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (control.classList.contains('is-prefix-open')) closePanel();
    else openPanel();
  });

  listEl.addEventListener('click', (event) => {
    const optionBtn = event.target.closest('.booking-form__phone-option');
    if (!optionBtn) return;
    event.preventDefault();
    chooseOption(String(optionBtn.dataset.country || '').toUpperCase());
  });

  searchEl.addEventListener('input', () => renderList(searchEl.value));
  searchEl.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
      trigger.focus();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const first = listEl.querySelector('.booking-form__phone-option');
      if (first) chooseOption(String(first.dataset.country || '').toUpperCase());
    }
  });

  select.addEventListener('change', syncTrigger);
  syncTrigger();
  renderList();
};

const initPhonePrefixSelect = (root) => {
  root.querySelectorAll('.js-phone-prefix').forEach((select) => enhancePhonePrefixSelect(select));
  root.querySelectorAll('.js-booking-phone, .booking-form__control--phone input[name="phone"]').forEach((input) => {
    createBookingPhoneMask(input);
    const sync = () => {
      syncPhoneFieldValidity(input);
      if (input.classList.contains('is-invalid') || input.value.trim()) {
        setBookingFieldInvalid(input, !isValidBookingPhone(input));
      }
    };
    input.addEventListener('input', sync);
    input.addEventListener('blur', sync);
    const control = input.closest('.booking-form__control--phone');
    control?.querySelector('.js-phone-prefix')?.addEventListener('change', () => {
      setBookingPhoneCountry(input, readPhoneCountry(control));
      sync();
    });
  });
  if (document.documentElement.dataset.phonePrefixUi === '1') return;
  document.documentElement.dataset.phonePrefixUi = '1';
  document.addEventListener('click', (event) => {
    if (event.target.closest('.booking-form__control--phone')) return;
    closePhonePrefixPanels();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePhonePrefixPanels();
  });
};

const getContactStep = (root, bookingType) => {
  const step = bookingType === 'travel'
    ? String(TRAVEL_ORDERER_STEP)
    : bookingType === 'course'
      ? String(COURSE_ORDERER_STEP)
      : (bookingType === 'lesson' ? String(LESSON_ORDERER_STEP) : '3');
  return root.querySelector(`[data-booking-step="${step}"]`);
};

const collectContact = (root, bookingType) => {
  const contactStep = getContactStep(root, bookingType);
  const notesField = bookingType === 'travel' ? 'order-notes' : 'notes';
  const phoneEl = contactStep?.querySelector('[name="phone"], .js-booking-phone');
  const phoneDigits = phoneEl ? bookingPhoneUnmasked(phoneEl) : nationalPhoneDigits(readFieldValue(contactStep, 'phone'));

  return {
    salutation: readFieldValue(contactStep, 'salutation'),
    first_name: readFieldValue(contactStep, 'first-name'),
    last_name: readFieldValue(contactStep, 'last-name'),
    email: readFieldValue(contactStep, 'email'),
    phone: phoneDigits,
    phone_prefix: readPhonePrefix(contactStep),
    phone_country: readPhoneCountry(contactStep),
    birth_date: polishBirthToIso(readFieldValue(contactStep, 'birth-date')),
    is_participant: readCheckbox(contactStep, 'is-participant'),
    address: readFieldValue(contactStep, 'address'),
    postcode: readFieldValue(contactStep, 'postcode'),
    city: readFieldValue(contactStep, 'city'),
    notes: readFieldValue(contactStep, notesField),
    rodo: readCheckbox(contactStep, 'rodo') || readCheckbox(root, 'rodo'),
    terms: readCheckbox(contactStep, 'terms') || readCheckbox(root, 'terms'),
    marketing: readCheckbox(contactStep, 'marketing'),
  };
};

const formatContactSummary = (contact) => {
  if (!contact.first_name || !contact.last_name) {
    return '—';
  }

  const parts = [`${contact.first_name} ${contact.last_name}`];
  if (contact.email) parts.push(contact.email);
  if (contact.phone) parts.push(formatPhoneWithPrefix(contact.phone, contact.phone_prefix));

  const address = [contact.address, contact.postcode, contact.city].filter(Boolean).join(', ');
  if (address) parts.push(address);
  if (contact.notes) parts.push(contact.notes);

  return parts.join(' · ');
};

const travelDisciplineFlagsFromConfig = (config = {}) => ({
  showSki: config.show_ski !== false,
  showSnowboard: Boolean(config.show_snowboard),
});

const defaultTravelTrainingDiscipline = (flags) => {
  if (flags?.showSki) return 'ski';
  if (flags?.showSnowboard) return 'snowboard';
  return '';
};

/** @returns {'ski'|'snowboard'|''} */
const normalizeTravelTrainingDiscipline = (discipline, flags) => {
  const showSki = flags?.showSki !== false;
  const showSnowboard = Boolean(flags?.showSnowboard);
  const value = String(discipline || '').trim();
  if (value === 'ski' && showSki) return 'ski';
  if (value === 'snowboard' && showSnowboard) return 'snowboard';
  return defaultTravelTrainingDiscipline({ showSki, showSnowboard });
};

const travelDisciplineLabel = (discipline, t) => {
  if (discipline === 'snowboard') return t('discipline_snowboard', 'Snowboard');
  if (discipline === 'ski') return t('discipline_ski', 'Narty');
  return '';
};

const parseTravelAddonsConfig = (root) => {
  const raw = root?.dataset?.bookingAddons || '{}';
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const travelAddonPrice = (config, slug, fallback = 0) => {
  const addon = (config.addons || []).find((item) => item.slug === slug);
  return Number(addon?.price ?? fallback);
};

const travelAddonChildPrice = (config, slug, fallback = 0) => {
  const addon = (config.addons || []).find((item) => item.slug === slug);
  const child = Number(addon?.price_child);
  if (Number.isFinite(child) && child > 0) return child;
  return Number(addon?.price ?? fallback);
};

const travelAddonTrainingAlt = (config) => {
  const addon = (config.addons || []).find((item) => item.slug === 'training');
  const altAdult = Number(addon?.price_alt ?? 0);
  const altChild = Number(addon?.price_alt_child ?? 0);
  const enabled = Boolean(addon?.alt_enabled) || altAdult > 0 || altChild > 0;
  const label = String(addon?.alt_label || 'Początkujący / bez zakwaterowania HSKI').trim()
    || 'Początkujący / bez zakwaterowania HSKI';
  return {
    enabled,
    adult: Number.isFinite(altAdult) ? altAdult : 0,
    child: Number.isFinite(altChild) ? altChild : 0,
    label,
  };
};

const normalizeTravelTrainingRate = (value) => {
  const raw = String(value || '').toLowerCase().trim();
  if (['alt', 'p1', 'beginner', 'external', 'special', 'second'].includes(raw)) return 'alt';
  return 'standard';
};

const travelAddonAgePrices = (config, slug) => {
  const addon = (config.addons || []).find((item) => item.slug === slug);
  const rows = addon?.age_prices;
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      id: String(row.id || ''),
      age_from: Number(row.age_from ?? row.od ?? 0),
      age_to: Number(row.age_to ?? row.do ?? 0),
      price: Number(row.price ?? 0),
    }))
    .filter((row) => Number.isFinite(row.price) && row.age_to >= row.age_from);
};

/** Wiersz macierzy bez DOB: dorosły → dorosly/18+; dziecko → najszerszy wiersz <18. */
const trainingBandForSlotWithoutAge = (bands, isAdultSlot) => {
  if (!Array.isArray(bands) || !bands.length) return null;
  if (isAdultSlot) {
    let best = null;
    for (const b of bands) {
      const id = String(b.id || '');
      const from = Number(b.age_from ?? 0);
      if (id !== 'dorosly' && !(Number.isFinite(from) && from >= 18)) continue;
      if (id === 'dorosly') return b;
      if (!best || from > Number(best.age_from ?? 0)) best = b;
    }
    return best;
  }
  let best = null;
  let bestSpan = -1;
  bands.forEach((b) => {
    const id = String(b.id || '');
    const from = Number(b.age_from ?? 0);
    const to = Number(b.age_to ?? 0);
    if (id === 'dorosly' || from >= 18) return;
    const span = Math.max(0, to - from);
    if (span > bestSpan) {
      bestSpan = span;
      best = b;
    }
  });
  return best;
};

/** Cena szkolenia z macierzy wieku (DOB) albo wiersz slotu; dopiero potem fallback. */
const trainingUnitPriceForAge = (config, age, isAdultSlot, adultFallback = 0, childFallback = 0) => {
  const bands = travelAddonAgePrices(config, 'training');
  if (bands.length) {
    if (age !== null && age !== undefined && Number.isFinite(age)) {
      let chosen = null;
      bands.forEach((b) => {
        if (age >= b.age_from && age <= b.age_to) {
          if (!chosen || b.age_from > chosen.age_from) chosen = b;
        }
      });
      if (!chosen) {
        let bestDistance = Infinity;
        bands.forEach((b) => {
          const distance = age < b.age_from ? (b.age_from - age) : (age - b.age_to);
          if (distance < bestDistance) {
            bestDistance = distance;
            chosen = b;
          }
        });
      }
      if (chosen && Number.isFinite(chosen.price)) return chosen.price;
    } else {
      const slotBand = trainingBandForSlotWithoutAge(bands, isAdultSlot);
      if (slotBand && Number.isFinite(slotBand.price)) return slotBand.price;
    }
  }
  return isAdultSlot ? adultFallback : childFallback;
};

const parseJsonDataset = (raw, fallback) => {
  try {
    const parsed = JSON.parse(raw || '');
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const ageFromIsoDate = (iso, asOfIso = '') => {
  const raw = String(iso || '').trim();
  if (!raw) return null;
  const birth = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = asOfIso ? new Date(`${asOfIso}T12:00:00`) : new Date();
  if (Number.isNaN(now.getTime())) return null;
  let age = now.getFullYear() - birth.getFullYear();
  const md = (now.getMonth() + 1) * 100 + now.getDate();
  const bmd = (birth.getMonth() + 1) * 100 + birth.getDate();
  if (md < bmd) age -= 1;
  return Math.max(0, age);
};

/**
 * Pingwin age↔package: young_max cannot take podstawowy; older_min–older_max on ekonomiczny
 * needs conscious continue via CTA (no checkbox). Age as of course date_from when available.
 * enabled_mode: auto (Harenda only) | 1 (all) | 0 (off). Ages come from Theme Options.
 */
const DEFAULT_PINGWIN_PKG_AGE = {
  enabled: false,
  enabled_mode: 'auto',
  auto_locations: ['harenda'],
  young_max_age: 5,
  older_min_age: 6,
  older_max_age: 12,
  package_podstawowy: 'podstawowy',
  package_ekonomiczny: 'ekonomiczny',
  msg_young_on_podstawowy:
    'W wybranym terminie kursu Twoje dziecko będzie miało mniej niż 6 lat. '
    + 'Pakiet podstawowy jest przeznaczony dla dzieci od 6. roku życia. '
    + 'Dla Twojego dziecka najlepszym wyborem jest Pakiet ekonomiczny, '
    + 'trwający od {date_from} do {date_to}.',
  msg_older_on_ekonomiczny:
    'Zachęcamy do wyboru Pakietu podstawowego, który jest lepiej dostosowany '
    + 'do dzieci od 6 roku życia. Pakiet ekonomiczny jest przeznaczony głównie '
    + 'dla dzieci w wieku 4 i 5 lat. Możesz świadomie zostać przy Pakiecie ekonomicznym, '
    + 'jeśli taki jest Twój wybór.',
  title_young_on_podstawowy: 'Przejdź na Pakiet Ekonomiczny',
  title_older_on_ekonomiczny: 'Rekomendujemy Pakiet podstawowy',
  btn_switch_ekonomiczny: 'Przejdź na Pakiet ekonomiczny',
  btn_switch_podstawowy: 'Przejdź na Pakiet podstawowy',
  btn_stay_ekonomiczny: 'Kontynuuj z Pakietem ekonomicznym',
  btn_back: 'Wróć',
};

/** Fill {date_from}/{date_to} from course booking dates (DD.MM.YYYY). */
const fillPingwinPkgAgePlaceholders = (template, rootEl) => {
  let text = String(template || '');
  if (!text) return '';
  const config = getCourseConfig(rootEl);
  const from = formatCourseDate(config.date_from || '');
  const to = formatCourseDate(config.date_to || '');
  if (from && to) {
    return text.replaceAll('{date_from}', from).replaceAll('{date_to}', to);
  }
  text = text.replace(/\s*,?\s*trwający od \{date_from\} do \{date_to\}\.?/giu, '.');
  text = text.replaceAll('{date_from}', '').replaceAll('{date_to}', '');
  return text.replace(/\.\s*\./g, '.').trim();
};

const parsePingwinPackageAgeConfig = (root) => {
  const fromDom = parseJsonDataset(root?.dataset?.pingwinPackageAge, null);
  if (fromDom && typeof fromDom === 'object') {
    return { ...DEFAULT_PINGWIN_PKG_AGE, ...fromDom };
  }
  const fromGlobal = window.hskiBooking?.pingwinPackageAge;
  if (fromGlobal && typeof fromGlobal === 'object') {
    return { ...DEFAULT_PINGWIN_PKG_AGE, ...fromGlobal };
  }
  return { ...DEFAULT_PINGWIN_PKG_AGE };
};

/** Resolve enabled for current booking location (auto = Harenda Zakopane only). */
const resolvePingwinPackageAgeEnabled = (root, cfg = DEFAULT_PINGWIN_PKG_AGE) => {
  const mode = String(cfg.enabled_mode ?? '').toLowerCase();
  if (mode === '0' || mode === 'off' || mode === 'no') return false;
  if (mode === '1' || mode === 'on' || mode === 'yes') return true;
  if (mode === 'auto' || mode === 'default' || mode === '') {
    const loc = String(getCourseConfig(root).location || root?.dataset?.location || '')
      .toLowerCase()
      .trim();
    if (!loc) return false;
    const autos = Array.isArray(cfg.auto_locations) && cfg.auto_locations.length
      ? cfg.auto_locations
      : ['harenda'];
    const aliases = new Set(['harenda', 'zakopane-harenda', 'poland', 'harenda-zakopane']);
    return autos.some((s) => {
      const slug = String(s || '').toLowerCase();
      return loc === slug || (slug === 'harenda' && aliases.has(loc));
    });
  }
  // Legacy payload: only `enabled` bool from server for this location.
  return Boolean(cfg.enabled);
};

const pingwinPackageKind = (packageId, packageLabel = '', cfg = DEFAULT_PINGWIN_PKG_AGE) => {
  const id = String(packageId || '').toLowerCase();
  const label = String(packageLabel || '').toLowerCase();
  const eco = String(cfg.package_ekonomiczny || 'ekonomiczny');
  const pod = String(cfg.package_podstawowy || 'podstawowy');
  if (id === eco || id.includes('ekonom') || label.includes('ekonom')) return eco;
  if (id === pod || id.includes('podstaw') || label.includes('podstaw')) return pod;
  return '';
};

/** @returns {'force_ekonomiczny'|'ack_ekonomiczny'|null} */
const pingwinPackageAgeScenario = (age, packageKind, cfg = DEFAULT_PINGWIN_PKG_AGE) => {
  if (age === null || age === undefined || !Number.isFinite(age) || !packageKind) return null;
  const youngMax = Number(cfg.young_max_age ?? 5);
  const olderMin = Number(cfg.older_min_age ?? 6);
  const olderMax = Number(cfg.older_max_age ?? 12);
  const eco = String(cfg.package_ekonomiczny || 'ekonomiczny');
  const pod = String(cfg.package_podstawowy || 'podstawowy');
  if (age <= youngMax && packageKind === pod) return 'force_ekonomiczny';
  if (age >= olderMin && age <= olderMax && packageKind === eco) return 'ack_ekonomiczny';
  return null;
};

/** Polish label for modal/scroll targeting: „Dziecko N” + name when filled. */
const getCourseStudentDisplayLabel = (card) => {
  if (!card) return '';
  const index = card.dataset.studentIndex || '1';
  const first = card.querySelector(`[name="student-first-name-${index}"]`)?.value?.trim() || '';
  const last = card.querySelector(`[name="student-last-name-${index}"]`)?.value?.trim() || '';
  const name = [first, last].filter(Boolean).join(' ');
  const base = `Dziecko ${index}`;
  return name ? `${base} (${name})` : base;
};

const scrollToCourseStudentCard = (card) => {
  if (!card || typeof card.scrollIntoView !== 'function') return;
  card.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'center',
    inline: 'nearest',
  });
};

const travelTripDate = (root) => (
  root?.dataset?.dateFrom
  || new URLSearchParams(window.location.search).get('date_from')
  || ''
);

const formatMoneyAmount = (amount, currency = 'PLN') => {
  const n = Number(amount) || 0;
  const formatted = n.toLocaleString('pl-PL', { maximumFractionDigits: 0 });
  const code = String(currency).toUpperCase();
  if (code === 'PLN') return `${formatted} zł`;
  if (code === 'EUR') return `${formatted} €`;
  return `${formatted} ${currency}`;
};

const formatQuoteTotal = (quote) => {
  if (!quote) return '';
  if (quote.total_formatted) return String(quote.total_formatted);
  const byCurrency = quote.totals_by_currency;
  if (byCurrency && typeof byCurrency === 'object') {
    const parts = Object.entries(byCurrency)
      .filter(([, amount]) => Number(amount) > 0)
      .map(([currency, amount]) => formatMoneyAmount(amount, currency));
    if (parts.length) return parts.join(' + ');
  }
  return '';
};

/** Cost rows for the success summary — same amounts as the quote / sidebar, not basePrice × people. */
const travelQuoteCostRows = (quote) => {
  const lines = Array.isArray(quote?.lines) ? quote.lines : [];
  const fallbackCurrency = quote?.currency || 'PLN';
  const buckets = {
    stay: [],
    skipass: [],
    insurance: [],
    cancellation: [],
    training: [],
    other: [],
  };

  lines.forEach((line) => {
    if (!line || Number(line.amount || 0) <= 0) return;
    const key = String(line.key || '');
    if (key === 'stay' || key === 'accommodation' || key === 'base') buckets.stay.push(line);
    else if (key === 'skipass') buckets.skipass.push(line);
    else if (key === 'mandatory_insurance') buckets.insurance.push(line);
    else if (key.startsWith('cancellation')) buckets.cancellation.push(line);
    else if (key.startsWith('training')) buckets.training.push(line);
    else if (key.startsWith('nnw')) return;
    else buckets.other.push(line);
  });

  const sumAmount = (group) => group.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const currencyOf = (group) => group[0]?.currency || fallbackCurrency;
  const row = (label, group) => {
    if (!group.length) return [];
    return [{ label, text: formatMoneyAmount(sumAmount(group), currencyOf(group)) }];
  };
  const lineRows = (group, formatLine) => group.map((line) => ({
    label: line.label || '',
    text: formatLine ? formatLine(line) : formatMoneyAmount(line.amount, line.currency || fallbackCurrency),
  }));

  return [
    ...lineRows(buckets.stay),
    ...row(ui(buckets.skipass[0]?.label || 'Skipass'), buckets.skipass),
    ...lineRows(buckets.insurance, (line) => {
      const qty = Math.max(1, Number(line.quantity) || 1);
      const currency = line.currency || fallbackCurrency;
      const total = formatMoneyAmount(line.amount, currency);
      if (qty <= 1) return total;
      return `${formatMoneyAmount(line.unit_price, currency)} × ${qty} ${ui('os.')} = ${total}`;
    }),
    ...row(
      buckets.cancellation.length > 1
        ? uiFmt('Ubezpieczenie od rezygnacji (×%s)', buckets.cancellation.length)
        : ui(buckets.cancellation[0]?.label || 'Ubezpieczenie od rezygnacji'),
      buckets.cancellation,
    ),
    ...row(
      buckets.training.length > 1
        ? `${buckets.training.length}x Szkolenie narciarskie`
        : (buckets.training[0]?.label || 'Szkolenie narciarskie'),
      buckets.training,
    ),
    ...lineRows(buckets.other),
  ];
};

const TRAVEL_STAY_AGE_ERROR = () => ui('Sprawdź daty urodzenia — wiek dziecka jest poza przedziałem cenowym tej oferty.');
const TRAVEL_STAY_ZERO_ERROR = () => ui('Nie można złożyć rezerwacji z ceną pobytu 0. Sprawdź daty urodzenia uczestników.');

const travelStayBlockedMessage = (quote) => {
  if (!quote || typeof quote !== 'object') return '';
  if (quote.blocked) return ui(quote.message) || TRAVEL_STAY_AGE_ERROR();
  const room = quote.room_quote;
  if (!room || typeof room !== 'object') return '';
  const errors = Array.isArray(room.errors) ? room.errors.filter(Boolean) : [];
  if (errors.length) return TRAVEL_STAY_AGE_ERROR();
  if (room.room_on_request) return '';
  const stay = Number(room.stay_total);
  if (Number.isFinite(stay) && stay <= 0) return TRAVEL_STAY_ZERO_ERROR();
  return '';
};

/**
 * Wieki dzieci bez slice(dorośli). Liczba dorosłych zostaje z selecta.
 * Gdy osób poniżej 18 lat jest dokładnie tyle, ile dzieci, bierzemy ich wiek
 * niezależnie od pozycji (checkbox „osoba zamawiająca” wstawia dorosłego w dowolny wiersz).
 * Gdy liczba się nie zgadza, zostają wiersze z data-guest-role=child.
 */
const collectTravelChildrenAges = (root, cards = []) => {
  const children = Number(root.querySelector('.js-booking-children')?.value || 0);
  if (children <= 0) return [];
  const asOf = travelTripDate(root);
  const ageOf = (card) => ageFromIsoDate(polishBirthToIso(readTravelParticipantBirthValue(card)), asOf);
  const ages = cards.map((card) => ageOf(card));
  const filled = ages.filter((age) => age !== null).length;
  const under18 = ages.filter((age) => age !== null && age < 18);
  if (under18.length === children && filled > 0) return under18;

  const storedRaw = parseJsonDataset(root.dataset.childrenAges, []);
  const stored = Array.isArray(storedRaw)
    ? storedRaw.map(Number).filter((n) => Number.isFinite(n)).slice(0, children)
    : [];
  const allFilled = cards.length > 0 && filled === cards.length;
  if (!allFilled) return stored;

  const childCards = cards.filter((card) => card.dataset.guestRole === 'child');
  if (childCards.length === children) {
    const slotAges = childCards.map((card) => ageOf(card));
    if (slotAges.every((age) => age !== null)) return slotAges;
  }
  if (!under18.length) {
    const adultAges = ages.filter((age) => age !== null && age >= 18);
    if (adultAges.length) return adultAges.slice(0, children);
  }
  return stored;
};

const collectTravelOfferSnapshot = (root) => {
  const resort = root.dataset.resort || '';
  const hotel = root.dataset.hotel || '';
  const country = root.dataset.country || '';
  const features = parseJsonDataset(root.dataset.bookingFeatures, []);
  const nights = Number(root.dataset.nights || 0);
  const room = root.dataset.roomTitle
    || root.querySelector('.js-booking-sidebar-room')?.textContent?.trim()
    || '';
  const roomIndex = root.dataset.roomIndex || '';

  return {
    resort,
    hotel,
    country,
    location: [country, resort].filter(Boolean).join(', '),
    meal_plan: root.dataset.mealPlan || '',
    nights: nights > 0 ? nights : null,
    skipass_included: root.dataset.skipassIncluded === '1',
    term_label: root.dataset.termLabel || '',
    room,
    room_index: roomIndex,
    features: Array.isArray(features)
      ? features.map((item) => (typeof item === 'string' ? item : item?.label || '')).filter(Boolean)
      : [],
  };
};

/** Attribution keys kept on thank-you URL for Ads / social conversion measurement. */
const BOOKING_ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
];
const BOOKING_ATTRIBUTION_STORAGE_KEY = 'hski_booking_attribution_v1';
const TRAVEL_THANK_YOU_STORAGE_KEY = 'hski_travel_thank_you_v1';

const readTextContent = (root, selector) => root.querySelector(selector)?.textContent?.trim() || '';

const isTravelThankYouPath = (pathname = window.location.pathname) => {
  const path = String(pathname || '').replace(/\/+$/, '');
  const configured = window.hskiBooking?.paths?.thank_you;
  if (configured) {
    try {
      const cfg = new URL(configured, window.location.origin).pathname.replace(/\/+$/, '');
      if (path === cfg || path.endsWith(cfg)) return true;
    } catch {
      /* ignore */
    }
  }
  return path.endsWith('/rezerwacja-dziekujemy')
    || path === '/rezerwacja-dziekujemy'
    || path.endsWith('/thank-you-for-your-booking')
    || path === '/thank-you-for-your-booking';
};

const isTravelThankYouView = (root) => (
  root?.dataset?.thankYou === '1'
  || new URLSearchParams(window.location.search).get('dziekujemy') === '1'
  || isTravelThankYouPath()
);

const captureBookingAttributionFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    let any = false;
    BOOKING_ATTRIBUTION_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        found[key] = value;
        any = true;
      }
    });
    if (!any) return;
    sessionStorage.setItem(BOOKING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(found));
  } catch {
    /* private mode / blocked storage */
  }
};

const getBookingAttributionParams = () => {
  const merged = {};
  try {
    const raw = sessionStorage.getItem(BOOKING_ATTRIBUTION_STORAGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw);
      if (stored && typeof stored === 'object') {
        BOOKING_ATTRIBUTION_KEYS.forEach((key) => {
          if (stored[key]) merged[key] = String(stored[key]);
        });
      }
    }
  } catch {
    /* ignore */
  }
  const params = new URLSearchParams(window.location.search);
  BOOKING_ATTRIBUTION_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) merged[key] = value;
  });
  return merged;
};

const applyAttributionToUrl = (url) => {
  const attribution = getBookingAttributionParams();
  Object.entries(attribution).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  return url;
};

const saveTravelThankYouSnapshot = (snapshot) => {
  try {
    sessionStorage.setItem(TRAVEL_THANK_YOU_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore */
  }
};

const readTravelThankYouSnapshot = () => {
  try {
    const raw = sessionStorage.getItem(TRAVEL_THANK_YOU_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const serializeTravelThankYouSnapshot = (root, reference = '') => {
  const resort = root.dataset.resort || readTextContent(root, '.js-travel-success-resort');
  const hotel = root.dataset.hotel || readTextContent(root, '.js-travel-success-hotel');
  const offerTitle = [resort, hotel].filter(Boolean).join(' · ');
  return {
    reference: reference || readTextContent(root, '.js-booking-ref'),
    offerTitle,
    resort,
    hotel,
    place: readTextContent(root, '.js-travel-success-place')
      || root.querySelector('.js-booking-sidebar-place')?.textContent?.trim()
      || '',
    termLabel: readTextContent(root, '.js-travel-success-term') || root.dataset.termLabel || '',
    guestsLabel: readTextContent(root, '.js-travel-success-guests'),
    room: readTextContent(root, '.js-travel-success-room')
      || root.querySelector('.js-booking-sidebar-room')?.textContent?.trim()
      || '',
    costsHtml: root.querySelector('.js-travel-success-costs')?.innerHTML || '',
    total: readTextContent(root, '.js-travel-success-total'),
    ordererLine: readTextContent(root, '.js-travel-success-orderer-line'),
    firstName: readTextContent(root, '.js-travel-success-first-name'),
    lastName: readTextContent(root, '.js-travel-success-last-name'),
    phone: readTextContent(root, '.js-travel-success-phone'),
    email: readTextContent(root, '.js-travel-success-email'),
    birth: readTextContent(root, '.js-travel-success-birth'),
    participantsHtml: root.querySelector('.js-travel-success-participants')?.innerHTML || '',
    participantsLine: readTextContent(root, '.js-travel-success-participants-line'),
    notes: readTextContent(root, '.js-travel-success-notes'),
    savedAt: Date.now(),
  };
};

const applyTravelThankYouSnapshot = (root, snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return;
  const setText = (selector, value) => {
    const el = root.querySelector(selector);
    if (el && value != null && value !== '') el.textContent = value;
  };

  if (snapshot.reference) {
    const refEl = root.querySelector('.js-booking-ref');
    if (refEl) refEl.textContent = snapshot.reference;
  }
  setText('.js-travel-success-resort', snapshot.resort);
  setText('.js-travel-success-hotel', snapshot.hotel);
  setText('.js-travel-success-place', snapshot.place);
  setText('.js-travel-success-term', snapshot.termLabel);
  setText('.js-travel-success-guests', snapshot.guestsLabel);
  const roomRow = root.querySelector('.js-travel-success-room-row');
  if (roomRow) {
    if (snapshot.room) {
      setText('.js-travel-success-room', snapshot.room);
      roomRow.hidden = false;
    } else {
      roomRow.hidden = true;
    }
  }
  const costsList = root.querySelector('.js-travel-success-costs');
  if (costsList && snapshot.costsHtml) costsList.innerHTML = snapshot.costsHtml;
  setText('.js-travel-success-total', snapshot.total);
  setText('.js-travel-success-orderer-line', snapshot.ordererLine);
  setText('.js-travel-success-first-name', snapshot.firstName);
  setText('.js-travel-success-last-name', snapshot.lastName);
  setText('.js-travel-success-phone', snapshot.phone);
  setText('.js-travel-success-email', snapshot.email);
  setText('.js-travel-success-birth', snapshot.birth);
  const participantsList = root.querySelector('.js-travel-success-participants');
  if (participantsList && snapshot.participantsHtml) {
    participantsList.innerHTML = snapshot.participantsHtml;
  }
  setText('.js-travel-success-participants-line', snapshot.participantsLine);
  const participantsWrap = root.querySelector('.travel-success__participants-wrap');
  if (participantsWrap) {
    participantsWrap.hidden = !snapshot.participantsHtml;
  }
  setText('.js-travel-success-notes', snapshot.notes);
};

const buildTravelThankYouUrl = (root, snapshot) => {
  const configured = (root.dataset.thankYouUrl || '').trim();
  const url = configured
    ? new URL(configured, window.location.origin)
    : new URL(window.location.href);

  if (configured) {
    url.search = '';
  } else {
    // Fallback when dedicated WP page is missing: same booking URL + ?dziekujemy=1
    [...url.searchParams.keys()].forEach((key) => {
      if (!BOOKING_ATTRIBUTION_KEYS.includes(key)) url.searchParams.delete(key);
    });
    url.searchParams.set('dziekujemy', '1');
  }

  if (snapshot?.reference) url.searchParams.set('ref', snapshot.reference);
  if (snapshot?.offerTitle) url.searchParams.set('offer_title', snapshot.offerTitle);
  applyAttributionToUrl(url);
  return `${url.pathname}${url.search}${url.hash}`;
};

const buildCourseSidebarFeatures = (root, config, packages = []) => {
  const pkg = packages.find((item) => item.id === config.package);
  const features = [];
  if (pkg?.note) features.push(pkg.note);

  const course = config.course || root?.dataset?.course || '';
  const isAdults = course.includes('dorosl') || course.includes('adult');
  if (!isAdults) {
    features.push('Grupa 4–12 lat');
  }

  const term = getSelectedCourseTermOption(root);
  const days = Number(term.days || 0);
  if (days > 0) {
    features.push(days === 1 ? '1 dzień' : `${days} dni`);
  }

  if (features.length) return features;

  const fromDom = parseJsonDataset(root?.dataset?.bookingFeatures, []);
  return fromDom
    .map((item) => (typeof item === 'string' ? item : item?.label || ''))
    .filter(Boolean);
};

const collectCourseProductSnapshot = (root, config, packages = []) => ({
  course_label: config.course_label || '',
  package: config.package || '',
  package_label: config.package_label || '',
  price: config.price || 0,
  discipline: config.discipline || '',
  location: config.location || '',
  date_from: config.date_from || '',
  date_to: config.date_to || '',
  features: buildCourseSidebarFeatures(root, config, packages),
});

const collectBookingPayload = (root, bookingType) => {
  const payload = {
    type: bookingType,
    offer_id: new URLSearchParams(window.location.search).get('offer') || '',
    page_id: root.dataset.pageId || '',
    contact: collectContact(root, bookingType),
    details: {},
    crm_lead_submission_id: root.dataset.crmLeadId || '',
  };

  if (bookingType === 'travel') {
    const travelCards = [...root.querySelectorAll('.booking-travel-participant')];
    const disciplineFlags = travelDisciplineFlagsFromConfig(parseTravelAddonsConfig(root));
    payload.details = {
      adults: Number(root.querySelector('.js-booking-adults')?.value || 1),
      children: Number(root.querySelector('.js-booking-children')?.value || 0),
      children_ages: collectTravelChildrenAges(root, travelCards),
      insurance: false,
      nnw_insurance: false,
      training: travelCards.some((card) => card.dataset.training === '1'),
      cancellation_insurance: root.querySelector('.js-booking-cancellation-toggle')?.getAttribute('aria-pressed') === 'true',
      notes: '',
      term: new URLSearchParams(window.location.search).get('term') || '0',
      room: root.dataset.roomTitle
        || root.querySelector('.js-booking-sidebar-room')?.textContent?.trim()
        || '',
      room_index: root.dataset.roomIndex
        || new URLSearchParams(window.location.search).get('room')
        || '',
      skip_apartment: root.dataset.skipApartment === '1',
      date_from: travelTripDate(root),
      offer: collectTravelOfferSnapshot(root),
      participants: travelCards.map((card, index) => {
        const rate = card.dataset.training === '1'
          ? normalizeTravelTrainingRate(card.dataset.trainingRate)
          : 'standard';
        const altMeta = travelAddonTrainingAlt(parseTravelAddonsConfig(root));
        return {
          first_name: readFieldValue(card, `participant-first-name-${index + 1}`),
          last_name: readFieldValue(card, `participant-last-name-${index + 1}`),
          birth: polishBirthToIso(readTravelParticipantBirthValue(card)),
          notes: readFieldValue(card, `participant-notes-${index + 1}`),
          training: card.dataset.training === '1',
          training_discipline: normalizeTravelTrainingDiscipline(card.dataset.discipline || '', disciplineFlags),
          training_asked: card.dataset.trainingAsked === '1',
          training_rate: rate,
          training_rate_label: rate === 'alt' ? altMeta.label : '',
          nnw: false,
          cancellation: false,
          insurance: false,
          is_orderer: Boolean(card.querySelector('.js-travel-is-orderer')?.checked),
          guest_role: card.dataset.guestRole === 'child' ? 'child' : 'adult',
          saved: card.dataset.saved === '1',
        };
      }),
      total: root.querySelector('.js-booking-total')?.textContent || '',
    };
  }

  if (bookingType === 'course') {
    const config = getCourseConfig(root);
    if (config.offer_id) payload.offer_id = config.offer_id;
    payload.details = {
      course: config.course,
      course_label: config.course_label,
      course_type: config.course_type,
      package: config.package,
      package_label: config.package_label,
      price: String(config.price),
      discipline: config.discipline,
      location: config.location,
      location_label: config.location_label || '',
      offer_id: config.offer_id || payload.offer_id || '',
      audience: config.audience || '',
      date_from: config.date_from,
      date_to: config.date_to,
      product: collectCourseProductSnapshot(root, config, resolveCoursePackages(root)),
      students: [...root.querySelectorAll('.booking-course-participant')].map((card, studentIndex) => {
        const index = card.dataset.studentIndex || '1';
        const pkg = card.querySelector('.js-course-student-package:checked');
        const meals = collectCourseStudentMeals(card);
        const siblingEnabled = shouldApplyCourseSiblingDiscount(
          config.audience || 'children',
          root.querySelectorAll('.booking-course-participant').length,
        );
        const quote = courseStudentQuoteParts(card, studentIndex + 1, config.price, { siblingEnabled });
        return {
          first_name: readFieldValue(card, `student-first-name-${index}`),
          last_name: readFieldValue(card, `student-last-name-${index}`),
          birth: polishBirthToIso(readFieldValue(card, `student-birth-${index}`)),
          notes: readFieldValue(card, `student-notes-${index}`),
          package: pkg?.value || '',
          package_label: pkg?.dataset.label || '',
          package_age_ack: card.dataset.packageAgeAck === '1',
          meal_plans: meals,
          meal_price: quote.mealPrice,
          meal_days: meals.reduce((sum, meal) => sum + (Number(meal.days) || 0), 0),
          meal_price_per_day: meals.reduce(
            (max, meal) => Math.max(max, Number(meal.price_per_day) || 0),
            0,
          ),
          package_price: quote.packagePrice,
          course_price: quote.coursePrice,
          sibling_ordinal: quote.siblingOrdinal,
          sibling_discount_percent: quote.siblingPercent,
          sibling_discount_amount: quote.siblingDiscount,
          sibling_discount_label: quote.siblingLabel,
          price: String(quote.lineNet),
        };
      }),
      total: root.querySelector('.js-booking-total')?.textContent || '',
    };
  }

  if (bookingType === 'lesson') {
    const config = getLessonConfig(root);
    const students = [...root.querySelectorAll('.booking-lesson-participant')].map((card) => ({
      first_name: card.querySelector('[name^="student-first-name-"]')?.value?.trim() || '',
      last_name: card.querySelector('[name^="student-last-name-"]')?.value?.trim() || '',
      birth: polishBirthToIso(card.querySelector('[name^="student-birth-"]')?.value?.trim() || ''),
      student_type: card.querySelector('[name^="student-type-"]')?.value || 'adult',
      skill_level: card.querySelector('[name^="student-skill-"]')?.value || '',
      is_orderer: Boolean(card.querySelector('[name^="student-is-orderer-"]')?.checked),
    }));
    payload.details = {
      lesson_type: config.lessonType,
      participants: String(students.length || config.participants),
      hours: String(config.hours),
      discipline: config.discipline,
      location: config.location,
      lesson_date: config.lessonDate,
      lesson_time: config.lessonTime,
      student: students[0] || {},
      students,
      lesson_schedule: [...root.querySelectorAll('.lesson-schedule__slot')].map((slot) => ({
        date: slot.dataset.date || '',
        hours: Number(slot.dataset.hours || slot.querySelector('.js-lesson-slot-hours')?.value || 0),
        start: slot.dataset.start || '',
        end: slot.dataset.end || '',
        times: [slot.dataset.start, slot.dataset.end].filter(Boolean),
      })),
      total: root.querySelector('.js-booking-total')?.textContent || '',
    };
  }

  return payload;
};

const buildCoursePackageOptions = (index, selectedId, packages = []) => packages.map((pkg) => {
  const checked = pkg.id === selectedId ? ' checked' : '';
  const selectedClass = pkg.id === selectedId ? ' is-selected' : '';
  const badge = pkg.badge
    ? `<span class="course-package-option__badge"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m12 2 2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z"/></svg>${escapeHtml(pkg.badge)}</span>`
    : '';
  const mealOptions = Array.isArray(pkg.meal_options) ? pkg.meal_options : [];
  const mealOptionsJson = escapeHtml(JSON.stringify(mealOptions));
  const mealUi = pkg.meal_ui === 'multiple' ? 'multiple' : 'single';

  return `
    <div class="course-package-option${selectedClass}">
      <label class="course-package-option__pick">
        <input
          type="radio"
          name="student-package-${index}"
          value="${escapeHtml(pkg.id)}"
          class="js-course-student-package"
          data-price="${pkg.price}"
          data-label="${escapeHtml(pkg.label)}"
          data-note="${escapeHtml(pkg.note)}"
          data-meal-ui="${mealUi}"
          data-meal-options="${mealOptionsJson}"${checked}
        >
        <span class="course-package-option__radio" aria-hidden="true"></span>
        <span class="course-package-option__body">
          <span class="course-package-option__head">
            <span class="course-package-option__name">${escapeHtml(pkg.label)}</span>
            ${badge}
          </span>
          <span class="course-package-option__note">${escapeHtml(pkg.note)}</span>
        </span>
        <span class="course-package-option__price">${formatCourseMoney(pkg.price)} zł</span>
      </label>
    </div>
  `;
}).join('');

const parsePackageMealOptions = (pkgOrInput) => {
  if (!pkgOrInput) return [];
  if (Array.isArray(pkgOrInput.meal_options)) {
    return normalizeMealOptions(pkgOrInput.meal_options);
  }
  const raw = pkgOrInput.dataset?.mealOptions || '';
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return normalizeMealOptions(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
};

const parsePackageMealUi = (pkgOrInput) => {
  const raw = pkgOrInput?.meal_ui || pkgOrInput?.dataset?.mealUi || 'single';
  return raw === 'multiple' ? 'multiple' : 'single';
};

const COURSE_MEAL_HELP_FALLBACK =
  'Polecamy wykupić dodatkowo obiad dla dziecka pod opieką instruktorów w pakiecie podstawowym od 12:00-13:00. Koszt obiadu to 30zł/dzień, czyli za 5 obiadów według programu to 150zł. Jeśli nie zdecydują się Państwo na wykupienie obiadów, dziecko o godz. 12:00 jest przekazywane Państwu w wyznaczonym miejscu, następnie oddawane o godz. 13:00 na dalsze zajęcia z instruktorem.';

const resolveCourseMealHelp = () => {
  const el = document.querySelector('.js-booking[data-booking-type="course"]');
  const fromDom = (el?.dataset?.mealTooltip || '').trim();
  return fromDom || ui(COURSE_MEAL_HELP_FALLBACK);
};

const buildCourseMealOptionsMarkup = (index, mealOptions = [], selectedIds = [], mealUi = 'single') => {
  const selected = new Set(selectedIds);
  const isMultiple = mealUi === 'multiple';
  const hint = isMultiple
    ? ui('Domyślnie: Tak. Możesz wybrać jeden lub kilka planów.')
    : ui('Domyślnie: Tak.');
  const mealHelp = resolveCourseMealHelp();
  // Sam data-tooltip (CSS) — bez title, żeby nie było podwójnego tooltipa przeglądarki.
  const mealHelpBtn = `
        <button
          type="button"
          class="booking-form__help"
          aria-label="${escapeHtml(mealHelp)}"
          data-tooltip="${escapeHtml(mealHelp)}"
        >?</button>`;

  if (!isMultiple) {
    // Pakiet podstawowy: Tak / Nie — Tak pierwsze i domyślne.
    const yesMeal = (mealOptions || []).find((meal) => meal.id === 'obiad')
      || (mealOptions || [])[0]
      || null;
    const explicitNone = selected.has('__none__') || selected.has('brak');
    const yesSelected = Boolean(
      yesMeal && !explicitNone && (selected.size === 0 || selected.has(yesMeal.id)),
    );
    const noneChecked = !yesSelected ? ' checked' : '';
    const yesChecked = yesSelected ? ' checked' : '';
    const yesPrice = yesMeal ? (Number(yesMeal.price) || 0) : 0;
    const yesRate = yesMeal ? (Number(yesMeal.price_per_day) || 0) : 0;
    const yesDays = yesMeal ? (Number(yesMeal.days) || 0) : 0;
    const yesPriceLabel = yesMeal ? formatCourseMealChoicePrice(yesMeal) : '';
    // Summary/sidebar: show choice ("Tak"/"Nie"), not the field/plan name ("Obiad").
    const radios = yesMeal ? `
      <label class="course-meal-option${yesSelected ? ' is-selected' : ''}">
        <input
          type="radio"
          name="student-meal-${index}"
          value="${escapeHtml(yesMeal.id)}"
          class="js-course-student-meal"
          data-price="${yesPrice}"
          data-price-per-day="${yesRate}"
          data-days="${yesDays}"
          data-label="${escapeHtml(ui('Tak'))}"${yesChecked}
        >
        <span class="course-meal-option__radio" aria-hidden="true"></span>
        <span class="course-meal-option__body">
          <span class="course-meal-option__name">${escapeHtml(ui('Tak'))}</span>
          ${yesPriceLabel ? `<span class="course-meal-option__price">${escapeHtml(yesPriceLabel)}</span>` : ''}
        </span>
      </label>
      <label class="course-meal-option${!yesSelected ? ' is-selected' : ''}">
        <input
          type="radio"
          name="student-meal-${index}"
          value=""
          class="js-course-student-meal"
          data-price="0"
          data-label="${escapeHtml(ui('Nie'))}"${noneChecked}
        >
        <span class="course-meal-option__radio" aria-hidden="true"></span>
        <span class="course-meal-option__body">
          <span class="course-meal-option__name">${escapeHtml(ui('Nie'))}</span>
        </span>
      </label>
    ` : '';

    return `
    <div class="course-meal-picker" data-meal-ui="single" data-has-options="${yesMeal ? '1' : '0'}">
      <span class="booking-form__label">${escapeHtml(ui('Obiad'))}${mealHelpBtn}</span>
      <p class="course-meal-picker__hint">${hint}</p>
      <div class="course-meal-picker__list" role="radiogroup" aria-label="${escapeHtml(uiFmt('Obiad kursanta nr %s', index))}">
        ${radios}
      </div>
    </div>
  `;
  }

  // Multiple: domyślnie zaznacz „obiad” (lub pierwszy plan), gdy brak wyboru.
  // `__none__` = użytkownik świadomie odznaczył wszystko.
  const activeSelected = (() => {
    if (selected.has('__none__') || selected.has('brak')) return new Set();
    if (selected.size > 0) return selected;
    const yesMeal = (mealOptions || []).find((meal) => meal.id === 'obiad')
      || (mealOptions || [])[0]
      || null;
    return yesMeal ? new Set([yesMeal.id]) : selected;
  })();

  const options = (mealOptions || []).map((meal) => {
    const checked = activeSelected.has(meal.id) ? ' checked' : '';
    const selectedClass = activeSelected.has(meal.id) ? ' is-selected' : '';
    const priceLabel = formatCourseMealChoicePrice(meal);
    return `
      <label class="course-meal-option${selectedClass}">
        <input
          type="checkbox"
          name="student-meal-${index}"
          value="${escapeHtml(meal.id)}"
          class="js-course-student-meal"
          data-price="${Number(meal.price) || 0}"
          data-price-per-day="${Number(meal.price_per_day) || 0}"
          data-days="${Number(meal.days) || 0}"
          data-label="${escapeHtml(meal.label || meal.id)}"${checked}
        >
        <span class="course-meal-option__box" aria-hidden="true"></span>
        <span class="course-meal-option__body">
          <span class="course-meal-option__name">${escapeHtml(meal.label || meal.id)}</span>
          ${priceLabel ? `<span class="course-meal-option__price">${escapeHtml(priceLabel)}</span>` : ''}
        </span>
      </label>
    `;
  }).join('');

  return `
    <div class="course-meal-picker" data-meal-ui="multiple" data-has-options="${mealOptions.length ? '1' : '0'}">
      <span class="booking-form__label">${escapeHtml(ui('Obiad'))}${mealHelpBtn}</span>
      <p class="course-meal-picker__hint">${hint}</p>
      <div class="course-meal-picker__list" role="group" aria-label="${escapeHtml(uiFmt('Obiad kursanta nr %s', index))}">
        ${options}
      </div>
    </div>
  `;
};

const collectCourseStudentMeals = (card) => [...card.querySelectorAll('.js-course-student-meal:checked')]
  .filter((input) => Boolean(input.value))
  .map((input) => ({
    id: input.value || '',
    label: input.dataset.label || input.value || '',
    price: Number(input.dataset.price) || 0,
    price_per_day: Number(input.dataset.pricePerDay) || 0,
    days: Number(input.dataset.days) || 0,
  }));

/** Label for "Obiad: …" rows — choice value, never the field name. */
const formatCourseMealChoiceLabel = (card, meals = collectCourseStudentMeals(card)) => {
  const mealUi = card?.querySelector?.('.course-meal-picker')?.dataset?.mealUi
    || card?.querySelector?.('.js-course-student-package:checked')?.dataset?.mealUi
    || 'single';
  if (mealUi !== 'multiple') {
    return meals.length ? ui('Tak') : ui('Nie');
  }
  if (!meals.length) return ui('Nie');
  return meals.map((meal) => meal.label).filter(Boolean).join(', ') || ui('Nie');
};

const courseStudentMealPrice = (card) => collectCourseStudentMeals(card).reduce((sum, meal) => sum + meal.price, 0);

/** Lustro SchoolCoursePricing::SIBLING_DISCOUNT_* — SSOT jest po stronie PHP przy submit. */
const SIBLING_DISCOUNT_2ND_PERCENT = 5;
const SIBLING_DISCOUNT_3RD_PLUS_PERCENT = 10;

const siblingDiscountPercentForOrdinal = (ordinal) => {
  if (ordinal <= 1) return 0;
  if (ordinal === 2) return SIBLING_DISCOUNT_2ND_PERCENT;
  return SIBLING_DISCOUNT_3RD_PLUS_PERCENT;
};

const siblingDiscountAmount = (coursePrice, percent) => {
  const price = Math.max(0, Number(coursePrice) || 0);
  const pct = Math.max(0, Number(percent) || 0);
  if (!price || !pct) return 0;
  return Math.round(price * (pct / 100));
};

const siblingDiscountLabel = (ordinal, percent) => {
  if (percent <= 0 || ordinal < 2) return '';
  return uiFmt('Zniżka rodzeństwa (%s. dziecko −%s%)', ordinal, percent);
};

const shouldApplyCourseSiblingDiscount = (audience, studentCount) => {
  if (studentCount < 2) return false;
  if (audience === 'adults') return false;
  return true;
};

/**
 * @returns {{
 *   packagePrice: number,
 *   mealPrice: number,
 *   coursePrice: number,
 *   siblingOrdinal: number,
 *   siblingPercent: number,
 *   siblingDiscount: number,
 *   siblingLabel: string,
 *   lineGross: number,
 *   lineNet: number,
 * }}
 */
const courseStudentQuoteParts = (card, ordinal, fallbackPackagePrice = 0, { siblingEnabled = false } = {}) => {
  const pkg = card.querySelector('.js-course-student-package:checked');
  const packagePrice = Number(pkg?.dataset.price || fallbackPackagePrice) || 0;
  const mealPrice = courseStudentMealPrice(card);
  const siblingPercent = siblingEnabled ? siblingDiscountPercentForOrdinal(ordinal) : 0;
  const siblingDiscount = siblingDiscountAmount(packagePrice, siblingPercent);
  const coursePrice = Math.max(0, packagePrice - siblingDiscount);
  return {
    packagePrice,
    mealPrice,
    coursePrice,
    siblingOrdinal: ordinal,
    siblingPercent,
    siblingDiscount,
    siblingLabel: siblingDiscountLabel(ordinal, siblingPercent),
    lineGross: packagePrice + mealPrice,
    lineNet: coursePrice + mealPrice,
  };
};

const courseStudentLinePrice = (card, fallbackPackagePrice = 0, ordinal = 1, siblingEnabled = false) => {
  const parts = courseStudentQuoteParts(card, ordinal, fallbackPackagePrice, { siblingEnabled });
  return parts.lineNet;
};

const syncCourseStudentMeals = (card, packages = [], { keepSelection = true } = {}) => {
  const index = card.dataset.studentIndex || '1';
  const pkgInput = card.querySelector('.js-course-student-package:checked');
  const pkg = findCoursePackage(packages, pkgInput?.value)
    || { meal_options: parsePackageMealOptions(pkgInput), meal_ui: parsePackageMealUi(pkgInput) };
  const mealOptions = Array.isArray(pkg.meal_options) ? pkg.meal_options : parsePackageMealOptions(pkgInput);
  const mealUi = parsePackageMealUi(pkg.meal_ui ? pkg : pkgInput);
  const hadMealsUi = Boolean(card.querySelector('.js-course-student-meals'));
  const explicitNone = Boolean(card.querySelector('.js-course-student-meal[value=""]:checked'));
  const prevSelected = keepSelection
    ? collectCourseStudentMeals(card).map((meal) => meal.id)
    : [];
  const allowedIds = new Set(mealOptions.map((meal) => meal.id));
  let selectedIds = prevSelected.filter((id) => allowedIds.has(id));
  if (mealUi === 'single' && selectedIds.length > 1) {
    selectedIds = selectedIds.slice(0, 1);
  }
  // Zachowaj jawne „Nie” / odznaczenie przy przebudowie (pusty wybór nie trafia do collect).
  if (keepSelection && hadMealsUi && selectedIds.length === 0) {
    if (mealUi === 'single' && explicitNone) {
      selectedIds = ['__none__'];
    } else if (mealUi === 'multiple') {
      selectedIds = ['__none__'];
    }
  }

  // Obiad tylko pod wybranym pakietem z meal_options — usuń poprzednie bloki.
  card.querySelectorAll('.js-course-student-meals').forEach((el) => el.remove());

  if (!pkgInput || !mealOptions.length) return;

  const option = pkgInput.closest('.course-package-option');
  if (!option) return;

  const wrap = document.createElement('div');
  wrap.className = 'course-student-card__meals js-course-student-meals';
  wrap.innerHTML = buildCourseMealOptionsMarkup(index, mealOptions, selectedIds, mealUi);
  option.appendChild(wrap);
};

const buildCourseStudentCard = (index, selectedPackageId, packages = [], calendarIcon = '') => {
  const calendarIconMarkup = calendarIcon
    ? `<img src="${escapeHtml(calendarIcon)}" alt="" width="20" height="20">`
    : '';
  const fallbackId = packages[0]?.id || 'podstawowy';
  const selectedId = packages.some((pkg) => pkg.id === selectedPackageId)
    ? selectedPackageId
    : fallbackId;
  const card = document.createElement('article');
  card.className = 'course-student-card booking-course-participant';
  card.dataset.studentIndex = String(index);
  card.innerHTML = `
    <div class="course-student-card__header">
      <h3 class="course-student-card__title">${escapeHtml(uiFmt('Kursant nr %s', index))}</h3>
      <button type="button" class="course-student-card__remove js-course-student-remove">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.4"/></svg>
        ${escapeHtml(ui('Usuń'))}
      </button>
    </div>
    <div class="course-student-card__fields">
      <label class="booking-form__field">
        <span class="booking-form__label">${escapeHtml(ui('Imię'))} <abbr title="${escapeHtml(ui('wymagane'))}">*</abbr></span>
        <input type="text" name="student-first-name-${index}" placeholder="${escapeHtml(ui('Imię'))}" required autocomplete="given-name">
      </label>
      <label class="booking-form__field">
        <span class="booking-form__label">${escapeHtml(ui('Nazwisko'))} <abbr title="${escapeHtml(ui('wymagane'))}">*</abbr></span>
        <input type="text" name="student-last-name-${index}" placeholder="${escapeHtml(ui('Nazwisko'))}" required autocomplete="family-name">
      </label>
      <label class="booking-form__field">
        <span class="booking-form__label">${escapeHtml(ui('Data urodzenia'))} <abbr title="${escapeHtml(ui('wymagane'))}">*</abbr></span>
        <span class="booking-form__control booking-form__control--date">
          <input
            type="text"
            name="student-birth-${index}"
            class="js-course-student-birth"
            inputmode="numeric"
            placeholder="DD.MM.RRRR"
            autocomplete="bday"
            required
          >
          <span class="booking-form__control-icon js-birth-datepicker-icon" aria-hidden="true">
            ${calendarIconMarkup}
          </span>
        </span>
      </label>
    </div>
    <div class="course-student-card__packages">
      <span class="booking-form__label">${escapeHtml(ui('Pakiet'))} <abbr title="${escapeHtml(ui('wymagane'))}">*</abbr></span>
      <div class="course-package-picker" role="radiogroup" aria-label="${escapeHtml(uiFmt('Pakiet kursanta nr %s', index))}">
        ${buildCoursePackageOptions(index, selectedId, packages)}
      </div>
    </div>
    <label class="booking-form__field course-student-card__notes">
      <span class="booking-form__label">
        ${escapeHtml(ui('Uwagi'))}
        <span class="booking-form__optional">${escapeHtml(ui('Opcjonalnie'))}</span>
      </span>
      <input type="text" name="student-notes-${index}" placeholder="${escapeHtml(ui('Brak'))}">
    </label>
  `;

  syncCourseStudentMeals(card, packages, { keepSelection: false });
  return card;
};

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isBookingFieldFilled = (field) => {
  if (!field) return false;
  if (field.type === 'checkbox') return field.checked;
  if (field.type === 'tel' || field.name === 'phone' || field.classList.contains('js-booking-phone')) {
    syncPhoneFieldValidity(field);
    return isValidBookingPhone(field);
  }
  if (field.type === 'email') {
    return Boolean(field.value?.trim()) && field.checkValidity();
  }
  if (field.name === 'birth-date' || field.classList.contains('js-booking-birth')) {
    return isAdultBirthDate(field.value?.trim());
  }
  return Boolean(field.value?.trim());
};

const syncInvalidWrappers = (field, invalid) => {
  if (!field) return;
  field.closest('.booking-select')?.classList.toggle('is-invalid', invalid);
  field.closest('.booking-form__control--date')?.classList.toggle('is-invalid', invalid);
  field.closest('.booking-form__control--phone')?.classList.toggle('is-invalid', invalid);
  field.closest('.js-lesson-student-birth')?.classList.toggle('is-invalid', invalid);
  field.closest('.lesson-student-card__birth-field')?.classList.toggle('is-invalid', invalid);
};

const setBookingFieldInvalid = (field, invalid) => {
  if (!field) return;
  field.classList.toggle('is-invalid', Boolean(invalid));
  syncInvalidWrappers(field, Boolean(invalid));
};

const scrollToFirstInvalid = (scope) => {
  if (!scope) return;
  const invalid = scope.querySelector('.is-invalid, .js-travel-save.is-attention');
  if (!invalid) return;
  const target = invalid.matches('input, select, textarea')
    ? (invalid.closest('.booking-form__field, .booking-form__control, .booking-select, .js-lesson-student-birth, .travel-participant, .course-package-picker, .booking-form__checkbox') || invalid)
    : invalid;
  target.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'center',
    inline: 'nearest',
  });
  const focusEl = invalid.matches('input, select, textarea, button')
    ? invalid
    : invalid.querySelector('input:not([type="hidden"]), select, textarea, button');
  window.setTimeout(() => {
    try {
      focusEl?.focus({ preventScroll: true });
    } catch {
      /* ignore */
    }
  }, prefersReducedMotion() ? 0 : 280);
};

const clearBookingFieldIfValid = (field) => {
  if (!field?.matches?.('input, textarea, select')) return;
  if (!field.classList.contains('is-invalid')) return;
  if (!isBookingFieldFilled(field)) return;
  setBookingFieldInvalid(field, false);
  if (field.classList.contains('js-travel-orderer-birth') || field.name === 'birth-date') {
    setOrdererBirthError(
      field.closest('[data-booking-step], .booking-page__panel, form') || field.closest('.js-booking'),
      '',
    );
  }
  const card = field.closest('.booking-travel-participant');
  if (card && !card.querySelector('.is-invalid')) card.classList.remove('is-invalid');
};

const setTravelBirthFieldInvalid = (card, invalid, message = '') => {
  if (!card) return;
  const index = card.dataset.participantIndex || '1';
  const birth = card.querySelector(`[name="participant-birth-${index}"]`);
  const errorEl = card.querySelector('.js-travel-birth-error');
  setBookingFieldInvalid(birth, invalid);
  if (errorEl) errorEl.textContent = invalid ? (message || '') : '';
};

const isTravelParticipantCardComplete = (card) => {
  if (!card) return false;
  const index = card.dataset.participantIndex || '1';
  const first = card.querySelector(`[name="participant-first-name-${index}"]`);
  const last = card.querySelector(`[name="participant-last-name-${index}"]`);
  const birth = card.querySelector(`[name="participant-birth-${index}"]`);
  if (!first?.value?.trim() || !last?.value?.trim()) return false;
  const birthResult = validateBirthDatePicker(card._birthMask || birth, {
    required: true,
    maxDate: new Date(),
    fieldLabel: ui('Data urodzenia'),
  });
  return Boolean(birthResult?.valid);
};

const validateStep = (root, step, bookingType, { markInvalid = true } = {}) => {
  const panel = root.querySelector(`[data-booking-step="${step}"]`);
  if (!panel) return true;

  if (bookingType === 'travel' && step === TRAVEL_PARTICIPANTS_STEP) {
    return validateTravelParticipantsStep(root, { markInvalid });
  }

  const requiredFields = [...panel.querySelectorAll('[required]')];
  let valid = true;

  requiredFields.forEach((field) => {
    if (
      bookingType === 'travel'
      && step === TRAVEL_ORDERER_STEP
      && (field.name === 'birth-date' || field.classList.contains('js-travel-orderer-birth'))
    ) {
      const birthResult = validateOrdererBirthDate(field._birthDatePicker || field, { required: true });
      if (markInvalid) {
        setBookingFieldInvalid(field, !birthResult.valid);
        setOrdererBirthError(panel, birthResult.valid ? '' : (birthResult.message || ORDERER_AGE_MESSAGE()));
      }
      if (!birthResult.valid) valid = false;
      return;
    }

    const fieldValid = isBookingFieldFilled(field);
    if (markInvalid) setBookingFieldInvalid(field, !fieldValid);
    if (!fieldValid) valid = false;
  });

  return valid;
};

const validateTravelParticipantsStep = (root, { markInvalid = true } = {}) => {
  const cards = [...root.querySelectorAll('.booking-travel-participant')];
  let valid = true;

  cards.forEach((card) => {
    const saved = card.dataset.saved === '1';
    const saveBtn = card.querySelector('.js-travel-save');
    if (!saved) {
      valid = false;
      if (markInvalid) {
        expandTravelParticipantCard(card);
        const complete = isTravelParticipantCardComplete(card);
        if (complete) {
          // Dane OK — podświetl tylko „Zapisz dane”, nie całą kartę.
          card.querySelectorAll('[required]').forEach((field) => {
            if (field.classList.contains('js-travel-birth')) return;
            setBookingFieldInvalid(field, false);
          });
          setTravelBirthFieldInvalid(card, false);
          card.classList.remove('is-invalid');
          saveBtn?.classList.add('is-attention');
        } else {
          saveBtn?.classList.remove('is-attention');
          const index = card.dataset.participantIndex || '1';
          const first = card.querySelector(`[name="participant-first-name-${index}"]`);
          const last = card.querySelector(`[name="participant-last-name-${index}"]`);
          const birth = card.querySelector(`[name="participant-birth-${index}"]`);
          setBookingFieldInvalid(first, !first?.value?.trim());
          setBookingFieldInvalid(last, !last?.value?.trim());
          const birthResult = validateBirthDatePicker(card._birthMask || birth, {
            required: true,
            maxDate: new Date(),
            fieldLabel: ui('Data urodzenia'),
          });
          setTravelBirthFieldInvalid(card, !birthResult.valid, birthResult.message || '');
          card.classList.add('is-invalid');
        }
      }
      return;
    }
    if (markInvalid) {
      card.classList.remove('is-invalid');
      saveBtn?.classList.remove('is-attention');
      setTravelBirthFieldInvalid(card, false);
    }
  });

  const privacy = root.querySelector('.js-booking-step2-privacy');
  if (privacy && !privacy.checked) {
    valid = false;
    if (markInvalid) setBookingFieldInvalid(privacy, true);
  } else if (privacy && markInvalid) {
    setBookingFieldInvalid(privacy, false);
  }

  return valid;
};

const expandTravelParticipantCard = (card) => {
  if (!card) return;
  card.dataset.saved = '0';
  card.classList.remove('is-saved');
  card.querySelector('.js-travel-edit-body')?.removeAttribute('hidden');
  card.querySelector('.js-travel-summary-body')?.setAttribute('hidden', '');
  refreshTravelParticipantStatus(card);
};

const refreshTravelParticipantStatus = (card) => {
  if (!card) return;
  const isOrderer = Boolean(card.querySelector('.js-travel-is-orderer')?.checked);
  const ordererChip = card.querySelector('.js-travel-orderer-chip');
  if (ordererChip) ordererChip.classList.toggle('is-checked', isOrderer);
  card.classList.toggle('is-orderer', isOrderer);

  const form = card.closest('.js-booking-participants-form');
  const total = form?.querySelectorAll('.booking-travel-participant').length || 1;
  const index = card.dataset.participantIndex || '1';
  const saved = card.dataset.saved === '1';
  const status = card.querySelector('.js-travel-status');
  if (status) {
    status.textContent = saved
      ? bookingTx(card, 'status_saved', 'Zapisano')
      : bookingTx(card, 'status_fill', '{index}/{total} - Wypełnij', { index, total });
    status.classList.toggle('is-saved', saved);
    status.hidden = saved;
  }
  const editBtn = card.querySelector(':scope > .travel-participant__head .js-travel-edit');
  if (editBtn) editBtn.hidden = !saved;
};

/** Regulamin / polityka — modal jak w makiecie (bez opuszczania strony). */
const legalModalCopy = () => ({
  regulamin: {
    eyebrow: ui('REGULAMIN'),
    title: ui('Regulamin szkoleń HSKI'),
  },
  privacy: {
    eyebrow: ui('POLITYKA PRYWATNOŚCI'),
    title: ui('Polityka prywatności HSKI'),
  },
  polityka: {
    eyebrow: ui('POLITYKA PRYWATNOŚCI'),
    title: ui('Polityka prywatności HSKI'),
  },
});

const legalContentCache = new Map();

const parseLegalDocument = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const title = doc.querySelector('.legal-page__title')?.textContent?.trim() || '';
  const content = doc.querySelector('.legal-page__content')?.innerHTML?.trim() || '';
  return { title, content };
};

const loadLegalDocument = async (url) => {
  if (legalContentCache.has(url)) return legalContentCache.get(url);
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const parsed = parseLegalDocument(await response.text());
  legalContentCache.set(url, parsed);
  return parsed;
};

const initBookingLegalModals = (root) => {
  const modal = root.querySelector('.js-booking-legal-modal');
  const bodyEl = root.querySelector('.js-booking-legal-body');
  const titleEl = root.querySelector('#booking-legal-modal-title');
  const eyebrowEl = root.querySelector('.js-booking-legal-eyebrow');
  const acceptBtn = root.querySelector('.js-booking-legal-accept');
  if (!modal || !bodyEl) return;

  let activeCheckbox = null;
  let loadToken = 0;

  const close = () => {
    if (typeof modal.close === 'function' && modal.open) modal.close();
    else modal.removeAttribute('open');
    activeCheckbox = null;
  };

  const setMeta = (legalKey, pageTitle = '') => {
    const catalog = legalModalCopy();
    const copy = catalog[legalKey] || catalog.regulamin;
    if (eyebrowEl) eyebrowEl.textContent = copy.eyebrow;
    if (titleEl) titleEl.textContent = pageTitle || copy.title;
  };

  const inlineDocs = (() => {
    const el = root.querySelector('.js-booking-legal-docs');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch {
      return null;
    }
  })();

  const open = (url, legalKey, checkbox) => {
    activeCheckbox = checkbox || null;
    loadToken += 1;
    const token = loadToken;
    setMeta(legalKey);
    bodyEl.innerHTML = `<p class="booking-legal-modal__loading">${escapeHtml(ui('Wczytywanie…'))}</p>`;
    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', 'open');

    const inline = inlineDocs?.[legalKey];
    if (inline?.html) {
      setMeta(legalKey, inline.title || '');
      bodyEl.innerHTML = inline.html;
      return;
    }

    loadLegalDocument(url)
      .then((doc) => {
        if (token !== loadToken) return;
        setMeta(legalKey, doc.title || inline?.title || '');
        if (doc.content) {
          bodyEl.innerHTML = doc.content;
          return;
        }
        bodyEl.innerHTML = `<p>${escapeHtml(ui('Nie udało się wczytać treści dokumentu.'))} <a href="${url}" target="_blank" rel="noopener">${escapeHtml(ui('Otwórz w nowej karcie'))}</a>.</p>`;
      })
      .catch(() => {
        if (token !== loadToken) return;
        bodyEl.innerHTML = `<p>${escapeHtml(ui('Nie udało się wczytać treści dokumentu.'))} <a href="${url}" target="_blank" rel="noopener">${escapeHtml(ui('Otwórz w nowej karcie'))}</a>.</p>`;
      });
  };

  root.querySelectorAll('.js-booking-legal-open').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const url = link.dataset.legalUrl || link.getAttribute('href');
      if (!url) return;
      const checkbox = link.closest('label')?.querySelector('input[type="checkbox"]') || null;
      open(url, link.dataset.legal || 'regulamin', checkbox);
    });
  });

  root.querySelectorAll('.js-booking-legal-close').forEach((btn) => {
    btn.addEventListener('click', close);
  });

  acceptBtn?.addEventListener('click', () => {
    if (activeCheckbox) {
      activeCheckbox.checked = true;
      activeCheckbox.classList.remove('is-invalid');
      activeCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
    close();
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
};

function initBookingRoot(root) {
  if (!root || root.dataset.bookingInitialized === '1') return;
  root.dataset.bookingInitialized = '1';

  const catalog = parseLessonCatalog(root?.dataset?.schoolLessonCatalog || '');
  if (catalog) setLessonCatalog(catalog);

  const panels = [...root.querySelectorAll('[data-booking-step]')];
  const stepItems = [...root.querySelectorAll('.booking-page__step, .booking-progress__item')];
  const totalEl = root.querySelector('.js-booking-total');
  const adultsSelect = root.querySelector('.js-booking-adults');
  const childrenSelect = root.querySelector('.js-booking-children');
  const insuranceCheck = root.querySelector('.js-booking-insurance');
  const trainingCheck = root.querySelector('.js-booking-training');
  const summaryGuests = root.querySelector('.js-booking-summary-guests');
  const summaryExtras = root.querySelector('.js-booking-summary-extras');
  const summaryContact = root.querySelector('.js-booking-summary-contact');
  const summaryEmail = root.querySelector('.js-booking-summary-email');
  const summaryPhone = root.querySelector('.js-booking-summary-phone');
  const summaryAddress = root.querySelector('.js-booking-summary-address');
  const summaryTerm = root.querySelector('.js-booking-summary-term');
  const summaryPlace = root.querySelector('.js-booking-summary-place');
  const sidebarGuests = root.querySelector('.js-booking-sidebar-guests');
  const summaryCourse = root.querySelector('.js-booking-summary-course');
  const summaryType = root.querySelector('.js-booking-summary-type');
  const sidebarCourse = root.querySelector('.js-booking-sidebar-course');
  const refEl = root.querySelector('.js-booking-ref');
  const submitBtn = root.querySelector('.js-booking-submit');
  const bookingType = root.dataset.bookingType || 'travel';
  let turnstileWidget = null;
  if ((bookingType === 'course' || bookingType === 'lesson') && isCrmDebug()) {
    const { restUrl } = getBookingApi();
    console.info(
      '[HSKI CRM] debug ON — lead before summary, reservation on submit. Disable: localStorage.removeItem("hski_crm_debug")',
    );
    logCrmDebug('init', {
      restUrl,
      type: bookingType,
      course: root.dataset.course,
      package: root.dataset.package,
      date_from: root.dataset.dateFrom,
      date_to: root.dataset.dateTo,
      discipline: root.dataset.discipline || root.dataset.lessonDiscipline,
      location: root.dataset.location || root.dataset.lessonLocation,
      lesson_type: root.dataset.lessonType,
      lesson_hours: root.dataset.lessonHours,
    });
  }
  const tx = parseBookingCopy(root);
  const t = (key, fallback = '', vars = {}) => fillCopy(tx[key] ?? ui(fallback), vars);
  const sidebarMount = root.querySelector('.booking-page__sidebar');
  const queryBookingAll = (selector) => {
    const list = [];
    const seen = new Set();
    [root, sidebarMount].filter(Boolean).forEach((scope) => {
      scope.querySelectorAll(selector).forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        list.push(el);
      });
    });
    return list;
  };
  const queryBookingOne = (selector) => queryBookingAll(selector)[0] || null;
  let stickySummary = { sync() {}, closeDrawer() {} };
  const getCourseCalendarIcon = () => {
    const base = (root.dataset.bookingAssets || '/wp-content/themes/HSKI/assets/images/booking').replace(/\/$/, '');
    return `${base}/calendar.png`;
  };
  const travelAddonsConfig = bookingType === 'travel' ? parseTravelAddonsConfig(root) : {};
  const travelDisciplineFlags = travelDisciplineFlagsFromConfig(travelAddonsConfig);
  const travelShowSki = travelDisciplineFlags.showSki;
  const travelShowSnowboard = travelDisciplineFlags.showSnowboard;
  const travelShowDisciplineUi = travelShowSki || travelShowSnowboard;
  const basePrice = Number(travelAddonsConfig.base_price || root.dataset.basePrice || 1599);
  const trainingPrice = travelAddonPrice(travelAddonsConfig, 'training', Number(root.dataset.trainingPrice || 200));
  const trainingChildPrice = travelAddonChildPrice(travelAddonsConfig, 'training', trainingPrice);
  const trainingAlt = travelAddonTrainingAlt(travelAddonsConfig);
  const nnwPrice = travelAddonPrice(travelAddonsConfig, 'nnw', Number(root.dataset.nnwPrice || 49));
  const cancellationPrice = travelAddonPrice(travelAddonsConfig, 'cancellation', Number(root.dataset.cancellationPrice || 49));
  const insurancePrice = travelAddonPrice(travelAddonsConfig, 'nnw', Number(root.dataset.insurancePrice || 89));
  const maxStep = Math.max(...panels.map((panel) => Number(panel.dataset.bookingStep)));

  let currentStep = 1;
  let participantCount = 1;
  let isSubmitting = false;
  let bookingFinished = false;
  let draftId = null;
  let saveTimer = null;
  let travelOccupancyChanged = false;
  const coursePackages = resolveCoursePackages(root);

  const setBookingCtaMuted = (btn, muted) => {
    if (!btn) return;
    btn.disabled = false;
    btn.removeAttribute('disabled');
    btn.classList.toggle('is-muted', Boolean(muted));
  };

  const updateTravelCtas = () => {
    if (bookingType !== 'travel') return;
    const canProceed = !travelStayError && (
      currentStep === TRAVEL_REVIEW_STEP
      || validateStep(root, currentStep, bookingType, { markInvalid: false })
    );
    queryBookingAll('.js-booking-sidebar-submit').forEach((btn) => {
      setBookingCtaMuted(btn, !canProceed);
    });
    stickySummary.sync();
  };

  const lastFormStep = () => Math.max(1, maxStep - 1);

  const readStepFromUrl = () => {
    const n = Number(new URLSearchParams(window.location.search).get('step'));
    if (!Number.isFinite(n) || n < 1) return null;
    return Math.min(Math.floor(n), lastFormStep());
  };

  const writeStepToUrl = (step, mode = 'push') => {
    if (mode === 'none') return;
    const nextStep = Math.min(Math.max(1, Number(step) || 1), lastFormStep());
    const url = new URL(window.location.href);
    if (nextStep <= 1) url.searchParams.delete('step');
    else url.searchParams.set('step', String(nextStep));
    const href = `${url.pathname}${url.search}${url.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const state = { bookingStep: nextStep };
    if (mode === 'push' && href !== current) {
      history.pushState(state, '', href);
      return;
    }
    history.replaceState(state, '', href);
  };

  const resolveDefaultCoursePackage = (config) => {
    if (coursePackages.some((pkg) => pkg.id === config.package)) {
      return config.package;
    }
    return coursePackages.find((pkg) => pkg.badge)?.id || coursePackages[0]?.id || 'podstawowy';
  };

  const getCounts = () => ({
    adults: Number(adultsSelect?.value || 1),
    children: Number(childrenSelect?.value || 0),
  });

  const persistDraft = ({ step = currentStep } = {}) => {
    if (isSubmitting) return;
    // Nie zapisuj ekranu potwierdzenia jako aktywnego draftu.
    if (step >= maxStep) return;
    try {
      const payload = collectBookingPayload(root, bookingType);
      const draft = upsertDraft({
        type: bookingType,
        step,
        payload,
        resumeUrl: `${window.location.pathname}${window.location.search}`,
      });
      draftId = draft?.id || draftId;
    } catch {
      // localStorage pełny / prywatny tryb — rezerwacja i tak działa bez koszyka
    }
  };

  const schedulePersist = () => {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => persistDraft(), 350);
  };

  const hydrateCourseStudents = (students = []) => {
    if (!Array.isArray(students) || !students.length) return;
    const wrap = root.querySelector('.js-booking-course-participants-wrap');
    if (!wrap) return;

    const config = getCourseConfig(root);
    const defaultPackage = resolveDefaultCoursePackage(config);
    while (wrap.querySelectorAll('.booking-course-participant').length < students.length
      && participantCount < 3) {
      participantCount += 1;
      const card = buildCourseStudentCard(participantCount, defaultPackage, coursePackages, getCourseCalendarIcon());
      wrap.appendChild(card);
      bindCourseStudentCard(card);
    }
    participantCount = wrap.querySelectorAll('.booking-course-participant').length;
    updateCourseStudentsCount(root);
    syncCourseStudentRemoveButtons(root);

    [...wrap.querySelectorAll('.booking-course-participant')].forEach((card, index) => {
      const student = students[index];
      if (!student) return;
      const idx = card.dataset.studentIndex || String(index + 1);
      setNamedField(card, `student-first-name-${idx}`, student.first_name);
      setNamedField(card, `student-last-name-${idx}`, student.last_name);
      {
        const polish = isoBirthToPolish(student.birth);
        if (polish) {
          if (card._birthMask && 'value' in card._birthMask) {
            card._birthMask.value = polish;
          } else {
            setNamedField(card, `student-birth-${idx}`, polish);
          }
        }
      }
      setNamedField(card, `student-notes-${idx}`, student.notes);
      if (student.package_age_ack) {
        card.dataset.packageAgeAck = '1';
      } else {
        delete card.dataset.packageAgeAck;
      }
      if (student.package) {
        const radio = card.querySelector(`.js-course-student-package[value="${CSS.escape(student.package)}"]`);
        if (radio) {
          card._pkgAgeProgrammatic = true;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          card._pkgAgeProgrammatic = false;
        }
      }
      const mealIds = Array.isArray(student.meal_plans)
        ? student.meal_plans.map((meal) => (typeof meal === 'string' ? meal : meal?.id)).filter(Boolean)
        : [];
      syncCourseStudentMeals(card, coursePackages, { keepSelection: false });
      const mealInputs = [...card.querySelectorAll('.js-course-student-meal')];
      if (mealInputs.length) {
        mealInputs.forEach((input) => {
          const on = mealIds.length
            ? mealIds.includes(input.value)
            : input.value === '';
          input.checked = on;
          input.closest('.course-meal-option')?.classList.toggle('is-selected', on);
        });
      }
    });
  };

  const hydrateLessonStudents = (students = []) => {
    if (!Array.isArray(students) || !students.length) return;
    const select = root.querySelector('.js-booking-lesson-participants');
    if (select) {
      select.value = String(students.length);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncLessonParticipantForms();
    [...root.querySelectorAll('.booking-lesson-participant')].forEach((card, index) => {
      const student = students[index];
      if (!student) return;
      const first = card.querySelector('[name^="student-first-name-"]');
      const last = card.querySelector('[name^="student-last-name-"]');
      if (first && student.first_name) first.value = student.first_name;
      if (last && student.last_name) last.value = student.last_name;
      if (student.student_type) {
        applyLessonStudentType(root, card, student.student_type, { skipMixCheck: true });
      }
      if (student.birth) {
        setLessonStudentBirthValue(card, student.birth);
      }
      if (student.skill_level) {
        const skill = card.querySelector('[name^="student-skill-"]');
        if (skill) skill.value = student.skill_level;
      }
      if (student.is_orderer) {
        const box = card.querySelector('[name^="student-is-orderer-"]');
        if (box) box.checked = true;
      }
    });
  };

  const hydrateTravelParticipants = (participants = []) => {
    if (!Array.isArray(participants) || !participants.length) return;
    syncTravelParticipantForms();
    [...root.querySelectorAll('.booking-travel-participant')].forEach((card, index) => {
      const person = participants[index];
      if (!person) return;
      const n = index + 1;
      setNamedField(card, `participant-first-name-${n}`, person.first_name);
      setNamedField(card, `participant-last-name-${n}`, person.last_name);
      {
        const polish = isoBirthToPolish(person.birth);
        if (polish) {
          if (card._birthMask && 'value' in card._birthMask) card._birthMask.value = polish;
          else setNamedField(card, `participant-birth-${n}`, polish);
        }
      }
      syncTravelTrainingPriceDisplay(card);
      setNamedField(card, `participant-notes-${n}`, person.notes || '');

      const orderer = card.querySelector('.js-travel-is-orderer');
      if (orderer) {
        orderer.checked = Boolean(person.is_orderer) && canBeTravelOrderer(card);
      }

      let discipline = person.training_discipline || (person.training ? 'ski' : '');
      discipline = normalizeTravelTrainingDiscipline(discipline, travelDisciplineFlags);
      if (person.training || discipline) {
        card.dataset.training = '1';
        card.dataset.discipline = discipline || defaultTravelTrainingDiscipline(travelDisciplineFlags);
        card.dataset.trainingRate = trainingAlt.enabled
          ? normalizeTravelTrainingRate(person.training_rate || person.training_tier || 'standard')
          : 'standard';
      } else {
        card.dataset.training = '0';
        card.dataset.discipline = '';
        card.dataset.trainingRate = 'standard';
      }
      card.dataset.trainingAsked = person.training_asked ? '1' : '0';
      syncTravelAddonUi(card);

      card.dataset.nnw = '0';

      if (person.saved !== false && person.first_name && person.last_name && person.birth) {
        saveTravelParticipantCard(card, { silent: true });
      } else {
        refreshTravelParticipantStatus(card);
      }
    });
  };

  const readDraftIdFromUrl = () => {
    const id = (new URLSearchParams(window.location.search).get('draft') || '').trim();
    return id || null;
  };

  const hydrateFromDraft = () => {
    const draft = getDraftForPage(bookingType, window.location.search);
    if (!draft?.payload) return null;
    draftId = draft.id;

    hydrateContact(root, draft.payload.contact);
    const details = draft.payload.details || {};
    if (draft.payload.crm_lead_submission_id) {
      root.dataset.crmLeadId = draft.payload.crm_lead_submission_id;
    }

    if (bookingType === 'course') {
      if (details.course_type) {
        const select = root.querySelector('.js-booking-course-type');
        if (select) {
          select.value = details.course_type;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      getCourseConfig(root);
      hydrateCourseStudents(details.students || []);
      updateCourseSummary();
      updateCourseStudentsNextButton(root);
      updateCourseOrdererButton();
    }

    if (bookingType === 'lesson') {
      if (details.lesson_type) {
        const radio = root.querySelector(`.js-booking-lesson-type[value="${CSS.escape(details.lesson_type)}"]`);
        if (radio?.type === 'radio') radio.checked = true;
        const typeHidden = root.querySelector('input.js-booking-lesson-type[type="hidden"]');
        if (typeHidden) typeHidden.value = details.lesson_type;
        root.dataset.lessonType = details.lesson_type;
      }
      if (details.discipline) setNamedField(root, 'discipline', details.discipline);
      if (details.location) setNamedField(root, 'location', details.location);
      if (details.hours) {
        rebuildLessonHoursOptions();
        const hoursSelect = root.querySelector('.js-booking-lesson-hours');
        if (hoursSelect) hoursSelect.value = String(details.hours);
        root.dataset.lessonHours = String(details.hours);
      }
      hydrateLessonStudents(details.students || []);
      (details.lesson_schedule || []).forEach((item) => {
        const start = item.start || item.times?.[0] || '';
        const end = item.end || item.times?.[1] || '';
        if (!item?.date || !start || !end) return;
        upsertLessonScheduleSlot(root, {
          date: item.date,
          start,
          end,
        });
      });
      updateLessonSchedulePanel(root);
      updateLessonSummary();
      updateLessonOrdererButton();
    }

    if (bookingType === 'travel') {
      const merged = applyUrlOccupancyToTravelDetails(details, window.location.search);
      travelOccupancyChanged = merged.occupancyChanged;
      if (!merged.fromUrl) {
        if (adultsSelect) adultsSelect.value = String(merged.adults);
        if (childrenSelect) childrenSelect.value = String(merged.children);
        const adultsVisible = root.querySelector('.js-booking-adults-visible');
        const childrenVisible = root.querySelector('.js-booking-children-visible');
        if (adultsVisible) adultsVisible.value = String(merged.adults);
        if (childrenVisible) childrenVisible.value = String(merged.children);
      }
      hydrateTravelParticipants(merged.participants);
      const people = merged.participants;
      const cancelOn = Boolean(
        details.cancellation_insurance
        || details.cancellation
        || details.rezygnacja
        || people.some((person) => person?.cancellation || person?.cancellation_insurance),
      );
      setBookingCancellation(cancelOn);
      updatePrice();
      updateSummary();
    }

    const resumeStep = Math.min(Math.max(1, Number(draft.step) || 1), maxStep - 1);
    return resumeStep;
  };

  const isTravelTrainingOn = (card) => {
    if (!card) return false;
    const toggle = card.querySelector('.js-travel-training-toggle');
    if (toggle) {
      return toggle.getAttribute('aria-pressed') === 'true';
    }
    return card.dataset.training === '1';
  };

  const isBookingCancellationOn = () => (
    root.querySelector('.js-booking-cancellation-toggle')?.getAttribute('aria-pressed') === 'true'
    || root.querySelector('.js-travel-cancel-card')?.classList.contains('is-added')
  );

  const getTravelAddonCounts = () => {
    const cards = [...root.querySelectorAll('.booking-travel-participant')];
    const trainingCount = cards.filter((card) => isTravelTrainingOn(card)).length;
    const cancellationCount = isBookingCancellationOn() ? 1 : 0;
    return { trainingCount, cancellationCount, nnwCount: 0, nnwOn: false };
  };

  const trainingPriceForCard = (card) => {
    const { adults } = getCounts();
    const index = Number(card?.dataset?.participantIndex || 1);
    const isAdultSlot = index <= adults;
    const rate = normalizeTravelTrainingRate(card?.dataset?.trainingRate);
    if (rate === 'alt' && trainingAlt.enabled) {
      const alt = isAdultSlot ? trainingAlt.adult : trainingAlt.child;
      if (alt > 0) return alt;
    }
    const birthIso = polishBirthToIso(readTravelParticipantBirthValue(card));
    const age = ageFromIsoDate(birthIso, travelTripDate(root));
    return trainingUnitPriceForAge(
      travelAddonsConfig,
      age,
      isAdultSlot,
      trainingPrice,
      trainingChildPrice,
    );
  };

  const sumTravelTrainingAmount = () => [...root.querySelectorAll('.booking-travel-participant')]
    .filter((card) => isTravelTrainingOn(card))
    .reduce((sum, card) => sum + trainingPriceForCard(card), 0);

  const syncTravelTrainingPriceDisplay = (card) => {
    if (!card) return;
    const el = card.querySelector('.js-travel-training-price');
    if (!el) return;
    const amount = trainingPriceForCard(card);
    el.textContent = Number.isInteger(amount) ? `${amount} zł` : formatPrice(amount);
  };

  const syncTravelDetailsSheet = () => {
    if (bookingType !== 'travel') return;
    stickySummary.sync();
  };

  const calcTravelTotal = () => {
    const { adults, children } = getCounts();
    const participants = adults + children;
    const { cancellationCount } = getTravelAddonCounts();
    let total = basePrice * participants;
    total += sumTravelTrainingAmount();
    total += cancellationCount * cancellationPrice;
    return total;
  };

  const isAdvancedPricing = () => root.dataset.pricingEnabled === '1';
  let lastTravelQuote = null;
  let travelStayError = '';
  let quoteTimer = 0;

  const showTravelStayError = (message) => {
    const text = String(message || '');
    queryBookingAll('.booking-sidebar__cost').forEach((box) => {
      let el = box.querySelector('.js-booking-stay-error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'booking-stay-error js-booking-stay-error';
        el.setAttribute('role', 'alert');
        box.appendChild(el);
      }
      el.textContent = text;
      el.hidden = text === '';
    });
  };

  const applyTravelStayBlock = (message) => {
    travelStayError = message;
    lastTravelQuote = null;
    showTravelStayError(message);
    queryBookingAll('.js-booking-total, .js-booking-accommodation').forEach((el) => {
      el.textContent = '—';
    });
    updateTravelCtas();
    stickySummary.sync();
  };

  const fetchTravelQuote = async () => {
    if (!isAdvancedPricing()) return null;
    const { adults, children } = getCounts();
    const cards = [...root.querySelectorAll('.booking-travel-participant')];
    const url = root.dataset.quoteUrl || '/wp-json/hski/v1/booking/quote';
    const payload = {
      offer_id: Number(root.dataset.offerId || new URLSearchParams(window.location.search).get('offer') || 0),
      page_id: Number(root.dataset.pageId || 0),
      details: {
        adults,
        children,
        children_ages: collectTravelChildrenAges(root, cards),
        term: new URLSearchParams(window.location.search).get('term') || '0',
        room_index: root.dataset.roomIndex || new URLSearchParams(window.location.search).get('room') || '',
        skip_apartment: root.dataset.skipApartment === '1',
        date_from: travelTripDate(root),
        nnw_insurance: false,
        cancellation_insurance: isBookingCancellationOn(),
        participants: cards.map((card) => ({
          training: card.dataset.training === '1',
          training_rate: card.dataset.training === '1'
            ? normalizeTravelTrainingRate(card.dataset.trainingRate)
            : 'standard',
          nnw: false,
          cancellation: false,
          birth: polishBirthToIso(readTravelParticipantBirthValue(card)),
          guest_role: card.dataset.guestRole === 'child' ? 'child' : 'adult',
          is_orderer: Boolean(card.querySelector('.js-travel-is-orderer')?.checked),
        })),
      },
    };
    const { restNonce } = getBookingApi();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(restNonce ? { 'X-WP-Nonce': restNonce } : {}),
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      return {
        blocked: true,
        message: ui(data?.message) || TRAVEL_STAY_AGE_ERROR(),
        room_quote: data?.room_quote || null,
      };
    }
    const blocked = travelStayBlockedMessage(data);
    if (blocked) {
      return {
        ...(data && typeof data === 'object' ? data : {}),
        blocked: true,
        message: blocked,
      };
    }
    return data;
  };

  const applyTravelQuote = (quote) => {
    if (!quote) return;
    lastTravelQuote = quote;
    const formatted = quote.total_formatted || formatQuoteTotal(quote) || formatPrice(quote.total || 0);
    const totalNodes = queryBookingAll('.js-booking-total');
    totalNodes.forEach((el) => { el.textContent = formatted; });
    if (totalEl && !totalNodes.includes(totalEl)) totalEl.textContent = formatted;
    const successTotal = root.querySelector('.js-travel-success-total');
    if (successTotal) successTotal.textContent = formatted;

    const stay = quote.room_quote?.stay_total;
    const acc = quote.room_quote?.accommodation;
    const stayCurrency = quote.room_quote?.currency || quote.currency;
    const lines = Array.isArray(quote.lines) ? quote.lines : [];
    const findLine = (key) => lines.find((line) => line.key === key);
    const skipass = findLine('skipass');
    const stayLine = findLine('stay') || findLine('accommodation');
    if (stayLine?.label) {
      queryBookingAll('.js-booking-stay-label').forEach((el) => {
        el.textContent = stayLine.label;
      });
    }
    if (stay != null || acc != null || stayLine) {
      queryBookingAll('.js-booking-accommodation').forEach((el) => {
        const shown = stayLine
          ? stayLine.amount
          : (skipass && acc != null ? acc : stay);
        el.textContent = formatMoneyAmount(shown, stayLine?.currency || stayCurrency);
      });
    }
    const sumLines = (prefix) => lines
      .filter((line) => String(line.key || '').startsWith(prefix))
      .reduce((sum, line) => sum + Number(line.amount || 0), 0);

    queryBookingAll('.js-booking-cost-skipass').forEach((row) => {
      row.hidden = !skipass;
      const amountEl = row.querySelector('.js-booking-skipass-amount');
      if (amountEl && skipass) {
        amountEl.textContent = formatMoneyAmount(skipass.amount, skipass.currency);
      }
      const labelEl = row.querySelector('.js-booking-skipass-label');
      if (labelEl && skipass?.label) {
        labelEl.textContent = skipass.label;
      }
    });

    const insurance = findLine('mandatory_insurance');
    const insuranceAmount = insurance ? Number(insurance.amount || 0) : 0;
    setCostRow(
      '.js-booking-cost-insurance',
      '.js-booking-insurance-amount',
      '.js-booking-insurance-label',
      insuranceAmount,
      '',
    );
    queryBookingAll('.js-booking-insurance-amount').forEach((el) => {
      if (!insurance) return;
      const qty = Math.max(1, Number(insurance.quantity) || 1);
      const unit = formatMoneyAmount(insurance.unit_price, insurance.currency);
      const total = formatMoneyAmount(insurance.amount, insurance.currency);
      el.textContent = qty > 1 ? `${unit} × ${qty} os. = ${total}` : total;
    });

    const trainAmount = sumLines('training_');
    const trainCurrency = lines.find((line) => String(line.key || '').startsWith('training_'))?.currency || 'PLN';
    const trainCount = lines.filter((line) => String(line.key || '').startsWith('training_')).length;
    setCostRow(
      '.js-booking-cost-training',
      '.js-booking-training-amount',
      '.js-booking-training-label',
      trainAmount,
      trainCount > 1 ? `Szkolenie (x${trainCount})` : 'Szkolenie',
    );
    queryBookingAll('.js-booking-training-amount').forEach((el) => {
      if (trainAmount > 0) el.textContent = formatMoneyAmount(trainAmount, trainCurrency);
    });

    setCostRow(
      '.js-booking-cost-nnw',
      '.js-booking-nnw-amount',
      '.js-booking-nnw-label',
      0,
      '',
    );

    const cancelAmount = sumLines('cancellation');
    const cancelCurrency = lines.find((line) => String(line.key || '').startsWith('cancellation'))?.currency || 'PLN';
    const cancelCount = lines.filter((line) => String(line.key || '').startsWith('cancellation')).length;
    setCostRow(
      '.js-booking-cost-cancellation',
      '.js-booking-cancellation-amount',
      '.js-booking-cancellation-label',
      cancelAmount,
      cancelCount > 1 ? `Ubezp. od rezygnacji (x${cancelCount})` : 'Ubezp. od rezygnacji',
    );
    queryBookingAll('.js-booking-cancellation-amount').forEach((el) => {
      if (cancelAmount > 0) el.textContent = formatMoneyAmount(cancelAmount, cancelCurrency);
    });
  };

  const setCostRow = (rowSelector, amountSelector, labelSelector, amount, label) => {
    queryBookingAll(rowSelector).forEach((row) => {
      const show = amount > 0;
      const amountEl = row.querySelector(amountSelector);
      row.hidden = !show;
      if (amountEl) {
        amountEl.textContent = formatPrice(show ? amount : 0);
      }
      if (!show) return;
      if (label && labelSelector) {
        const labelEl = row.querySelector(labelSelector);
        if (labelEl) labelEl.textContent = label;
      }
    });
  };

  const updateTravelCostRows = () => {
    const { trainingCount, cancellationCount } = getTravelAddonCounts();
    const cancelAmount = cancellationCount * cancellationPrice;
    const trainAmount = sumTravelTrainingAmount();

    setCostRow(
      '.js-booking-cost-cancellation',
      '.js-booking-cancellation-amount',
      '.js-booking-cancellation-label',
      cancelAmount,
      'Ubezp. od rezygnacji',
    );
    setCostRow(
      '.js-booking-cost-training',
      '.js-booking-training-amount',
      '.js-booking-training-label',
      trainAmount,
      trainingCount > 1 ? `Szkolenie (x${trainingCount})` : 'Szkolenie',
    );
    setCostRow(
      '.js-booking-cost-nnw',
      '.js-booking-nnw-amount',
      '.js-booking-nnw-label',
      0,
      '',
    );
  };

  const calcSchoolTotal = () => {
    if (bookingType === 'lesson') {
      const config = getLessonConfig(root);
      const people = root.querySelectorAll('.booking-lesson-participant').length || config.participants;
      return getLessonPrice({
      people,
      hours: config.hours,
      lessonType: config.lessonType,
      location: config.location,
      discipline: config.discipline,
    });
    }

    const cards = root.querySelectorAll('.booking-course-participant');
    if (!cards.length) return getCourseConfig(root).price;

    const config = getCourseConfig(root);
    const siblingEnabled = shouldApplyCourseSiblingDiscount(config.audience || 'children', cards.length);
    return [...cards].reduce(
      (sum, card, index) => sum + courseStudentLinePrice(card, config.price, index + 1, siblingEnabled),
      0,
    );
  };

  const calcTotal = () => (bookingType === 'travel' ? calcTravelTotal() : calcSchoolTotal());

  const updatePrice = () => {
    const totals = queryBookingAll('.js-booking-total');
    if (!totals.length && !totalEl) return;

    if (bookingType === 'lesson' || bookingType === 'course') {
      const formatted = `${calcTotal().toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
      totals.forEach((el) => { el.textContent = formatted; });
      if (totalEl && !totals.includes(totalEl)) totalEl.textContent = formatted;
      stickySummary.sync();
      return;
    }
    if (isAdvancedPricing()) {
      window.clearTimeout(quoteTimer);
      quoteTimer = window.setTimeout(async () => {
        try {
          const quote = await fetchTravelQuote();
          if (quote?.blocked) {
            applyTravelStayBlock(ui(quote.message) || TRAVEL_STAY_AGE_ERROR());
            return;
          }
          if (quote) {
            travelStayError = '';
            showTravelStayError('');
            applyTravelQuote(quote);
            syncTravelDetailsSheet();
            updateTravelCtas();
            return;
          }
        } catch {
          /* fallback below */
        }
        if (travelStayError) return;
        const formatted = formatQuoteTotal(lastTravelQuote) || formatPrice(calcTravelTotal());
        totals.forEach((el) => { el.textContent = formatted; });
        stickySummary.sync();
      }, 160);
      return;
    }
    const total = calcTotal();
    const formatted = formatPrice(total);
    totals.forEach((el) => { el.textContent = formatted; });
    if (totalEl && !totals.includes(totalEl)) totalEl.textContent = formatted;

    const accommodationFormatted = (() => {
      const base = Number(root.dataset.basePrice) || 0;
      const { adults, children } = getCounts();
      return formatPrice(base * (adults + children));
    })();
    queryBookingAll('.js-booking-accommodation').forEach((el) => {
      el.textContent = accommodationFormatted;
    });
    updateTravelCostRows();
    syncTravelDetailsSheet();
  };

  const updateCourseSummary = () => {
    const config = getCourseConfig(root);
    const discipline = config.discipline === 'snowboard' ? 'Snowboard' : 'Narty';
    const cards = [...root.querySelectorAll('.booking-course-participant')];
    const locationLabel = config.location_label || courseLocationLabel(root, config.location);
    const datesLabel = config.date_from && config.date_to
      ? formatCourseDateRange(config.date_from, config.date_to, config.days)
      : '—';

    queryBookingAll('.js-booking-course-discipline').forEach((el) => {
      el.textContent = discipline;
    });
    queryBookingAll('.js-booking-course-location').forEach((el) => {
      el.textContent = locationLabel;
    });
    queryBookingAll('.js-booking-course-dates').forEach((el) => {
      el.textContent = datesLabel;
    });

    const sidebarTitleEl = queryBookingOne('.js-booking-course-sidebar-title');
    const linesEl = queryBookingOne('.js-booking-course-lines');
    if (sidebarTitleEl) sidebarTitleEl.textContent = config.course_label || ui('Kurs');

    if (linesEl) {
      const renderLine = (title, detail, price, { negative = false } = {}) => `
        <div class="booking-sidebar__line-item${negative ? ' booking-sidebar__line-item--discount' : ''}">
          <div>
            <p class="booking-sidebar__line-title">${escapeHtml(title)}</p>
            <p class="booking-sidebar__line-detail">${escapeHtml(detail)}</p>
          </div>
          <p class="booking-sidebar__line-price">${negative ? '−' : ''}${formatCourseMoney(Math.abs(price))} zł</p>
        </div>
      `;

      if (cards.length) {
        const siblingEnabled = shouldApplyCourseSiblingDiscount(config.audience || 'children', cards.length);
        const chunks = [];
        cards.forEach((card, index) => {
          const pkg = card.querySelector('.js-course-student-package:checked');
          const pkgLabel = pkg?.dataset.label || config.package_label || ui('Pakiet');
          const meals = collectCourseStudentMeals(card);
          const quote = courseStudentQuoteParts(card, index + 1, config.price, { siblingEnabled });
          // Kurs = sama cena pakietu; obiad osobną linią (stawka × dni).
          chunks.push(renderLine(
            config.course_label || ui('Kurs'),
            `${pkgLabel} · ${discipline}`,
            quote.packagePrice,
          ));
          if (quote.mealPrice > 0) {
            const rate = meals.reduce((max, meal) => Math.max(max, Number(meal.price_per_day) || 0), 0);
            const days = meals.reduce((sum, meal) => sum + (Number(meal.days) || 0), 0);
            const mealDetail = formatCourseMealRateDays(rate, days)
              || formatCourseMealChoiceLabel(card, meals);
            chunks.push(renderLine(ui('Obiad'), mealDetail, quote.mealPrice));
          }
          if (quote.siblingDiscount > 0) {
            chunks.push(renderLine(
              quote.siblingLabel,
              ui('Tylko kurs (bez obiadu)'),
              quote.siblingDiscount,
              { negative: true },
            ));
          }
        });
        linesEl.innerHTML = chunks.join('');
      } else {
        const packageLabel = config.package_label || config.package || 'Pakiet';
        linesEl.innerHTML = renderLine(
          config.course_label || 'Kurs',
          `${packageLabel} · ${discipline}`,
          config.price,
        );
      }
    }

    if (summaryCourse) summaryCourse.textContent = config.course_label || '—';
    if (sidebarCourse) sidebarCourse.textContent = config.course_label || '—';
    updatePrice();
  };

  const updateCourseOrdererButton = () => {
    if (bookingType !== 'course') return;
    const panel = root.querySelector(`[data-booking-step="${COURSE_ORDERER_STEP}"]`);
    const nextBtn = panel?.querySelector('.js-booking-next');
    if (!panel || !nextBtn) return;
    setBookingCtaMuted(nextBtn, !validateStep(root, COURSE_ORDERER_STEP, bookingType, { markInvalid: false }));
  };

  const sendBookingLead = () => {
    if (bookingType !== 'travel' && bookingType !== 'course' && bookingType !== 'lesson') return;
    // Lead: tylko imię, nazwisko, telefon, e-mail — bez czekania na resztę formularza.
    const contact = collectContact(root, bookingType);
    if (
      !contact.first_name
      || !contact.last_name
      || !contact.email
      || !contact.phone
    ) return;
    if (!root.dataset.crmLeadId) {
      root.dataset.crmLeadId = newSubmissionId();
    }
    const { restUrl, restNonce } = getBookingApi();
    const leadUrl = `${String(restUrl).replace(/\/$/, '')}/lead`;
    const payload = collectBookingPayload(root, bookingType);
    payload.submissionId = root.dataset.crmLeadId;
    logCrmDebug('lead → POST', leadUrl, {
      type: bookingType,
      submissionId: payload.submissionId,
      contact: payload.contact,
    });
    fetch(leadUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(restNonce ? { 'X-WP-Nonce': restNonce } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        logCrmDebug('lead ← response', {
          type: bookingType,
          status: response.status,
          ok: response.ok,
          skipped: data.skipped,
          crm: data.crm,
          body: data,
        });
      })
      .catch((error) => {
        logCrmDebug('lead ← network error', error);
      });
  };

  const updateCourseStudentsCount = (rootEl) => {
    const countEl = rootEl.querySelector('.js-course-students-count');
    const count = rootEl.querySelectorAll('.booking-course-participant').length;
    if (countEl) countEl.textContent = String(count);
  };

  const validateCourseStudentsStep = (rootEl, { markInvalid = true } = {}) => {
    const cards = [...rootEl.querySelectorAll('.booking-course-participant')];
    if (!cards.length) return false;

    const pkgAgeCfg = parsePingwinPackageAgeConfig(rootEl);
    const asOf = getCourseConfig(rootEl).date_from || travelTripDate(rootEl) || '';
    let valid = true;
    cards.forEach((card) => {
      const index = card.dataset.studentIndex || '1';
      const first = card.querySelector(`[name="student-first-name-${index}"]`);
      const last = card.querySelector(`[name="student-last-name-${index}"]`);
      const birth = card.querySelector(`[name="student-birth-${index}"]`);
      const pkg = card.querySelector('.js-course-student-package:checked');

      [first, last].forEach((field) => {
        if (!field) return;
        const fieldValid = Boolean(field.value?.trim());
        if (markInvalid) setBookingFieldInvalid(field, !fieldValid);
        if (!fieldValid) valid = false;
      });

      const birthResult = validateBirthDatePicker(card._birthMask || birth, {
        required: true,
        maxDate: new Date(),
        fieldLabel: t('label_birth', 'Data urodzenia'),
      });
      if (markInvalid && birth) setBookingFieldInvalid(birth, !birthResult.valid);
      if (!birthResult.valid) valid = false;

      const picker = card.querySelector('.course-package-picker');
      if (markInvalid) picker?.classList.toggle('is-invalid', !pkg);
      if (!pkg) valid = false;

      if (resolvePingwinPackageAgeEnabled(rootEl, pkgAgeCfg) && birthResult.valid && pkg) {
        const birthIso = polishBirthToIso(
          card._birthMask?.value || birth?.value || '',
        );
        const age = ageFromIsoDate(birthIso, asOf);
        const kind = pingwinPackageKind(pkg.value, pkg.dataset.label || '', pkgAgeCfg);
        const scenario = pingwinPackageAgeScenario(age, kind, pkgAgeCfg);
        if (scenario === 'force_ekonomiczny') {
          if (markInvalid) picker?.classList.add('is-invalid');
          valid = false;
        } else if (scenario === 'ack_ekonomiczny' && card.dataset.packageAgeAck !== '1') {
          if (markInvalid) picker?.classList.add('is-invalid');
          valid = false;
        }
      }
    });

    return valid;
  };

  const updateCourseStudentsNextButton = (rootEl) => {
    const btn = rootEl.querySelector('.js-course-students-next');
    if (!btn) return;
    setBookingCtaMuted(btn, !validateCourseStudentsStep(rootEl, { markInvalid: false }));
  };

  const getCourseProductUrl = (rootEl) => {
    const params = new URLSearchParams(window.location.search);
    const productUrl = rootEl.dataset.courseProductUrl || './klub-pingwina.html';
    const base = productUrl.split('?')[0];
    return params.toString() ? `${base}?${params.toString()}` : base;
  };

  const updateCourseReview = () => {
    if (bookingType !== 'course') return;

    const contact = collectContact(root, bookingType);
    const config = getCourseConfig(root);
    const fieldsets = [...root.querySelectorAll('.booking-course-participant')];
    const discipline = config.discipline === 'snowboard' ? 'Snowboard' : 'Narty';
    const dates = config.date_from && config.date_to
      ? formatCourseDateRange(config.date_from, config.date_to, config.days)
      : '—';
    const courseTitle = config.course_label || 'Kurs';

    const ordererLine = root.querySelector('.js-course-review-orderer-line');
    const ordererPhone = root.querySelector('.js-course-review-orderer-phone');
    const ordererNotes = root.querySelector('.js-course-review-orderer-notes');
    const studentsList = root.querySelector('.js-course-review-students-list');

    const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    if (ordererLine) {
      ordererLine.textContent = fullName && contact.email
        ? `${fullName} · ${contact.email}`
        : fullName || contact.email || '—';
    }
    if (ordererPhone) {
      ordererPhone.textContent = formatPhoneWithPrefix(contact.phone, contact.phone_prefix) || '—';
    }
    if (ordererNotes) ordererNotes.textContent = contact.notes || ui('Brak');

    if (studentsList) {
      studentsList.innerHTML = fieldsets.map((card) => {
        const index = card.dataset.studentIndex || '1';
        const first = card.querySelector(`[name="student-first-name-${index}"]`)?.value?.trim() || '—';
        const last = card.querySelector(`[name="student-last-name-${index}"]`)?.value?.trim() || '—';
        const birthRaw = card.querySelector(`[name="student-birth-${index}"]`)?.value?.trim() || '';
        const birth = formatBirthDisplay(birthRaw);
        const notes = card.querySelector(`[name="student-notes-${index}"]`)?.value?.trim() || ui('Brak');
        const pkg = card.querySelector('.js-course-student-package:checked');
        const pkgLabel = pkg?.dataset.label || config.package_label || '—';
        const meals = collectCourseStudentMeals(card);
        const mealOptions = parsePackageMealOptions(pkg);
        const catalogMeals = findCoursePackage(coursePackages, pkg?.value)?.meal_options;
        const packageOffersMeals = mealOptions.length > 0
          || (Array.isArray(catalogMeals) && catalogMeals.length > 0);
        const mealChoice = formatCourseMealChoiceLabel(card, meals);
        const mealDetail = meals.length
          ? (formatCourseMealLineDetail(meals) || mealChoice)
          : mealChoice;
        const mealRow = packageOffersMeals
          ? `<div><dt>${escapeHtml(ui('Obiad:'))}</dt><dd>${escapeHtml(mealDetail)}</dd></div>`
          : '';

        return `
          <article class="course-review-student">
            <div class="course-review-student__head">
              <div>
                <h3 class="course-review-student__title">${escapeHtml(courseTitle)}</h3>
                <div class="course-review-student__subtitle">
                  <span>${escapeHtml(dates)}</span>
                  <span class="course-review-student__discipline">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 19 19 5M6 22 22 6M4 6l13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                    ${escapeHtml(discipline)}
                  </span>
                </div>
              </div>
              <button type="button" class="lesson-review-edit lesson-review-edit--lg js-course-review-edit-students">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" stroke-width="2"/></svg>
                ${escapeHtml(ui('Edytuj'))}
              </button>
            </div>
            <div class="course-review-student__grid">
              <div>
                <div class="course-review-student__section-title">${escapeHtml(ui('Dane osobowe'))}</div>
                <dl class="course-review-student__details">
                  <div><dt>${escapeHtml(ui('Imię:'))}</dt><dd>${escapeHtml(first)}</dd></div>
                  <div><dt>${escapeHtml(ui('Nazwisko:'))}</dt><dd>${escapeHtml(last)}</dd></div>
                  <div><dt>${escapeHtml(ui('Data urodzenia:'))}</dt><dd>${escapeHtml(birth)}</dd></div>
                  <div><dt>${escapeHtml(ui('Pakiet:'))}</dt><dd>${escapeHtml(pkgLabel)}</dd></div>
                  ${mealRow}
                </dl>
              </div>
              <div>
                <div class="course-review-student__section-title">${escapeHtml(ui('Uwagi'))}</div>
                <p class="course-review-student__notes">${escapeHtml(notes)}</p>
              </div>
            </div>
          </article>
        `;
      }).join('');
    }

    updateCourseSummary();
  };

  const initCourseReview = (rootEl) => {
    rootEl.querySelector('.js-course-review-edit-orderer')?.addEventListener('click', () => goToStep(COURSE_ORDERER_STEP));
    rootEl.addEventListener('click', (event) => {
      if (event.target.closest('.js-course-review-edit-students')) {
        goToStep(COURSE_STUDENTS_STEP);
      }
    });

    rootEl.querySelectorAll('.js-course-crosssell-add').forEach((button) => {
      button.addEventListener('click', () => {
        const wrap = rootEl.querySelector('.js-booking-course-participants-wrap');
        if (!wrap || participantCount >= 3) return;

        const config = getCourseConfig(rootEl);
        const defaultPackage = resolveDefaultCoursePackage(config);
        participantCount += 1;
        const card = buildCourseStudentCard(participantCount, defaultPackage, coursePackages, getCourseCalendarIcon());
        wrap.appendChild(card);
        bindCourseStudentCard(card);
        updateCourseStudentsCount(rootEl);
        syncCourseStudentRemoveButtons(rootEl);
        updatePrice();
        updateSummary();
        updateCourseSummary();
        updateCourseStudentsNextButton(rootEl);
        goToStep(COURSE_STUDENTS_STEP);
      });
    });

    const ordererToggle = rootEl.querySelector('.js-course-review-orderer-toggle');
    const ordererBody = rootEl.querySelector('.js-course-review-orderer-body');
    ordererToggle?.addEventListener('click', () => {
      const expanded = ordererToggle.getAttribute('aria-expanded') === 'true';
      ordererToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      ordererToggle.classList.toggle('is-open', !expanded);
      if (ordererBody) ordererBody.hidden = expanded;
    });
  };

  const bindReviewAccordionToggle = (toggle, body, labels) => {
    if (!toggle || !body) return;

    const setExpanded = (nextExpanded) => {
      toggle.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
      toggle.classList.toggle('is-open', nextExpanded);
      toggle.setAttribute('aria-label', nextExpanded ? labels.collapse : labels.expand);
      body.hidden = !nextExpanded;
    };

    // Start zwinięty (niezależnie od HTML).
    setExpanded(false);

    const onToggle = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      setExpanded(!expanded);
    };

    toggle.addEventListener('click', onToggle);

    // Klik w nagłówek (poza Edytuj / strzałką) też rozwija/zwija.
    const head = toggle.closest('.lesson-review-orderer')?.querySelector('.lesson-review-orderer__head');
    head?.addEventListener('click', (event) => {
      if (event.target.closest('.lesson-review-edit, .lesson-review-orderer__toggle')) return;
      onToggle(event);
    });
  };

  const updateTravelReview = () => {
    if (bookingType !== 'travel') return;

    const contact = collectContact(root, bookingType);
    const ordererLine = root.querySelector('.js-travel-review-orderer-line');
    const ordererFirst = root.querySelector('.js-travel-review-orderer-first');
    const ordererLast = root.querySelector('.js-travel-review-orderer-last');
    const ordererEmail = root.querySelector('.js-travel-review-orderer-email');
    const ordererPhone = root.querySelector('.js-travel-review-orderer-phone');
    const ordererBirth = root.querySelector('.js-travel-review-orderer-birth');
    const participantsLine = root.querySelector('.js-travel-review-participants-line');
    const list = root.querySelector('.js-travel-review-participants-list');

    const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    if (ordererLine) {
      ordererLine.textContent = fullName && contact.email
        ? `${fullName} · ${contact.email}`
        : fullName || contact.email || '—';
    }
    if (ordererFirst) ordererFirst.textContent = contact.first_name || '—';
    if (ordererLast) ordererLast.textContent = contact.last_name || '—';
    if (ordererEmail) ordererEmail.textContent = contact.email || '—';
    if (ordererPhone) {
      ordererPhone.textContent = formatPhoneWithPrefix(contact.phone, contact.phone_prefix) || '—';
    }
    if (ordererBirth) {
      ordererBirth.textContent = formatBirthDisplay(contact.birth_date) || '—';
    }

    const resort = root.dataset.resort || '';
    const hotel = root.dataset.hotel || '';
    const termLabel = normalizeTravelTermLabel(
      root.querySelector('.js-booking-sidebar-term')?.textContent
      || root.dataset.termLabel
      || '',
    );
    const place = root.querySelector('.js-booking-sidebar-place')?.textContent?.trim()
      || root.dataset.country
      || '';
    const room = normalizeTravelRoomForReview(
      root.dataset.roomTitle
      || root.querySelector('.js-booking-sidebar-room')?.textContent
      || '',
    );
    const { adults, children } = getCounts();
    const guestsLabel = formatTravelGuestsLabel(adults, children);

    const offerTitle = root.querySelector('.js-travel-review-offer-title');
    const offerTerm = root.querySelector('.js-travel-review-offer-term');
    const offerPlace = root.querySelector('.js-travel-review-offer-place');
    const offerRoom = root.querySelector('.js-travel-review-offer-room');
    const offerGuests = root.querySelector('.js-travel-review-offer-guests');
    if (offerTitle) {
      offerTitle.textContent = [resort, hotel].filter(Boolean).join(' · ') || '—';
    }
    if (offerTerm && termLabel) {
      offerTerm.textContent = termLabel;
    }
    if (offerPlace && place) {
      offerPlace.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 22s7-7.58 7-13a7 7 0 1 0-14 0c0 5.42 7 13 7 13z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="2"/></svg> ${escapeHtml(place)}`;
    }
    if (offerRoom) offerRoom.textContent = room || '—';
    if (offerGuests) offerGuests.textContent = guestsLabel || '—';

    if (!list) return;
    const cards = [...root.querySelectorAll('.booking-travel-participant')];
    const names = [];
    list.innerHTML = cards.map((card, idx) => {
      const index = card.dataset.participantIndex || String(idx + 1);
      const first = readFieldValue(card, `participant-first-name-${index}`) || '—';
      const last = readFieldValue(card, `participant-last-name-${index}`) || '—';
      const full = `${first} ${last}`.trim();
      if (full && full !== '—') names.push(full);
      const birth = formatBirthDisplay(readFieldValue(card, `participant-birth-${index}`)) || '—';
      const notes = readFieldValue(card, `participant-notes-${index}`) || 'Brak';
      const trainingOn = card.dataset.training === '1';
      const discipline = travelDisciplineLabel(card.dataset.discipline || '', t);
      const rateIsAlt = trainingOn && normalizeTravelTrainingRate(card.dataset.trainingRate) === 'alt';
      let trainingLabel = `${t('summary_training', 'Szkolenie')}: ${t('summary_no', 'Nie')}`;
      if (trainingOn) {
        const parts = [];
        if (discipline) parts.push(discipline);
        if (rateIsAlt) parts.push(trainingAlt.label);
        trainingLabel = parts.length
          ? `${t('summary_training', 'Szkolenie')}: ${parts.join(' · ')}`
          : t('summary_training', 'Szkolenie');
      }
      const isOrderer = Boolean(card.querySelector('.js-travel-is-orderer')?.checked);
      const title = isOrderer
        ? `${t('participant_title', 'Uczestnik nr {index}', { index })} · ${t('orderer_checkbox', 'Osoba zamawiająca')}`
        : t('participant_title', 'Uczestnik nr {index}', { index });

      return `
        <article class="travel-review-participant">
          <div class="travel-review-participant__head">
            <h3 class="travel-review-participant__title">${escapeHtml(title)}</h3>
            <div class="travel-review-participant__subtitle">
              <span>${escapeHtml(full)}</span>
              ${trainingOn && discipline ? `<span class="course-review-student__discipline">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 19 19 5M6 22 22 6M4 6l13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                ${escapeHtml(discipline)}
              </span>` : ''}
            </div>
          </div>
          <dl class="lesson-review-orderer__details travel-review-participant__details">
            <div><dt>${escapeHtml(t('label_first_name', 'Imię'))}:</dt><dd>${escapeHtml(first)}</dd></div>
            <div><dt>${escapeHtml(t('label_last_name', 'Nazwisko'))}:</dt><dd>${escapeHtml(last)}</dd></div>
            <div><dt>${escapeHtml(t('review_birth', 'Data urodzenia'))}:</dt><dd>${escapeHtml(birth)}</dd></div>
            <div><dt>${escapeHtml(t('summary_addons', 'Dodatki'))}:</dt><dd>${escapeHtml(trainingLabel)}</dd></div>
            <div><dt>${escapeHtml(t('notes_label', 'Uwagi'))}:</dt><dd>${escapeHtml(notes)}</dd></div>
          </dl>
        </article>
      `;
    }).join('');

    if (participantsLine) {
      participantsLine.textContent = names.length ? names.join(' · ') : '—';
    }
  };

  const initTravelReview = (rootEl) => {
    if (bookingType !== 'travel') return;
    rootEl.querySelector('.js-travel-review-edit-orderer')?.addEventListener('click', () => {
      goToStep(TRAVEL_ORDERER_STEP);
    });
    rootEl.addEventListener('click', (event) => {
      if (event.target.closest('.js-travel-review-edit-participants')) {
        goToStep(TRAVEL_PARTICIPANTS_STEP);
      }
    });
    bindReviewAccordionToggle(
      rootEl.querySelector('.js-travel-review-orderer-toggle'),
      rootEl.querySelector('.js-travel-review-orderer-body'),
      {
        expand: t('review_orderer_expand', 'Rozwiń dane zamawiającego'),
        collapse: t('review_orderer_collapse', 'Zwiń dane zamawiającego'),
      },
    );
    bindReviewAccordionToggle(
      rootEl.querySelector('.js-travel-review-participants-toggle'),
      rootEl.querySelector('.js-travel-review-participants-body'),
      {
        expand: t('review_participants_expand', 'Rozwiń listę uczestników'),
        collapse: t('review_participants_collapse', 'Zwiń listę uczestników'),
      },
    );
    bindReviewAccordionToggle(
      rootEl.querySelector('.js-travel-success-orderer-toggle'),
      rootEl.querySelector('.js-travel-success-orderer-body'),
      {
        expand: t('review_orderer_expand', 'Rozwiń dane zamawiającego'),
        collapse: t('review_orderer_collapse', 'Zwiń dane zamawiającego'),
      },
    );
    bindReviewAccordionToggle(
      rootEl.querySelector('.js-travel-success-participants-toggle'),
      rootEl.querySelector('.js-travel-success-participants-body'),
      {
        expand: t('review_participants_expand', 'Rozwiń listę uczestników'),
        collapse: t('review_participants_collapse', 'Zwiń listę uczestników'),
      },
    );
  };

  const initCourseStudents = () => {
    const wrap = root.querySelector('.js-booking-course-participants-wrap');
    if (!wrap || bookingType !== 'course') return;

    const config = getCourseConfig(root);
    const defaultPackage = resolveDefaultCoursePackage(config);

    wrap.innerHTML = '';
    participantCount = 1;
    const card = buildCourseStudentCard(1, defaultPackage, coursePackages, getCourseCalendarIcon());
    wrap.appendChild(card);
    bindCourseStudentCard(card);
    updateCourseStudentsCount(root);
    syncCourseStudentRemoveButtons(root);
    updateCourseStudentsNextButton(root);
  };

  const reindexCourseStudentCards = (wrap) => {
    if (!wrap) return;
    [...wrap.querySelectorAll('.booking-course-participant')].forEach((card, idx) => {
      const index = idx + 1;
      card.dataset.studentIndex = String(index);
      const title = card.querySelector('.course-student-card__title');
      if (title) title.textContent = uiFmt('Kursant nr %s', index);

      card.querySelectorAll('[name^="student-"]').forEach((field) => {
        field.name = field.name.replace(/-(\d+)$/, `-${index}`);
      });

      const picker = card.querySelector('.course-package-picker');
      if (picker) picker.setAttribute('aria-label', `Pakiet kursanta nr ${index}`);
      bindCourseBirthPicker(card);
    });
    participantCount = wrap.querySelectorAll('.booking-course-participant').length;
  };

  const syncCourseStudentRemoveButtons = (rootEl) => {
    const cards = [...rootEl.querySelectorAll('.booking-course-participant')];
    cards.forEach((card) => {
      const removeBtn = card.querySelector('.js-course-student-remove');
      if (!removeBtn) return;
      removeBtn.hidden = cards.length <= 1;
    });
  };

  let pingwinPkgAgePending = null;

  const selectCourseStudentPackage = (card, packageId) => {
    if (!card || !packageId) return false;
    const radio = card.querySelector(`.js-course-student-package[value="${CSS.escape(packageId)}"]`);
    if (!radio) return false;
    card._pkgAgeProgrammatic = true;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    card._pkgAgeProgrammatic = false;
    return true;
  };

  const showPingwinPackageAgeModal = (rootEl, pending) => {
    const modal = rootEl.querySelector('.js-booking-pkg-age-modal');
    const cfg = parsePingwinPackageAgeConfig(rootEl);
    if (!modal || typeof modal.showModal !== 'function') {
      // Bez dialogu nie da się wymusić CTA — zostaw unresolved, Dalej ponowi blokadę.
      return;
    }

    pingwinPkgAgePending = pending;
    const titleEl = modal.querySelector('.js-booking-pkg-age-title');
    const subjectEl = modal.querySelector('.js-booking-pkg-age-subject');
    const textEl = modal.querySelector('.js-booking-pkg-age-text');
    const primaryBtn = modal.querySelector('.js-booking-pkg-age-primary');
    const secondaryBtn = modal.querySelector('.js-booking-pkg-age-secondary');
    const isForce = pending?.scenario === 'force_ekonomiczny';
    const childLabel = getCourseStudentDisplayLabel(pending?.card);

    if (titleEl) {
      titleEl.textContent = ui(isForce
        ? (cfg.title_young_on_podstawowy || DEFAULT_PINGWIN_PKG_AGE.title_young_on_podstawowy)
        : (cfg.title_older_on_ekonomiczny || DEFAULT_PINGWIN_PKG_AGE.title_older_on_ekonomiczny));
    }
    if (subjectEl) {
      subjectEl.textContent = childLabel ? uiFmt('Dotyczy: %s', childLabel) : '';
      subjectEl.hidden = !childLabel;
    }
    if (textEl) {
      const raw = ui(isForce
        ? (cfg.msg_young_on_podstawowy || DEFAULT_PINGWIN_PKG_AGE.msg_young_on_podstawowy)
        : (cfg.msg_older_on_ekonomiczny || DEFAULT_PINGWIN_PKG_AGE.msg_older_on_ekonomiczny));
      textEl.textContent = fillPingwinPkgAgePlaceholders(raw, rootEl);
    }

    // Scenario A: Wróć | Przejdź na Ekonomiczny
    // Scenario B: Kontynuuj z Ekonomicznym | Przejdź na Podstawowy
    if (secondaryBtn) {
      secondaryBtn.hidden = false;
      secondaryBtn.textContent = ui(isForce
        ? (cfg.btn_back || DEFAULT_PINGWIN_PKG_AGE.btn_back || 'Wróć')
        : (cfg.btn_stay_ekonomiczny || 'Kontynuuj z Pakietem ekonomicznym'));
    }
    if (primaryBtn) {
      primaryBtn.hidden = false;
      primaryBtn.disabled = false;
      primaryBtn.classList.remove('is-muted');
      primaryBtn.textContent = ui(isForce
        ? (cfg.btn_switch_ekonomiczny || 'Przejdź na Pakiet ekonomiczny')
        : (cfg.btn_switch_podstawowy || 'Przejdź na Pakiet podstawowy'));
    }

    scrollToCourseStudentCard(pending?.card);
    if (!modal.open) modal.showModal();
  };

  const initPingwinPackageAgeModal = (rootEl) => {
    const modal = rootEl.querySelector('.js-booking-pkg-age-modal');
    if (!modal) return;

    const cfg = () => parsePingwinPackageAgeConfig(rootEl);
    const primaryBtn = modal.querySelector('.js-booking-pkg-age-primary');
    const secondaryBtn = modal.querySelector('.js-booking-pkg-age-secondary');

    const afterModalChoice = () => {
      updateCourseStudentsNextButton(rootEl);
      updateCourseSummary();
      schedulePersist();
    };

    // Orange CTA: A → switch Ekonomiczny; B → switch Podstawowy
    primaryBtn?.addEventListener('click', () => {
      const pending = pingwinPkgAgePending;
      const card = pending?.card;
      pingwinPkgAgePending = null;
      if (pending?.scenario === 'force_ekonomiczny') {
        selectCourseStudentPackage(card, cfg().package_ekonomiczny);
        delete card?.dataset?.packageAgeAck;
      } else if (pending?.scenario === 'ack_ekonomiczny') {
        selectCourseStudentPackage(card, cfg().package_podstawowy);
        delete card?.dataset?.packageAgeAck;
      }
      modal.close();
      afterModalChoice();
    });

    // White CTA: A → Wróć (dismiss, no switch); B → Kontynuuj z Ekonomicznym (ack)
    secondaryBtn?.addEventListener('click', () => {
      const pending = pingwinPkgAgePending;
      const card = pending?.card;
      pingwinPkgAgePending = null;
      if (pending?.scenario === 'ack_ekonomiczny' && card) {
        card.dataset.packageAgeAck = '1';
      }
      // force_ekonomiczny: tylko zamknięcie — pakiet zostaje; Dalej znów pokaże modal.
      modal.close();
      afterModalChoice();
    });

    const onDismissWithoutChoice = () => {
      const pending = pingwinPkgAgePending;
      if (!pending) return;
      pingwinPkgAgePending = null;
      // Bez przełączenia i bez ack — unresolved blokuje Dalej / submit (per-child scroll).
      afterModalChoice();
    };

    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      modal.close();
      onDismissWithoutChoice();
    });

    modal.addEventListener('click', (event) => {
      if (event.target !== modal) return;
      modal.close();
      onDismissWithoutChoice();
    });
  };

  const evaluatePingwinPackageAge = (card, { openModal = true } = {}) => {
    const cfg = parsePingwinPackageAgeConfig(root);
    if (!resolvePingwinPackageAgeEnabled(root, cfg) || !card || card._pkgAgeProgrammatic) return null;

    const index = card.dataset.studentIndex || '1';
    const birthInput = card.querySelector(`[name="student-birth-${index}"]`);
    const birthRaw = card._birthMask?.value || birthInput?.value || '';
    const birthIso = polishBirthToIso(birthRaw);
    if (!birthIso || !/^\d{4}-\d{2}-\d{2}$/.test(birthIso)) return null;

    const pkg = card.querySelector('.js-course-student-package:checked');
    if (!pkg) return null;

    const asOf = getCourseConfig(root).date_from || travelTripDate(root) || '';
    const age = ageFromIsoDate(birthIso, asOf);
    const kind = pingwinPackageKind(pkg.value, pkg.dataset.label || '', cfg);
    const scenario = pingwinPackageAgeScenario(age, kind, cfg);

    if (!scenario) {
      delete card.dataset.packageAgeAck;
      card._pkgAgeShownKey = '';
      return null;
    }

    if (scenario === 'ack_ekonomiczny' && card.dataset.packageAgeAck === '1') {
      return scenario;
    }

    if (scenario === 'force_ekonomiczny') {
      delete card.dataset.packageAgeAck;
    }

    const shownKey = `${scenario}:${age}:${kind}`;
    if (openModal && card._pkgAgeShownKey !== shownKey) {
      card._pkgAgeShownKey = shownKey;
      showPingwinPackageAgeModal(root, { card, scenario, age, kind });
    }

    return scenario;
  };

  const recheckPingwinPackageAgeAfterBirthChange = (card, input) => {
    const birthRaw = String(card._birthMask?.value || input?.value || '').trim();
    const birthIso = polishBirthToIso(birthRaw) || '';
    const prevIso = card._pkgAgeBirthIso || '';
    if (birthIso === prevIso) return;
    card._pkgAgeBirthIso = birthIso;
    // Age/DOB change invalidates prior ack and modal dedupe — same as package change.
    delete card.dataset.packageAgeAck;
    card._pkgAgeShownKey = '';
    if (birthIso) evaluatePingwinPackageAge(card);
  };

  const bindCourseBirthPicker = (card) => {
    const input = card.querySelector('[name^="student-birth-"]');
    if (!input) return null;
    const seedBirth = String(input.value || '').trim();
    destroyBirthDatePicker(card._birthMask);
    if (card._birthMask?.masked) destroyPolishDateMask(card._birthMask);
    card._pkgAgeBirthIso = polishBirthToIso(seedBirth) || '';
    const picker = attachBirthDatePicker(input, {
      maxDate: new Date(),
      onChange: () => {
        setBookingFieldInvalid(input, false);
        recheckPingwinPackageAgeAfterBirthChange(card, input);
        updateCourseStudentsNextButton(root);
        updateCourseSummary();
        schedulePersist();
      },
    });
    card._birthMask = picker;
    // Typed DOB: flatpickr onChange may not fire until blur — recheck on input too.
    input.addEventListener('input', () => {
      recheckPingwinPackageAgeAfterBirthChange(card, input);
      updateCourseStudentsNextButton(root);
      updateCourseSummary();
      schedulePersist();
    });
    input.addEventListener('change', () => {
      recheckPingwinPackageAgeAfterBirthChange(card, input);
    });
    input.addEventListener('blur', () => {
      const result = validateBirthDatePicker(picker, {
        required: true,
        maxDate: new Date(),
        fieldLabel: t('label_birth', 'Data urodzenia'),
      });
      setBookingFieldInvalid(input, !result.valid);
      if (result.valid) {
        recheckPingwinPackageAgeAfterBirthChange(card, input);
      } else {
        card._pkgAgeBirthIso = '';
        delete card.dataset.packageAgeAck;
        card._pkgAgeShownKey = '';
      }
    });
    return picker;
  };

  const bindCourseStudentCard = (card) => {
    bindCourseBirthPicker(card);

    card.querySelectorAll('input, textarea').forEach((field) => {
      field.addEventListener('input', () => {
        updateSummary();
        updateCourseStudentsNextButton(root);
        updateCourseSummary();
      });
      field.addEventListener('change', () => {
        updateSummary();
        updateCourseStudentsNextButton(root);
        updateCourseSummary();
      });
    });

    card.querySelectorAll('.js-course-student-package').forEach((input) => {
      input.addEventListener('change', () => {
        card.querySelectorAll('.course-package-option').forEach((option) => {
          const radio = option.querySelector('.js-course-student-package');
          option.classList.toggle('is-selected', Boolean(radio?.checked));
        });
        card.querySelector('.course-package-picker')?.classList.remove('is-invalid');
        if (!card._pkgAgeProgrammatic) {
          delete card.dataset.packageAgeAck;
          card._pkgAgeShownKey = '';
        }
        syncCourseStudentMeals(card, coursePackages, { keepSelection: false });
        evaluatePingwinPackageAge(card);
        updatePrice();
        updateCourseSummary();
        updateCourseStudentsNextButton(root);
      });
    });

    card.addEventListener('change', (event) => {
      if (event.target?.classList?.contains('js-course-student-meal')) {
        card.querySelectorAll('.course-meal-option').forEach((option) => {
          const input = option.querySelector('.js-course-student-meal');
          option.classList.toggle('is-selected', Boolean(input?.checked));
        });
        updatePrice();
        updateCourseSummary();
        updateCourseStudentsNextButton(root);
        schedulePersist();
      }
    });

    card.querySelector('.js-course-student-remove')?.addEventListener('click', () => {
      const wrap = root.querySelector('.js-booking-course-participants-wrap');
      const count = wrap?.querySelectorAll('.booking-course-participant').length || 0;
      if (!wrap || count <= 1) return;

      destroyBirthDatePicker(card._birthMask);
      card.remove();
      reindexCourseStudentCards(wrap);
      syncCourseStudentRemoveButtons(root);
      updateCourseStudentsCount(root);
      updatePrice();
      updateSummary();
      updateCourseSummary();
      updateCourseStudentsNextButton(root);
    });
  };

  const applyCourseUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const backLink = root.querySelector('.js-booking-course-back');
    if (backLink) {
      backLink.href = getCourseProductUrl(root);
    }

    const courseTypeSelect = root.querySelector('.js-booking-course-type');
    const dateFrom = params.get('date_from') || root.dataset.dateFrom || '';
    const dateTo = params.get('date_to') || root.dataset.dateTo || '';

    if (courseTypeSelect && dateFrom && dateTo) {
      const match = [...courseTypeSelect.options].find(
        (option) => option.dataset.dateFrom === dateFrom && option.dataset.dateTo === dateTo,
      );
      if (match) {
        courseTypeSelect.value = match.value;
      }
    }

    getCourseConfig(root);
    initCourseStudents();
    updateCourseSummary();
    updateCourseStudentsNextButton(root);
    updateCourseOrdererButton();
  };

  const bindCourseTypeSelect = () => {
    const select = root.querySelector('.js-booking-course-type');
    if (!select) return;

    select.addEventListener('change', () => {
      getCourseConfig(root);
      updateCourseSummary();
      updateCourseStudentsNextButton(root);
      updateCourseOrdererButton();
    });
  };

  const updateLessonSummary = () => {
    const config = getLessonConfig(root);
    const people = root.querySelectorAll('.booking-lesson-participant').length || config.participants;
    const price = getLessonPrice({
      people,
      hours: config.hours,
      lessonType: config.lessonType,
      location: config.location,
      discipline: config.discipline,
    });
    const typeLabel = config.lessonType === 'season' ? ui('Pakiety godzin') : ui('Pojedyncze lekcje');
    const hoursLabel = config.lessonType === 'season' ? uiFmt('%s h pakiet', config.hours) : `${config.hours} h`;
    const participantsLabel = lessonPeopleNominative(people);

    if (summaryType) {
      summaryType.textContent = `${typeLabel} · ${hoursLabel} · ${participantsLabel}`;
    }

    const disciplineEl = queryBookingOne('.js-booking-lesson-discipline');
    const locationEl = queryBookingOne('.js-booking-lesson-location');
    const datesEl = queryBookingOne('.js-booking-lesson-dates');
    const detailEl = queryBookingOne('.js-booking-lesson-detail');
    const priceLineEl = queryBookingOne('.js-booking-lesson-price');

    if (disciplineEl) {
      disciplineEl.textContent = config.discipline === 'snowboard' ? 'Snowboard' : 'Narty';
    }
    if (locationEl) {
      locationEl.textContent = lessonLocationLabel(config.location);
    }
    if (datesEl) {
      datesEl.textContent = getLessonDatesLabel(root);
    }
    if (detailEl) {
      detailEl.textContent = lessonWithInstructorDetail(config.hours, people);
    }
    if (priceLineEl) {
      priceLineEl.textContent = `${price.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
    }

    updatePrice();
  };

  const rebuildLessonHoursOptions = () => {
    const hoursSelect = root.querySelector('.js-booking-lesson-hours');
    const participantsSelect = root.querySelector('.js-booking-lesson-participants');
    if (!hoursSelect || !participantsSelect) return;

    const people = Number(participantsSelect.value || 2);
    const location = getLessonConfig(root).location;
    const matrix = lessonPriceMatrixForPeople(people, location);
    const current = String(hoursSelect.value || '');
    const allHours = [
      ...Object.keys(matrix.single || {}),
      ...Object.keys(matrix.season || {}),
    ];
    if (current && !allHours.includes(current) && allHours.length) {
      hoursSelect.value = allHours[0];
      const typeInput = root.querySelector('input.js-booking-lesson-type[type="hidden"]');
      if (typeInput) {
        typeInput.value = Object.prototype.hasOwnProperty.call(matrix.single || {}, allHours[0])
          ? 'single'
          : 'season';
      }
    }
  };

  const LESSON_MAX_PARTICIPANTS = 3;

  const syncLessonParticipantForms = (targetCount = null) => {
    const wrap = root.querySelector('.js-booking-lesson-participants-wrap');
    if (!wrap || bookingType !== 'lesson') return;

    const parsedTarget = Number(targetCount);
    const target = Number.isFinite(parsedTarget) && parsedTarget > 0
      ? Math.min(LESSON_MAX_PARTICIPANTS, parsedTarget)
      : getLessonConfig(root).participants;
    let current = wrap.querySelectorAll('.booking-lesson-participant').length;
    const existingType = wrap.querySelector('.booking-lesson-participant [name^="student-type-"]')?.value
      || defaultLessonStudentType(root);

    while (current < target) {
      current += 1;
      const card = buildLessonStudentCard(root, current, { studentType: existingType });
      wrap.appendChild(card);
    }

    while (current > target) {
      const last = wrap.querySelector('.booking-lesson-participant:last-of-type');
      destroyBirthDatePicker(last?._birthMask);
      if (last?._birthMask?.masked) destroyPolishDateMask(last._birthMask);
      last?.remove();
      current -= 1;
    }

    reindexLessonStudentCards(wrap);
    syncLessonStudentRemoveButtons(root);
    updateLessonStudentsCount(root);
    updateLessonAddParticipantButton(root);
    updateLessonSchedulePanel(root);
    updateLessonStudentsNextButton(root);
    updateSummary();
  };

  const syncLessonStudentRemoveButtons = (rootEl) => {
    const cards = [...rootEl.querySelectorAll('.booking-lesson-participant')];
    cards.forEach((card) => {
      const removeBtn = card.querySelector('.js-lesson-student-remove');
      if (!removeBtn) return;
      removeBtn.hidden = cards.length <= 1;
    });
  };

  const updateLessonAddParticipantButton = (rootEl) => {
    const btn = rootEl.querySelector('.js-booking-add-lesson-participant');
    if (!btn) return;
    const count = rootEl.querySelectorAll('.booking-lesson-participant').length;
    btn.hidden = count >= LESSON_MAX_PARTICIPANTS;
  };

  let lessonMixPending = null;

  const getLessonGroupType = (rootEl) => (
    rootEl.querySelector('.booking-lesson-participant [name^="student-type-"]')?.value || 'adult'
  );

  const showLessonMixModal = (rootEl, pending = null) => {
    const modal = rootEl.querySelector('.js-booking-mix-modal');
    if (!modal || typeof modal.showModal !== 'function') {
      window.alert(t('mix_title', 'Nie łączymy dzieci z dorosłymi'));
      return;
    }

    lessonMixPending = pending;
    const titleEl = modal.querySelector('.js-booking-mix-title');
    const textEl = modal.querySelector('.js-booking-mix-text');
    const infoEl = modal.querySelector('.js-booking-mix-info');
    const switchBtn = modal.querySelector('.js-booking-mix-switch');
    const stayBtn = modal.querySelector('.js-booking-mix-stay');
    const currentType = pending?.currentType === 'child' ? 'child' : 'adult';
    const nextType = pending?.nextType === 'child' ? 'child' : 'adult';

    if (titleEl) {
      titleEl.textContent = t('mix_title', 'Nie łączymy dzieci z dorosłymi');
    }
    if (textEl) {
      textEl.textContent = t(
        'mix_text',
        'W jednej lekcji indywidualnej nie możemy połączyć dziecka z osobą dorosłą — to zupełnie inny sposób prowadzenia zajęć. Jeśli chcesz zapisać i dziecko, i dorosłego, złóż osobne rezerwacje: jedną dla dorosłego, drugą dla dziecka.',
      );
    }
    if (infoEl) {
      infoEl.textContent = t(
        'mix_info',
        'Pamiętaj też, że osoby w jednej grupie powinny być na zbliżonym poziomie umiejętności.',
      );
    }
    if (switchBtn) {
      switchBtn.textContent = nextType === 'child'
        ? t('mix_switch_children', 'Zamień na dzieci')
        : t('mix_switch_adults', 'Zamień na dorosłych');
    }
    if (stayBtn) {
      stayBtn.textContent = currentType === 'child'
        ? t('mix_stay_children', 'Zostań na dzieciach')
        : t('mix_stay_adults', 'Zostań na dorosłych');
    }

    if (!modal.open) modal.showModal();
  };

  const initLessonMixModal = (rootEl) => {
    const modal = rootEl.querySelector('.js-booking-mix-modal');
    if (!modal) return;

    modal.querySelector('.js-booking-mix-switch')?.addEventListener('click', () => {
      const pending = lessonMixPending;
      lessonMixPending = null;
      modal.close();
      pending?.onSwitch?.();
    });

    modal.querySelector('.js-booking-mix-stay')?.addEventListener('click', () => {
      const pending = lessonMixPending;
      lessonMixPending = null;
      modal.close();
      pending?.onStay?.();
    });

    modal.addEventListener('cancel', () => {
      const pending = lessonMixPending;
      lessonMixPending = null;
      pending?.onStay?.();
    });

    modal.addEventListener('click', (event) => {
      if (event.target !== modal) return;
      const pending = lessonMixPending;
      lessonMixPending = null;
      modal.close();
      pending?.onStay?.();
    });
  };

  const requestLessonGroupTypeChange = (rootEl, nextType, { onSwitched } = {}) => {
    const currentType = getLessonGroupType(rootEl);
    if (!nextType || nextType === currentType) {
      onSwitched?.(currentType);
      return;
    }

    showLessonMixModal(rootEl, {
      currentType,
      nextType,
      onSwitch: () => {
        applyLessonStudentTypeToAll(rootEl, nextType);
        updateLessonStudentsNextButton(rootEl);
        updateSummary();
        onSwitched?.(nextType);
      },
      onStay: () => {
        // Typy na kartach bez zmian — UI zostaje przy currentType.
      },
    });
  };

  const getLessonStudentTypes = (rootEl, exceptCard = null) => (
    [...rootEl.querySelectorAll('.booking-lesson-participant')]
      .filter((card) => card !== exceptCard)
      .map((card) => card.querySelector('[name^="student-type-"]')?.value || '')
      .filter(Boolean)
  );

  const hasLessonTypeConflict = (rootEl, nextType, exceptCard = null) => {
    if (!nextType) return false;
    const opposite = nextType === 'child' ? 'adult' : 'child';
    return getLessonStudentTypes(rootEl, exceptCard).includes(opposite);
  };

  const setLessonBirthError = (birthWrap, message = '') => {
    if (!birthWrap) return;
    const field = birthWrap.querySelector('.js-lesson-birth-field');
    const errorEl = birthWrap.querySelector('.js-lesson-birth-error');
    field?.classList.toggle('is-invalid', Boolean(message));
    birthWrap.classList.toggle('is-invalid', Boolean(message));
    if (errorEl) errorEl.textContent = message;
  };

  const bindLessonBirthPicker = (card) => {
    const input = card.querySelector('[name^="student-birth-"]');
    if (!input) return null;
    destroyBirthDatePicker(card._birthMask);
    if (card._birthMask?.masked) destroyPolishDateMask(card._birthMask);

    const picker = attachBirthDatePicker(input, {
      maxDate: new Date(),
      minDate: getLessonChildMinBirthDate(),
      onChange: () => {
        const birthWrap = card.querySelector('.js-lesson-student-birth');
        setLessonBirthError(birthWrap, '');
        setBookingFieldInvalid(input, false);
        updateLessonStudentsNextButton(root);
        updateSummary();
      },
    });
    card._birthMask = picker;

    input.addEventListener('blur', () => {
      const birthWrap = card.querySelector('.js-lesson-student-birth');
      if (birthWrap?.hidden) return;
      const result = validateBirthDatePicker(picker, {
        required: true,
        maxDate: new Date(),
        minDate: getLessonChildMinBirthDate(),
        minDateMessage: uiFmt('Data urodzenia: wiek dziecka nie może przekraczać %s lat', LESSON_MAX_CHILD_AGE),
        fieldLabel: t('label_birth', 'Data urodzenia'),
      });
      setLessonBirthError(birthWrap, result.valid ? '' : result.message);
    });
    return picker;
  };

  const setLessonStudentBirthValue = (card, value) => {
    const polish = isoBirthToPolish(value);
    if (!polish) return;
    if (card._birthMask) {
      card._birthMask.value = polish;
      return;
    }
    const input = card.querySelector('[name^="student-birth-"]');
    if (input) input.value = polish;
  };

  const applyLessonStudentType = (rootEl, card, nextType, { skipMixCheck = false } = {}) => {
    const typeInput = card.querySelector('[name^="student-type-"]');
    const birthWrap = card.querySelector('.js-lesson-student-birth');
    const birthInput = card.querySelector('[name^="student-birth-"]');
    const ordererBox = card.querySelector('.js-lesson-student-orderer');
    const isChild = nextType === 'child';

    if (!skipMixCheck && hasLessonTypeConflict(rootEl, nextType, card)) {
      requestLessonGroupTypeChange(rootEl, nextType);
      return false;
    }

    if (isChild && ordererBox?.checked) {
      ordererBox.checked = false;
    }

    card.querySelectorAll('.js-lesson-student-type').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.value === nextType);
      el.setAttribute('aria-pressed', el.dataset.value === nextType ? 'true' : 'false');
    });
    if (typeInput) typeInput.value = nextType;
    card.classList.toggle('has-child-birth', isChild);
    if (birthWrap) birthWrap.hidden = !isChild;
    if (birthInput) {
      birthInput.required = isChild;
      if (!isChild) {
        if (card._birthMask) card._birthMask.value = '';
        else birthInput.value = '';
        setLessonBirthError(birthWrap, '');
      }
    }
    return true;
  };

  /** Klient: przełącznik na jednej karcie ustawia typ dla całej grupy (same dzieci albo sami dorośli). */
  const applyLessonStudentTypeToAll = (rootEl, nextType) => {
    rootEl.querySelectorAll('.booking-lesson-participant').forEach((card) => {
      applyLessonStudentType(rootEl, card, nextType, { skipMixCheck: true });
    });
  };

  const defaultLessonStudentType = (rootEl) => {
    const raw = String(
      rootEl?.dataset?.lessonAudience
      || new URLSearchParams(window.location.search).get('audience')
      || '',
    ).toLowerCase();
    if (raw === 'children' || raw === 'child' || raw === 'dzieci') return 'child';
    return 'adult';
  };

  const buildLessonStudentCard = (rootEl, index, { studentType = null } = {}) => {
    const initialType = studentType || defaultLessonStudentType(rootEl);
    const isChild = initialType === 'child';
    const card = document.createElement('article');
    card.className = `lesson-student-card booking-lesson-participant${isChild ? ' has-child-birth' : ''}`;
    card.dataset.studentIndex = String(index);
    card.innerHTML = `
      <div class="lesson-student-card__header">
        <h4 class="lesson-student-card__title">${escapeHtml(uiFmt('Kursant nr %s', index))}</h4>
        <button type="button" class="lesson-student-card__remove js-lesson-student-remove">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.4"/></svg>
          ${escapeHtml(ui('Usuń'))}
        </button>
      </div>
      <div class="lesson-student-card__type" role="radiogroup" aria-label="${escapeHtml(ui('Typ kursanta'))}">
        <button type="button" class="lesson-student-card__type-btn${initialType === 'adult' ? ' is-active' : ''} js-lesson-student-type" data-value="adult" aria-pressed="${initialType === 'adult' ? 'true' : 'false'}">
          <span class="lesson-student-card__type-radio" aria-hidden="true"></span>${escapeHtml(ui('Dorosły'))}
        </button>
        <button type="button" class="lesson-student-card__type-btn${initialType === 'child' ? ' is-active' : ''} js-lesson-student-type" data-value="child" aria-pressed="${initialType === 'child' ? 'true' : 'false'}">
          <span class="lesson-student-card__type-radio" aria-hidden="true"></span>${escapeHtml(ui('Dziecko'))}
        </button>
        <input type="hidden" name="student-type-${index}" value="${initialType}">
      </div>
      ${index === 1 ? `
        <label class="lesson-student-card__orderer">
          <input type="checkbox" class="js-lesson-student-orderer" name="student-is-orderer-1" value="1">
          <span>${escapeHtml(ui('Kursant jest też zamawiającym'))} <em>${escapeHtml(ui('— uzupełnimy dane'))}</em></span>
        </label>
      ` : ''}
      <div class="lesson-student-card__fields">
        <label class="booking-form__field">
          <span class="booking-form__label">${escapeHtml(ui('Imię'))} <abbr title="${escapeHtml(ui('wymagane'))}">*</abbr></span>
          <input type="text" name="student-first-name-${index}" placeholder="${escapeHtml(ui('np. Anna'))}" required autocomplete="given-name">
        </label>
        <label class="booking-form__field">
          <span class="booking-form__label">${escapeHtml(ui('Nazwisko'))} <abbr title="${escapeHtml(ui('wymagane'))}">*</abbr></span>
          <input type="text" name="student-last-name-${index}" placeholder="${escapeHtml(ui('np. Kowalska'))}" required autocomplete="family-name">
        </label>
        <label class="lesson-student-card__birth booking-form__field js-lesson-student-birth"${isChild ? '' : ' hidden'}>
          <span class="booking-form__label">${escapeHtml(ui('Data urodzenia'))} <abbr title="${escapeHtml(ui('wymagane'))}">*</abbr></span>
          <span class="lesson-student-card__birth-field js-lesson-birth-field booking-form__control booking-form__control--date">
            <input type="text" name="student-birth-${index}" class="js-lesson-student-birth-input" inputmode="numeric" autocomplete="bday" placeholder="DD.MM.RRRR" aria-label="${escapeHtml(ui('Data urodzenia'))}"${isChild ? ' required' : ''}>
            <span class="booking-form__control-icon js-birth-datepicker-icon" aria-hidden="true">
              <img src="${escapeHtml(getCourseCalendarIcon())}" alt="" width="20" height="20">
            </span>
          </span>
          <span class="lesson-student-card__birth-error js-lesson-birth-error" role="alert"></span>
        </label>
      </div>
    `;

    bindLessonBirthPicker(card);

    card.querySelectorAll('.js-lesson-student-type').forEach((btn) => {
      btn.addEventListener('click', () => {
        const nextType = btn.dataset.value || 'adult';
        // Jedna zmiana = cała grupa; przy przejściu dzieci↔dorośli najpierw modal.
        requestLessonGroupTypeChange(rootEl, nextType);
      });
    });

    card.querySelector('.js-lesson-student-remove')?.addEventListener('click', () => {
      const wrap = rootEl.querySelector('.js-booking-lesson-participants-wrap');
      const count = wrap?.querySelectorAll('.booking-lesson-participant').length || 0;
      if (count <= 1) return;
      destroyBirthDatePicker(card._birthMask);
      if (card._birthMask?.masked) destroyPolishDateMask(card._birthMask);
      card.remove();
      reindexLessonStudentCards(wrap);
      const nextCount = wrap?.querySelectorAll('.booking-lesson-participant').length || 1;
      setLessonPoolConfig(rootEl, { people: nextCount });
      syncLessonStudentRemoveButtons(rootEl);
      updateLessonAddParticipantButton(rootEl);
      updateLessonStudentsNextButton(rootEl);
      updateSummary();
      schedulePersist();
    });

    card.querySelector('.js-lesson-student-orderer')?.addEventListener('change', (event) => {
      if (!event.target.checked) return;
      const currentType = card.querySelector('[name^="student-type-"]')?.value || 'adult';
      // Zamawiający = dorosły. Przy grupie dzieci najpierw potwierdź zmianę typu całej grupy.
      if (currentType === 'child' || hasLessonTypeConflict(rootEl, 'adult', card)) {
        event.target.checked = false;
        requestLessonGroupTypeChange(rootEl, 'adult', {
          onSwitched: (type) => {
            if (type !== 'adult') return;
            const box = card.querySelector('.js-lesson-student-orderer');
            if (box) box.checked = true;
            updateLessonStudentsNextButton(rootEl);
            updateSummary();
          },
        });
        return;
      }
      if (currentType !== 'adult') {
        applyLessonStudentType(rootEl, card, 'adult', { skipMixCheck: true });
      }
      updateLessonStudentsNextButton(rootEl);
      updateSummary();
    });

    card.addEventListener('input', (event) => {
      const field = event.target;
      if (field?.matches?.('input:not([type="checkbox"]), textarea, select')) {
        field.classList.toggle('is-invalid', !field.value?.trim());
      }
      updateLessonStudentsNextButton(rootEl);
      updateSummary();
    });

    return card;
  };

  const reindexLessonStudentCards = (wrap) => {
    if (!wrap) return;
    [...wrap.querySelectorAll('.booking-lesson-participant')].forEach((card, idx) => {
      const index = idx + 1;
      card.dataset.studentIndex = String(index);
      const title = card.querySelector('.lesson-student-card__title');
      if (title) title.textContent = uiFmt('Kursant nr %s', index);

      card.querySelectorAll('[name^="student-"]').forEach((field) => {
        field.name = field.name.replace(/-(\d+)$/, `-${index}`);
      });

      const orderer = card.querySelector('.lesson-student-card__orderer');
      if (orderer) orderer.hidden = index !== 1;
      if (index !== 1) {
        card.querySelector('.js-lesson-student-orderer')?.remove();
        card.querySelector('.lesson-student-card__orderer')?.remove();
      }
    });
  };

  const updateLessonStudentsCount = (rootEl) => {
    const count = rootEl.querySelectorAll('.booking-lesson-participant').length;
    rootEl.querySelectorAll('.js-lesson-students-count').forEach((el) => {
      el.textContent = String(count);
    });
  };

  const syncLessonScheduleVisibility = (rootEl) => {
    const schedule = rootEl.querySelector('.js-lesson-schedule');
    if (schedule) schedule.hidden = false;
  };

  const LESSON_START_TIMES = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'];
  const LESSON_END_TIMES = [...LESSON_START_TIMES, '20:00'];
  const lessonStartTimes = () => {
    const fromCatalog = lessonStartTimesForLocation(getLessonConfig(root).location);
    return fromCatalog.length ? fromCatalog : LESSON_START_TIMES;
  };
  const lessonEndTimes = () => {
    const fromCatalog = lessonEndTimesForLocation(getLessonConfig(root).location);
    return fromCatalog.length ? fromCatalog : LESSON_END_TIMES;
  };

  const timeToMinutes = (value) => {
    const parts = String(value || '').split(':');
    if (parts.length < 2) return NaN;
    return Number(parts[0]) * 60 + Number(parts[1]);
  };

  const hoursBetween = (start, end) => {
    const diff = timeToMinutes(end) - timeToMinutes(start);
    return Number.isFinite(diff) ? diff / 60 : 0;
  };

  const addHoursToTime = (start, hours) => {
    const mins = timeToMinutes(start) + Number(hours) * 60;
    if (!Number.isFinite(mins) || mins < 0) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 23) return '';
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const formatLessonSlotDate = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('pl-PL', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const closeAllBookingSelects = (except = null) => {
    root.querySelectorAll('.booking-select.is-open').forEach((el) => {
      if (el === except) return;
      el.classList.remove('is-open');
      el.querySelector('.booking-select__trigger')?.setAttribute('aria-expanded', 'false');
      const panel = el.querySelector('.booking-select__panel');
      if (panel) panel.hidden = true;
    });
  };

  const setBookingSelectDisabled = (wrap, disabled) => {
    if (!wrap) return;
    wrap.classList.toggle('is-disabled', disabled);
    const trigger = wrap.querySelector('.booking-select__trigger');
    if (trigger) trigger.disabled = disabled;
    if (disabled) {
      wrap.classList.remove('is-open');
      wrap.querySelector('.booking-select__trigger')?.setAttribute('aria-expanded', 'false');
      const panel = wrap.querySelector('.booking-select__panel');
      if (panel) panel.hidden = true;
    }
  };

  const setBookingSelectValue = (wrap, value, label) => {
    if (!wrap) return;
    wrap.dataset.value = value == null ? '' : String(value);
    const valueEl = wrap.querySelector('.booking-select__value');
    if (valueEl) valueEl.textContent = label || '—';
    wrap.querySelectorAll('.booking-select__option').forEach((option) => {
      option.classList.toggle('is-selected', option.dataset.value === String(value));
    });
  };

  const renderBookingSelectOptions = (panel, groups, selected = '') => {
    if (!panel) return;
    panel.innerHTML = groups.map((group) => {
      const heading = group.label
        ? `<div class="booking-select__group">${escapeHtml(group.label)}</div>`
        : '';
      const options = (group.options || []).map((opt) => {
        const selectedClass = String(opt.value) === String(selected) ? ' is-selected' : '';
        const disabledClass = opt.disabled ? ' is-disabled' : '';
        const typeAttr = opt.type ? ` data-type="${escapeHtml(opt.type)}"` : '';
        return `<button type="button" class="booking-select__option${selectedClass}${disabledClass}" role="option" data-value="${escapeHtml(String(opt.value))}" data-label="${escapeHtml(opt.label)}"${typeAttr}${opt.disabled ? ' disabled' : ''}>${escapeHtml(opt.label)}</button>`;
      }).join('');
      return heading + options;
    }).join('');
  };

  const getLessonPackageGroups = (people, usedHours = 0) => {
    const location = getLessonConfig(root).location;
    const matrix = lessonPriceMatrixForPeople(people, location);
    const mapBucket = (bucket, type, packageLabel) => Object.entries(bucket || {}).map(([hours, price]) => ({
      value: hours,
      type,
      disabled: usedHours > 0 && Number(hours) < usedHours,
      label: packageLabel
        ? uiFmt('%sh (pakiet) — %s zł', hours, price)
        : uiFmt('%sh — %s zł', hours, price),
    }));
    return [
      { label: ui('Pojedyncze lekcje'), options: mapBucket(matrix.single, 'single', false) },
      { label: ui('Pakiety godzin'), options: mapBucket(matrix.season, 'season', true) },
    ];
  };

  const getLessonPeopleGroups = () => ([{
    options: [1, 2, 3].map((n) => ({
      value: String(n),
      label: n === 1
        ? ui('1 osoba (do jednego instruktora)')
        : uiFmt('%s osoby (do jednego instruktora)', n),
    })),
  }]);

  const findPackageOption = (people, hours, lessonType) => {
    const groups = getLessonPackageGroups(people);
    const type = lessonType === 'season' ? 'season' : 'single';
    for (const group of groups) {
      const match = group.options.find((opt) => String(opt.value) === String(hours) && opt.type === type)
        || group.options.find((opt) => String(opt.value) === String(hours));
      if (match) return match;
    }
    return groups[0]?.options[0] || null;
  };

  const refreshLessonConfigSelects = (rootEl) => {
    const config = getLessonConfig(rootEl);
    const used = getLessonScheduleUsedHours(rootEl);
    const packageSelect = rootEl.querySelector('.js-lesson-package-select');
    const peopleSelect = rootEl.querySelector('.js-lesson-people-select');
    const packageOpt = findPackageOption(config.participants, config.hours, config.lessonType);
    renderBookingSelectOptions(
      rootEl.querySelector('.js-lesson-package-select-panel'),
      getLessonPackageGroups(config.participants, used),
      String(config.hours),
    );
    setBookingSelectValue(packageSelect, String(config.hours), packageOpt?.label || `${config.hours}h`);
    renderBookingSelectOptions(
      rootEl.querySelector('.js-lesson-people-select-panel'),
      getLessonPeopleGroups(),
      String(config.participants),
    );
    const peopleOpt = getLessonPeopleGroups()[0].options.find((opt) => opt.value === String(config.participants));
    setBookingSelectValue(peopleSelect, String(config.participants), peopleOpt?.label);
  };

  const setLessonPoolConfig = (rootEl, { hours, people, lessonType } = {}) => {
    const hoursInput = rootEl.querySelector('.js-booking-lesson-hours');
    const peopleInput = rootEl.querySelector('.js-booking-lesson-participants');
    const typeInput = rootEl.querySelector('input.js-booking-lesson-type[type="hidden"]');
    const prevType = getLessonConfig(rootEl).lessonType;
    const hoursChanged = hours != null;
    const typeChanged = Boolean(lessonType) && lessonType !== prevType;
    if (hours != null) {
      if (hoursInput) hoursInput.value = String(hours);
      rootEl.dataset.lessonHours = String(hours);
    }
    if (people != null) {
      if (peopleInput) peopleInput.value = String(people);
      rootEl.dataset.lessonParticipants = String(people);
    }
    if (lessonType) {
      if (typeInput) typeInput.value = lessonType;
      rootEl.dataset.lessonType = lessonType;
    }
    if (people != null) syncLessonParticipantForms(people);
    if (hoursChanged || typeChanged) resyncLessonScheduleToPackage(rootEl);
    updateLessonSchedulePanel(rootEl);
    updateLessonSummary();
  };

  const isLessonSeasonPackage = (rootEl) => getLessonConfig(rootEl).lessonType === 'season';

  const syncLessonScheduleCopy = (rootEl) => {
    const season = isLessonSeasonPackage(rootEl);
    const lead = rootEl.querySelector('.js-lesson-schedule-lead');
    if (lead) {
      lead.textContent = season
        ? ui('Dodaj terminy w trakcie sezonu — godziny możesz rozbić na kilka zjazdów.')
        : ui('Dodaj termin i wybierz godzinę rozpoczęcia — cały pakiet to jeden ciągły blok godzin.');
    }
  };

  const updateLessonSchedulePanel = (rootEl) => {
    syncLessonScheduleVisibility(rootEl);
    const config = getLessonConfig(rootEl);
    const count = rootEl.querySelectorAll('.booking-lesson-participant').length || config.participants;
    const totalHours = config.hours;
    const hoursText = lessonWithInstructorDetail(totalHours, count);

    const poolText = rootEl.querySelector('.js-lesson-schedule-pool-text');
    const totalEl = rootEl.querySelector('.js-lesson-schedule-total');
    if (poolText) poolText.textContent = hoursText;
    if (totalEl) totalEl.textContent = `${totalHours}h`;

    syncLessonScheduleCopy(rootEl);
    refreshLessonConfigSelects(rootEl);
    updateLessonScheduleHours(rootEl);
  };

  const getLessonScheduleUsedHours = (rootEl, exceptSlot = null) => (
    [...rootEl.querySelectorAll('.lesson-schedule__slot')].reduce((sum, slot) => {
      if (slot === exceptSlot) return sum;
      return sum + Number(slot.dataset.hours || slot.querySelector('.js-lesson-slot-hours')?.value || 0);
    }, 0)
  );

  const getLessonScheduleRemaining = (rootEl, exceptSlot = null) => {
    const total = getLessonConfig(rootEl).hours;
    return Math.max(0, total - getLessonScheduleUsedHours(rootEl, exceptSlot));
  };

  const getLessonScheduleSlotCount = (rootEl, exceptSlot = null) => (
    [...rootEl.querySelectorAll('.lesson-schedule__slot')].filter((slot) => slot !== exceptSlot).length
  );

  const isValidLessonEndTime = (time) => lessonEndTimes().includes(time);

  const lessonTimeRangesOverlap = (startA, endA, startB, endB) => {
    const a0 = timeToMinutes(startA);
    const a1 = timeToMinutes(endA);
    const b0 = timeToMinutes(startB);
    const b1 = timeToMinutes(endB);
    if (![a0, a1, b0, b1].every(Number.isFinite)) return false;
    // Stykanie się (np. 08–09 i 09–12) jest OK — konflikt tylko przy przecięciu.
    return a0 < b1 && b0 < a1;
  };

  const lessonRangeOverlapsExisting = (rootEl, date, start, end, exceptSlot = null) => {
    if (!date || !start || !end) return false;
    return [...rootEl.querySelectorAll('.lesson-schedule__slot')].some((slot) => {
      if (slot === exceptSlot || slot.dataset.date !== date) return false;
      return lessonTimeRangesOverlap(start, end, slot.dataset.start || '', slot.dataset.end || '');
    });
  };

  /** Elastyczny termin: jeden ciągły blok = cały pakiet. Sezon: dowolny kawałek z puli. */
  const getLessonBlockHours = (rootEl, replaceEl = null) => getLessonScheduleRemaining(rootEl, replaceEl);

  const getValidLessonEndTimes = (rootEl, start, replaceEl = null, date = null) => {
    const remaining = getLessonScheduleRemaining(rootEl, replaceEl);
    if (!start || remaining < 1) return [];
    const day = date || lessonHoursModalState.date || '';
    return lessonEndTimes().filter((end) => {
      const hours = hoursBetween(start, end);
      if (!(hours >= 1 && hours <= remaining && Number.isInteger(hours))) return false;
      if (day && lessonRangeOverlapsExisting(rootEl, day, start, end, replaceEl)) return false;
      return true;
    });
  };

  const getValidLessonStartTimes = (rootEl, replaceEl = null, date = null) => {
    const remaining = getLessonScheduleRemaining(rootEl, replaceEl);
    if (remaining < 1) return [];
    const day = date || lessonHoursModalState.date || '';
    if (!isLessonSeasonPackage(rootEl)) {
      const blockHours = remaining;
      return lessonStartTimes().filter((start) => {
        const end = addHoursToTime(start, blockHours);
        return Boolean(end && isValidLessonEndTime(end) && hoursBetween(start, end) === blockHours);
      });
    }
    return lessonStartTimes().filter((start) => getValidLessonEndTimes(rootEl, start, replaceEl, day).length > 0);
  };

  const updateLessonScheduleHours = (rootEl) => {
    const config = getLessonConfig(rootEl);
    const total = config.hours;
    const used = getLessonScheduleUsedHours(rootEl);
    const slotCount = getLessonScheduleSlotCount(rootEl);
    const usedEl = rootEl.querySelector('.js-lesson-schedule-used');
    const labelEl = rootEl.querySelector('.js-lesson-schedule-pool-label');
    const addBtn = rootEl.querySelector('.js-lesson-schedule-add');

    if (usedEl) usedEl.textContent = `${used}h`;
    if (labelEl) {
      labelEl.textContent = used >= total ? 'ROZPISANE' : 'DO ROZPISANIA';
    }
    const season = isLessonSeasonPackage(rootEl);
    // Single/elastyczny: tylko jeden ciągły blok — po wyborze ukryj „Dodaj termin”.
    // Sezon: wiele terminów; ukryj gdy wykorzystany cały limit godzin.
    const addDisabled = season ? used >= total : (slotCount >= 1 || used >= total);
    addBtn?.classList.toggle('is-disabled', addDisabled);
    if (addBtn) addBtn.hidden = season ? used >= total : slotCount >= 1;
    if (addDisabled) {
      rootEl.querySelector('.js-lesson-schedule-date-input')?._birthDatePicker?.close?.();
      rootEl.querySelector('.js-lesson-schedule-date-input')?.blur?.();
    }
    updateLessonSummary();
  };

  const upsertLessonScheduleSlot = (rootEl, {
    date,
    start,
    end,
    replaceEl = null,
  } = {}) => {
    if (!date || !start || !end) return;
    const hours = hoursBetween(start, end);
    const remaining = getLessonScheduleRemaining(rootEl, replaceEl);
    const season = isLessonSeasonPackage(rootEl);
    if (hours < 1 || hours > remaining || !Number.isInteger(hours)) return;
    if (!season && hours !== remaining) return;
    if (!season && !replaceEl && getLessonScheduleSlotCount(rootEl) >= 1) return;
    if (season && lessonRangeOverlapsExisting(rootEl, date, start, end, replaceEl)) return;

    const slotsWrap = rootEl.querySelector('.js-lesson-schedule-slots');
    if (!slotsWrap) return;

    const slot = replaceEl || document.createElement('article');
    slot.className = 'lesson-schedule__slot';
    slot.dataset.date = date;
    slot.dataset.start = start;
    slot.dataset.end = end;
    slot.dataset.hours = String(hours);
    slot.innerHTML = `
      <div class="lesson-schedule__slot-head">
        <strong>${escapeHtml(formatLessonSlotDate(date))}</strong>
        <button type="button" class="lesson-schedule__slot-remove js-lesson-slot-remove" aria-label="${escapeHtml(ui('Usuń termin'))}">×</button>
      </div>
      <p class="lesson-schedule__slot-time">${escapeHtml(start)} – ${escapeHtml(end)} · ${hours}h</p>
      <input type="hidden" class="js-lesson-slot-hours" value="${hours}">
      <button type="button" class="lesson-schedule__slot-edit js-lesson-slot-edit">${escapeHtml(ui('Edytuj godziny'))}</button>
    `;

    slot.querySelector('.js-lesson-slot-remove')?.addEventListener('click', () => {
      slot.remove();
      updateLessonScheduleHours(rootEl);
      refreshLessonConfigSelects(rootEl);
    });
    slot.querySelector('.js-lesson-slot-edit')?.addEventListener('click', () => {
      openLessonHoursModal(rootEl, {
        date: slot.dataset.date,
        start: slot.dataset.start,
        end: slot.dataset.end,
        replaceEl: slot,
      });
    });

    if (!replaceEl) slotsWrap.appendChild(slot);
    updateLessonScheduleHours(rootEl);
    refreshLessonConfigSelects(rootEl);
  };

  /** Po zmianie pakietu dopasuj sloty do nowego limitu godzin. */
  const resyncLessonScheduleToPackage = (rootEl) => {
    const season = isLessonSeasonPackage(rootEl);
    const total = getLessonConfig(rootEl).hours;
    const slots = [...rootEl.querySelectorAll('.lesson-schedule__slot')];
    if (!slots.length) return;

    if (!season) {
      const [first, ...rest] = slots;
      rest.forEach((slot) => slot.remove());
      const date = first.dataset.date || '';
      const start = first.dataset.start || '';
      const end = addHoursToTime(start, total);
      if (!date || !start || !end || !isValidLessonEndTime(end)) {
        first.remove();
        return;
      }
      first.dataset.end = end;
      first.dataset.hours = String(total);
      const timeEl = first.querySelector('.lesson-schedule__slot-time');
      if (timeEl) timeEl.textContent = `${start} – ${end} · ${total}h`;
      const hoursInput = first.querySelector('.js-lesson-slot-hours');
      if (hoursInput) hoursInput.value = String(total);
      return;
    }

    let used = getLessonScheduleUsedHours(rootEl);
    [...slots].reverse().forEach((slot) => {
      if (used <= total) return;
      used -= Number(slot.dataset.hours || 0);
      slot.remove();
    });

    used = getLessonScheduleUsedHours(rootEl);
    if (used <= total) return;
    const remainingSlots = [...rootEl.querySelectorAll('.lesson-schedule__slot')];
    const last = remainingSlots[remainingSlots.length - 1];
    if (!last) return;
    const others = used - Number(last.dataset.hours || 0);
    const allow = Math.max(0, total - others);
    if (allow < 1) {
      last.remove();
      return;
    }
    const start = last.dataset.start || '';
    const end = addHoursToTime(start, allow);
    if (!start || !end || !isValidLessonEndTime(end)) {
      last.remove();
      return;
    }
    last.dataset.end = end;
    last.dataset.hours = String(allow);
    const timeEl = last.querySelector('.lesson-schedule__slot-time');
    if (timeEl) timeEl.textContent = `${start} – ${end} · ${allow}h`;
    const hoursInput = last.querySelector('.js-lesson-slot-hours');
    if (hoursInput) hoursInput.value = String(allow);
  };

  const toLessonDateIso = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  };

  let lessonHoursModalState = {
    date: '',
    start: '',
    end: '',
    replaceEl: null,
  };

  const updateLessonHoursModalSave = (rootEl) => {
    const saveBtn = rootEl.querySelector('.js-lesson-hours-save');
    if (!saveBtn) return;
    const { start, end, replaceEl } = lessonHoursModalState;
    const hours = hoursBetween(start, end);
    const remaining = getLessonScheduleRemaining(rootEl, replaceEl);
    const season = isLessonSeasonPackage(rootEl);
    const valid = Boolean(
      start
      && end
      && hours >= 1
      && hours <= remaining
      && Number.isInteger(hours)
      && (season || hours === remaining)
      && !(season && lessonRangeOverlapsExisting(
        rootEl,
        lessonHoursModalState.date,
        start,
        end,
        replaceEl,
      )),
    );
    saveBtn.disabled = !valid;
  };

  const applyLessonHoursBlockEnd = (rootEl) => {
    const { start, replaceEl } = lessonHoursModalState;
    const blockHours = getLessonBlockHours(rootEl, replaceEl);
    const endWrap = rootEl.querySelector('.js-lesson-hours-end');
    const end = start && blockHours >= 1 ? addHoursToTime(start, blockHours) : '';
    const valid = Boolean(end && isValidLessonEndTime(end) && hoursBetween(start, end) === blockHours);
    lessonHoursModalState.end = valid ? end : '';
    setBookingSelectValue(endWrap, lessonHoursModalState.end, lessonHoursModalState.end || '—');
    // Elastyczny termin: koniec wynika z bloku godzin — bez ręcznego wyboru.
    setBookingSelectDisabled(endWrap, true);
    updateLessonHoursModalSave(rootEl);
  };

  const applyLessonHoursSeasonEndOptions = (rootEl, preferredEnd = '') => {
    const { start, replaceEl } = lessonHoursModalState;
    const endWrap = rootEl.querySelector('.js-lesson-hours-end');
    const ends = getValidLessonEndTimes(rootEl, start, replaceEl);
    const endValid = ends.includes(preferredEnd) ? preferredEnd : (ends[0] || '');
    lessonHoursModalState.end = endValid;
    renderBookingSelectOptions(
      rootEl.querySelector('.js-lesson-hours-end-panel'),
      [{ options: [{ value: '', label: '—' }, ...ends.map((time) => ({ value: time, label: time }))] }],
      endValid,
    );
    setBookingSelectValue(endWrap, endValid, endValid || '—');
    setBookingSelectDisabled(endWrap, !start || ends.length === 0);
    updateLessonHoursModalSave(rootEl);
  };

  const openLessonHoursModal = (rootEl, { date, start = '', end = '', replaceEl = null } = {}) => {
    const modal = rootEl.querySelector('.js-lesson-hours-modal');
    if (!modal || !date) return;
    const season = isLessonSeasonPackage(rootEl);
    const remaining = getLessonScheduleRemaining(rootEl, replaceEl);
    if (remaining < 1 && !replaceEl) return;
    if (!season && !replaceEl && getLessonScheduleSlotCount(rootEl) >= 1) return;

    lessonHoursModalState = { date, start, end, replaceEl };
    const dateEl = rootEl.querySelector('.js-lesson-hours-modal-date');
    const remainEl = rootEl.querySelector('.js-lesson-hours-modal-remain');
    const remainLabel = rootEl.querySelector('.js-lesson-hours-modal-remain-label');
    const modalLabel = rootEl.querySelector('.js-lesson-hours-modal-label');
    const hintEl = rootEl.querySelector('.js-lesson-hours-modal-hint');
    if (dateEl) dateEl.textContent = formatLessonSlotDate(date);
    if (remainEl) remainEl.textContent = `${remaining}h`;
    if (remainLabel) remainLabel.textContent = season ? ui('pozostało') : ui('blok');
    if (modalLabel) {
      modalLabel.textContent = season ? ui('Wybierz godziny lekcji') : ui('Wybierz godzinę rozpoczęcia');
    }
    if (hintEl) {
      hintEl.textContent = season
        ? ui('Pakiet sezonowy możesz rozbić na kilka terminów. Godziny w tym samym dniu nie mogą się nakładać.')
        : ui('Lekcja to jeden ciągły blok godzin. Wybierz rozpoczęcie — zakończenie ustawi się automatycznie (np. 3h od 10:00 to 10:00–13:00).');
    }

    const startOptions = getValidLessonStartTimes(rootEl, replaceEl);
    const startValid = startOptions.includes(start) ? start : '';
    lessonHoursModalState.start = startValid;
    renderBookingSelectOptions(
      rootEl.querySelector('.js-lesson-hours-start-panel'),
      [{ options: [{ value: '', label: '—' }, ...startOptions.map((time) => ({ value: time, label: time }))] }],
      startValid,
    );
    setBookingSelectValue(rootEl.querySelector('.js-lesson-hours-start'), startValid, startValid || '—');
    if (season) applyLessonHoursSeasonEndOptions(rootEl, end);
    else applyLessonHoursBlockEnd(rootEl);

    if (typeof modal.showModal === 'function') modal.showModal();
  };

  const closeLessonScheduleDatePicker = (rootEl) => {
    const dateInput = rootEl.querySelector('.js-lesson-schedule-date-input');
    dateInput?._birthDatePicker?.close?.();
    dateInput?.blur?.();
  };

  const closeLessonHoursModal = (rootEl) => {
    const modal = rootEl.querySelector('.js-lesson-hours-modal');
    closeAllBookingSelects();
    lessonHoursModalState = { date: '', start: '', end: '', replaceEl: null };
    if (modal?.open) modal.close();
    closeLessonScheduleDatePicker(rootEl);
  };

  const setLessonScheduleEditorOpen = (rootEl, open) => {
    const editor = rootEl.querySelector('.js-lesson-schedule-editor');
    const toggle = rootEl.querySelector('.js-lesson-schedule-toggle');
    const label = rootEl.querySelector('.js-lesson-schedule-toggle-label');
    if (editor) editor.hidden = !open;
    toggle?.setAttribute('aria-expanded', String(open));
    toggle?.classList.toggle('is-open', open);
    if (label) label.textContent = open ? ui('Gotowe') : ui('Zmień');
    if (!open) closeAllBookingSelects();
  };

  const initLessonSchedule = (rootEl) => {
    const dateInput = rootEl.querySelector('.js-lesson-schedule-date-input');
    const addBtn = rootEl.querySelector('.js-lesson-schedule-add');
    const hoursInput = rootEl.querySelector('.js-booking-lesson-hours');
    const peopleInput = rootEl.querySelector('.js-booking-lesson-participants');
    const typeInput = rootEl.querySelector('input.js-booking-lesson-type[type="hidden"]');
    const config = getLessonConfig(rootEl);

    if (hoursInput && !hoursInput.value) hoursInput.value = String(config.hours);
    if (peopleInput && !peopleInput.value) peopleInput.value = String(config.participants);
    if (typeInput && !typeInput.value) typeInput.value = config.lessonType || 'single';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 18);

    const picker = dateInput
      ? attachBirthDatePicker(dateInput, {
        minDate: today,
        maxDate,
        autocomplete: 'off',
        allowInput: false,
        confirm: false,
        onChange: (date) => {
          const iso = toLessonDateIso(date);
          picker?.close?.();
          picker.value = '';
          dateInput.blur();
          if (!iso) return;
          if (!isLessonSeasonPackage(rootEl) && getLessonScheduleSlotCount(rootEl) >= 1) return;
          if (getLessonScheduleRemaining(rootEl) < 1) return;
          // Po close flatpickr czasem wraca focus na input — odłóż modal, żeby kalendarz nie otworzył się znowu.
          window.setTimeout(() => {
            closeLessonScheduleDatePicker(rootEl);
            openLessonHoursModal(rootEl, { date: iso });
          }, 0);
        },
      })
      : null;

    // Tylko jawne otwarcie z „Dodaj termin” — bez auto-open przy focus/kliknięciu inputu.
    if (picker?.fp) {
      try {
        picker.fp.set('clickOpens', false);
      } catch {
        /* ignore */
      }
    }

    addBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (addBtn.hidden || addBtn.classList.contains('is-disabled')) return;
      picker?.open();
    });

    rootEl.querySelector('.js-lesson-schedule-toggle')?.addEventListener('click', () => {
      const editor = rootEl.querySelector('.js-lesson-schedule-editor');
      setLessonScheduleEditorOpen(rootEl, Boolean(editor?.hidden));
    });
    rootEl.querySelector('.js-lesson-schedule-editor-done')?.addEventListener('click', () => {
      setLessonScheduleEditorOpen(rootEl, false);
    });

    rootEl.addEventListener('click', (event) => {
      const option = event.target.closest('.booking-select__option');
      if (option) {
        event.preventDefault();
        if (option.disabled || option.classList.contains('is-disabled')) return;
        const wrap = option.closest('.booking-select');
        if (!wrap || wrap.classList.contains('is-disabled')) return;
        const value = option.dataset.value || '';
        const label = option.dataset.label || option.textContent.trim();
        setBookingSelectValue(wrap, value, label);
        closeAllBookingSelects();
        const selectType = wrap.dataset.select || '';
        if (selectType === 'people') {
          const people = Number(value);
          if (people) setLessonPoolConfig(rootEl, { people });
        } else if (selectType === 'package') {
          const hours = Number(value);
          if (hours) {
            setLessonPoolConfig(rootEl, {
              hours,
              lessonType: option.dataset.type === 'season' ? 'season' : 'single',
            });
          }
        } else if (selectType === 'start') {
          lessonHoursModalState.start = value;
          const remainEl = rootEl.querySelector('.js-lesson-hours-modal-remain');
          if (remainEl) {
            remainEl.textContent = `${getLessonScheduleRemaining(rootEl, lessonHoursModalState.replaceEl)}h`;
          }
          if (isLessonSeasonPackage(rootEl)) applyLessonHoursSeasonEndOptions(rootEl);
          else applyLessonHoursBlockEnd(rootEl);
        } else if (selectType === 'end') {
          if (isLessonSeasonPackage(rootEl)) {
            lessonHoursModalState.end = value;
            updateLessonHoursModalSave(rootEl);
          } else {
            // Elastyczny termin: koniec jest wyliczany z bloku godzin.
            applyLessonHoursBlockEnd(rootEl);
          }
        }
        return;
      }

      const trigger = event.target.closest('.booking-select__trigger');
      if (trigger) {
        const wrap = trigger.closest('.booking-select');
        if (!wrap || wrap.classList.contains('is-disabled') || trigger.disabled) return;
        const willOpen = !wrap.classList.contains('is-open');
        closeAllBookingSelects(willOpen ? wrap : null);
        wrap.classList.toggle('is-open', willOpen);
        trigger.setAttribute('aria-expanded', String(willOpen));
        const panel = wrap.querySelector('.booking-select__panel');
        if (panel) panel.hidden = !willOpen;
        return;
      }

      if (!event.target.closest('.booking-select')) closeAllBookingSelects();
    });

    rootEl.querySelector('.js-lesson-hours-cancel')?.addEventListener('click', () => closeLessonHoursModal(rootEl));
    rootEl.querySelector('.js-lesson-hours-save')?.addEventListener('click', () => {
      const { date, start, end, replaceEl } = lessonHoursModalState;
      upsertLessonScheduleSlot(rootEl, { date, start, end, replaceEl });
      closeLessonHoursModal(rootEl);
      closeLessonScheduleDatePicker(rootEl);
    });
    const hoursModal = rootEl.querySelector('.js-lesson-hours-modal');
    hoursModal?.addEventListener('click', (event) => {
      if (event.target === hoursModal) closeLessonHoursModal(rootEl);
    });
    hoursModal?.addEventListener('cancel', () => {
      lessonHoursModalState = { date: '', start: '', end: '', replaceEl: null };
      closeAllBookingSelects();
      closeLessonScheduleDatePicker(rootEl);
    });
    hoursModal?.addEventListener('close', () => {
      closeLessonScheduleDatePicker(rootEl);
    });

    updateLessonSchedulePanel(rootEl);
  };

  const validateLessonStudentsStep = (rootEl, { markInvalid = true } = {}) => {
    const cards = [...rootEl.querySelectorAll('.booking-lesson-participant')];
    if (!cards.length) return false;

    const types = cards.map((card) => card.querySelector('[name^="student-type-"]')?.value || 'adult');
    if (types.includes('adult') && types.includes('child')) {
      if (markInvalid) {
        const currentType = types[0] === 'child' ? 'child' : 'adult';
        const nextType = currentType === 'child' ? 'adult' : 'child';
        showLessonMixModal(rootEl, {
          currentType,
          nextType,
          onSwitch: () => {
            applyLessonStudentTypeToAll(rootEl, nextType);
            updateLessonStudentsNextButton(rootEl);
            updateSummary();
          },
          onStay: () => {
            applyLessonStudentTypeToAll(rootEl, currentType);
            updateLessonStudentsNextButton(rootEl);
            updateSummary();
          },
        });
      }
      return false;
    }

    let valid = true;
    cards.forEach((card) => {
      const first = card.querySelector('[name^="student-first-name-"]');
      const last = card.querySelector('[name^="student-last-name-"]');
      const type = card.querySelector('[name^="student-type-"]')?.value || 'adult';
      const birthWrap = card.querySelector('.js-lesson-student-birth');
      const birth = card.querySelector('[name^="student-birth-"]');

      [first, last].forEach((field) => {
        if (!field) return;
        const fieldValid = Boolean(field.value?.trim());
        if (markInvalid) setBookingFieldInvalid(field, !fieldValid);
        if (!fieldValid) valid = false;
      });

      if (type === 'child') {
        const result = validateBirthDatePicker(card._birthMask || birth, {
          required: true,
          maxDate: new Date(),
          minDate: getLessonChildMinBirthDate(),
          minDateMessage: uiFmt('Data urodzenia: wiek dziecka nie może przekraczać %s lat', LESSON_MAX_CHILD_AGE),
          fieldLabel: t('label_birth', 'Data urodzenia'),
        });
        if (markInvalid) {
          setLessonBirthError(birthWrap, result.valid ? '' : result.message);
          setBookingFieldInvalid(birth, !result.valid);
        }
        if (!result.valid) valid = false;
      } else if (markInvalid) {
        setLessonBirthError(birthWrap, '');
        setBookingFieldInvalid(birth, false);
      }
    });

    return valid;
  };

  const updateLessonStudentsNextButton = (rootEl) => {
    const btn = rootEl.querySelector('.js-lesson-students-next');
    if (!btn) return;
    setBookingCtaMuted(btn, !validateLessonStudentsStep(rootEl, { markInvalid: false }));
  };

  const getLessonPackageLabel = (config) => {
    const discipline = config.discipline === 'snowboard' ? 'Snowboard' : 'Narty';
    if (config.lessonType === 'season') {
      return `${discipline} · Pakiet ${config.hours} godzin`;
    }
    const hoursWord = config.hours === 1 ? 'godzina' : config.hours < 5 ? 'godziny' : 'godzin';
    return `${discipline} · ${config.hours} ${hoursWord}`;
  };

  const getLessonSchedulePills = (rootEl) => (
    [...rootEl.querySelectorAll('.lesson-schedule__slot')].map((slot) => {
      const date = slot.dataset.date;
      const hours = Number(slot.dataset.hours || slot.querySelector('.js-lesson-slot-hours')?.value || 0);
      const start = slot.dataset.start || '';
      const end = slot.dataset.end || '';
      if (!date || !hours) return null;
      const dateStr = new Date(`${date}T12:00:00`).toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      if (start && end) return `${dateStr} · ${start}–${end}`;
      return `${dateStr} · ${hours}h`;
    }).filter(Boolean)
  );

  const getLessonProductUrl = (rootEl) => {
    const params = new URLSearchParams(window.location.search);
    const productUrl = rootEl.dataset.lessonProductUrl || './lekcje-indywidualne.html';
    const base = productUrl.split('?')[0];
    return params.toString() ? `${base}?${params.toString()}` : base;
  };

  const updateLessonReview = () => {
    if (bookingType !== 'lesson') return;

    const contact = collectContact(root, bookingType);
    const config = getLessonConfig(root);
    const cards = [...root.querySelectorAll('.booking-lesson-participant')];
    const schedulePills = getLessonSchedulePills(root);

    const ordererLine = root.querySelector('.js-lesson-review-orderer-line');
    const ordererPhone = root.querySelector('.js-lesson-review-orderer-phone');
    const ordererNotes = root.querySelector('.js-lesson-review-orderer-notes');
    const packageEl = root.querySelector('.js-lesson-review-package');
    const locationEl = root.querySelector('.js-lesson-review-location');
    const studentsCountEl = root.querySelector('.js-lesson-review-students-count');
    const studentsList = root.querySelector('.js-lesson-review-students-list');

    const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    if (ordererLine) {
      ordererLine.textContent = fullName && contact.email
        ? `${fullName} · ${contact.email}`
        : fullName || contact.email || '—';
    }
    if (ordererPhone) {
      ordererPhone.textContent = formatPhoneWithPrefix(contact.phone, contact.phone_prefix) || '—';
    }
    if (ordererNotes) ordererNotes.textContent = contact.notes || '—';

    if (packageEl) packageEl.textContent = getLessonPackageLabel(config);
    if (locationEl) {
      const label = LESSON_LOCATIONS[config.location] || config.location;
      locationEl.textContent = config.location === 'harenda' ? `${label}, ${ui('Polska')}` : label;
    }
    if (studentsCountEl) studentsCountEl.textContent = String(cards.length);

    if (studentsList) {
      studentsList.innerHTML = cards.map((card) => {
        const first = card.querySelector('[name^="student-first-name-"]')?.value?.trim() || '';
        const last = card.querySelector('[name^="student-last-name-"]')?.value?.trim() || '';
        const type = card.querySelector('[name^="student-type-"]')?.value || 'adult';
        const typeLabel = type === 'child' ? ui('dziecko') : ui('dorosły');
        const name = [first, last].filter(Boolean).join(' ') || '—';
        const safeName = escapeHtml(name);
        const pillsHtml = schedulePills.length
          ? schedulePills.map((pill) => `<span class="lesson-review-slot-pill">${escapeHtml(pill)}</span>`).join('')
          : `<span class="lesson-review-slot-pill lesson-review-slot-pill--muted">${escapeHtml(ui('Brak wybranych terminów'))}</span>`;

        return `
          <article class="lesson-review-student">
            <div class="lesson-review-student__head">
              <p class="lesson-review-student__name"><strong>${safeName}</strong> <span>(${typeLabel})</span></p>
            </div>
            <div class="lesson-review-student__schedule">
              <div class="lesson-review-student__schedule-label">${escapeHtml(ui('WYBRANE TERMINY'))}</div>
              <div class="lesson-review-student__schedule-pills">${pillsHtml}</div>
            </div>
            <button type="button" class="lesson-review-edit js-lesson-review-edit-schedule">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" stroke-width="2"/></svg>
              Edytuj terminy
            </button>
          </article>
        `;
      }).join('');
    }

    updateLessonSummary();
  };

  const initLessonReview = (rootEl) => {
    rootEl.querySelector('.js-lesson-review-edit-orderer')?.addEventListener('click', () => goToStep(LESSON_ORDERER_STEP));

    rootEl.querySelector('.js-lesson-review-edit-lesson')?.addEventListener('click', () => {
      window.location.href = getLessonProductUrl(rootEl);
    });

    rootEl.addEventListener('click', (event) => {
      if (event.target.closest('.js-lesson-review-edit-schedule')) {
        goToStep(LESSON_STUDENTS_STEP);
      }
    });

    const ordererToggle = rootEl.querySelector('.js-lesson-review-orderer-toggle');
    const ordererBody = rootEl.querySelector('.js-lesson-review-orderer-body');
    ordererToggle?.addEventListener('click', () => {
      const expanded = ordererToggle.getAttribute('aria-expanded') === 'true';
      ordererToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      ordererToggle.classList.toggle('is-open', !expanded);
      if (ordererBody) ordererBody.hidden = expanded;
    });
  };

  const formatLessonMoney = (amount) => (
    `${amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`
  );

  const updateLessonConfirmation = () => {
    if (bookingType !== 'lesson') return;

    const contact = collectContact(root, bookingType);
    const config = getLessonConfig(root);
    const people = root.querySelectorAll('.booking-lesson-participant').length || config.participants;
    const price = getLessonPrice({
      people,
      hours: config.hours,
      lessonType: config.lessonType,
      location: config.location,
      discipline: config.discipline,
    });
    const priceText = formatLessonMoney(price);
    const discipline = config.discipline === 'snowboard' ? 'Snowboard' : 'Narty';
    const locationText = lessonLocationLabel(config.location);
    const detailText = lessonWithInstructorDetail(config.hours, people);

    root.querySelector('.js-lesson-confirm-discipline')?.replaceChildren(document.createTextNode(discipline));
    root.querySelector('.js-lesson-confirm-location')?.replaceChildren(document.createTextNode(locationText));
    const datesEl = root.querySelector('.js-lesson-confirm-dates');
    if (datesEl) datesEl.textContent = getLessonDatesLabel(root);
    const peopleEl = root.querySelector('.js-lesson-confirm-people');
    if (peopleEl) {
      peopleEl.textContent = lessonPeopleNominative(people);
    }
    const detailEl = root.querySelector('.js-lesson-confirm-detail');
    const priceEl = root.querySelector('.js-lesson-confirm-price');
    const totalElConfirm = root.querySelector('.js-lesson-confirm-total');
    if (detailEl) detailEl.textContent = detailText;
    if (priceEl) priceEl.textContent = priceText;
    if (totalElConfirm) totalElConfirm.textContent = priceText;

    const pillsWrap = root.querySelector('.js-lesson-confirm-schedule-pills');
    const schedulePills = getLessonSchedulePills(root);
    if (pillsWrap) {
      pillsWrap.innerHTML = schedulePills.length
        ? schedulePills.map((pill) => `<span class="lesson-review-slot-pill lesson-confirm-schedule-pill">${escapeHtml(pill)}</span>`).join('')
        : `<span class="lesson-review-slot-pill lesson-review-slot-pill--muted">${escapeHtml(ui('Brak wybranych terminów'))}</span>`;
    }

    const studentsWrap = root.querySelector('.js-lesson-confirm-students');
    if (studentsWrap) {
      const cards = [...root.querySelectorAll('.booking-lesson-participant')];
      studentsWrap.innerHTML = cards.length
        ? cards.map((card) => {
          const first = card.querySelector('[name^="student-first-name-"]')?.value?.trim() || '';
          const last = card.querySelector('[name^="student-last-name-"]')?.value?.trim() || '';
          const type = card.querySelector('[name^="student-type-"]')?.value || 'adult';
          const birth = card.querySelector('[name^="student-birth-"]')?.value?.trim() || '';
          const typeLabel = type === 'child' ? ui('dziecko') : ui('dorosły');
          const name = [first, last].filter(Boolean).join(' ') || '—';
          const birthHtml = birth
            ? `<p class="lesson-confirm-student__meta">${escapeHtml(ui('Data urodzenia:'))} ${escapeHtml(birth)}</p>`
            : '';
          return `<article class="lesson-confirm-student">
            <p class="lesson-confirm-student__name"><strong>${escapeHtml(name)}</strong> <span>(${typeLabel})</span></p>
            ${birthHtml}
          </article>`;
        }).join('')
        : `<p class="lesson-confirm-student__empty">${escapeHtml(ui('Brak danych kursantów'))}</p>`;
    }

    const detailsEl = root.querySelector('.js-lesson-confirm-orderer-details');
    if (detailsEl) {
      const rows = [
        [ui('Imię'), contact.first_name],
        [ui('Nazwisko'), contact.last_name],
        [ui('Telefon'), contact.phone],
        [ui('Email'), contact.email],
      ];
      detailsEl.innerHTML = rows.map(([label, value]) => (
        `<div class="lesson-confirm-orderer__row"><span>${label}:</span> <strong>${escapeHtml(value || '—')}</strong></div>`
      )).join('');
    }

    const notesEl = root.querySelector('.js-lesson-confirm-orderer-notes');
    if (notesEl) notesEl.textContent = contact.notes || '—';
  };

  const getBookingAsset = (file) => {
    const base = (root.dataset.bookingAssets || './src/img/booking').replace(/\/$/, '');
    return `${base}/${file}`;
  };

  const setAddonAddState = (btn, on) => {
    if (!btn) return;
    btn.classList.toggle('is-added', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = btn.querySelector('.travel-addon__add-label');
    if (label) label.textContent = on ? t('btn_added', 'Dodano') : t('btn_add', 'Dodaj');
    else btn.textContent = on ? t('btn_added', 'Dodano') : t('btn_add', 'Dodaj');
  };

  const setTravelTraining = (card, on) => {
    card.dataset.training = on ? '1' : '0';
    if (on) {
      card.dataset.discipline = normalizeTravelTrainingDiscipline(
        card.dataset.discipline || defaultTravelTrainingDiscipline(travelDisciplineFlags),
        travelDisciplineFlags,
      );
      if (!card.dataset.trainingRate) {
        card.dataset.trainingRate = 'standard';
      }
    } else {
      card.dataset.discipline = '';
      card.dataset.trainingRate = 'standard';
    }
    syncTravelAddonUi(card);
  };

  const nudgeModal = root.querySelector('.js-travel-nudge');
  const nudgeTitleEl = root.querySelector('.js-travel-nudge-title');
  const nudgeBodyEl = root.querySelector('.js-travel-nudge-body');
  const nudgeNoteEl = root.querySelector('.js-travel-nudge-note');
  let nudgeCard = null;
  let nudgeRestoreFocus = null;

  const formatNudgePrice = (value) => {
    const amount = Number(value) || 0;
    if (Number.isInteger(amount)) return `${amount} zł`;
    return formatPrice(amount);
  };

  const closeTrainingNudge = () => {
    if (!nudgeModal) return;
    nudgeModal.classList.remove('is-open');
    nudgeModal.hidden = true;
    document.documentElement.classList.remove('booking-nudge-open');
    nudgeCard = null;
    const focusEl = nudgeRestoreFocus;
    nudgeRestoreFocus = null;
    if (focusEl && typeof focusEl.focus === 'function') {
      requestAnimationFrame(() => focusEl.focus());
    }
  };

  const openTrainingNudge = (card) => {
    if (!nudgeModal || !card) return;
    const index = card.dataset.participantIndex || '1';
    const firstName = readFieldValue(card, `participant-first-name-${index}`) || '';
    const genitive = genitiveImie(firstName) || firstName;
    const price = formatNudgePrice(trainingPriceForCard(card));
    if (nudgeTitleEl) {
      nudgeTitleEl.textContent = t(
        'nudge_title',
        'Może jednak szkolenie dla {name_genitive}?',
        { name: firstName, name_genitive: genitive },
      );
    }
    if (nudgeBodyEl) {
      nudgeBodyEl.textContent = t(
        'nudge_body',
        'Stok to świetna okazja, żeby podszkolić technikę albo nauczyć się jazdy od zera. Z instruktorem HSKI {name} zrobi to bezpiecznie i szybciej złapie radość z jazdy.',
        { name: firstName, name_genitive: genitive },
      );
    }
    if (nudgeNoteEl) {
      const note = t(
        'nudge_note',
        'Szkolenie dla {name_genitive} — {price}.',
        { name_genitive: escapeHtml(genitive), price: `<strong>${escapeHtml(price)}</strong>` },
      );
      nudgeNoteEl.innerHTML = note;
    }
    const addBtn = root.querySelector('.js-travel-nudge-add');
    const skipBtn = root.querySelector('.js-travel-nudge-decline');
    if (addBtn) addBtn.textContent = t('nudge_add', 'Tak, dodaj szkolenie');
    if (skipBtn) skipBtn.textContent = t('nudge_skip', 'Nie, dziękuję');
    nudgeCard = card;
    nudgeRestoreFocus = document.activeElement;
    nudgeModal.hidden = false;
    document.documentElement.classList.add('booking-nudge-open');
    requestAnimationFrame(() => {
      nudgeModal.classList.add('is-open');
      root.querySelector('.js-travel-nudge-add')?.focus();
    });
  };

  const maybeOfferTrainingNudge = (card) => {
    if (!card || card.dataset.training === '1' || card.dataset.trainingAsked === '1') return;
    card.dataset.trainingAsked = '1';
    openTrainingNudge(card);
    schedulePersist();
  };

  nudgeModal?.querySelectorAll('.js-travel-nudge-skip').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      closeTrainingNudge();
    });
  });

  root.querySelector('.js-travel-nudge-add')?.addEventListener('click', (event) => {
    event.preventDefault();
    const card = nudgeCard;
    closeTrainingNudge();
    if (!card) return;
    if (!card.dataset.discipline) {
      card.dataset.discipline = defaultTravelTrainingDiscipline(travelDisciplineFlags);
    }
    setTravelTraining(card, true);
    if (card.dataset.saved === '1') renderTravelParticipantSummary(card);
    updatePrice();
    updateSummary();
    schedulePersist();
  });

  const cancelNudgeModal = root.querySelector('.js-travel-cancel-nudge');
  const cancelNudgeTitleEl = root.querySelector('.js-travel-cancel-nudge-title');
  const cancelNudgeBodyEl = root.querySelector('.js-travel-cancel-nudge-body');
  const cancelNudgeNoteEl = root.querySelector('.js-travel-cancel-nudge-note');
  let cancelNudgeRestoreFocus = null;
  let cancelNudgeContinue = null;

  const isCancellationDeclined = () => Boolean(
    root.querySelector('.js-booking-cancellation-decline')?.checked,
  );

  const needsCancellationNudge = () => (
    bookingType === 'travel'
    && !isBookingCancellationOn()
    && !isCancellationDeclined()
  );

  const closeCancelNudge = ({ continueFlow = false } = {}) => {
    if (!cancelNudgeModal) return;
    cancelNudgeModal.classList.remove('is-open');
    cancelNudgeModal.hidden = true;
    document.documentElement.classList.remove('booking-nudge-open');
    const onContinue = cancelNudgeContinue;
    cancelNudgeContinue = null;
    const focusEl = cancelNudgeRestoreFocus;
    cancelNudgeRestoreFocus = null;
    if (focusEl && typeof focusEl.focus === 'function') {
      requestAnimationFrame(() => focusEl.focus());
    }
    if (continueFlow && typeof onContinue === 'function') onContinue();
  };

  const openCancelNudge = (onContinue) => {
    if (!cancelNudgeModal) {
      if (typeof onContinue === 'function') onContinue();
      return;
    }
    const price = formatNudgePrice(cancellationPrice);
    if (cancelNudgeTitleEl) {
      cancelNudgeTitleEl.textContent = t(
        'cancel_nudge_title',
        'Ubezpieczenie od kosztów rezygnacji?',
      );
    }
    if (cancelNudgeBodyEl) {
      cancelNudgeBodyEl.textContent = t(
        'cancel_nudge_body',
        'Chroni przed utratą wpłaty przy odwołaniu wyjazdu z przyczyn losowych (choroba, wypadek, sytuacja rodzinna). Polisa obejmuje całą rezerwację.',
      );
    }
    if (cancelNudgeNoteEl) {
      const note = t(
        'cancel_nudge_note',
        'Ubezpieczenie dla całej rezerwacji — {price}.',
        { price: `<strong>${escapeHtml(price)}</strong>` },
      );
      cancelNudgeNoteEl.innerHTML = note;
    }
    const addBtn = root.querySelector('.js-travel-cancel-nudge-add');
    const skipBtn = root.querySelector('.js-travel-cancel-nudge-decline');
    if (addBtn) addBtn.textContent = t('cancel_nudge_add', 'Tak, dodaj ubezpieczenie');
    if (skipBtn) skipBtn.textContent = t('cancel_nudge_skip', 'Nie, dziękuję');
    cancelNudgeContinue = onContinue;
    cancelNudgeRestoreFocus = document.activeElement;
    cancelNudgeModal.hidden = false;
    document.documentElement.classList.add('booking-nudge-open');
    requestAnimationFrame(() => {
      cancelNudgeModal.classList.add('is-open');
      addBtn?.focus();
    });
  };

  const markCancellationDeclined = () => {
    const decline = root.querySelector('.js-booking-cancellation-decline');
    if (decline) decline.checked = true;
    setBookingCancellation(false);
  };

  cancelNudgeModal?.querySelectorAll('.js-travel-cancel-nudge-skip').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      const isDecline = el.classList.contains('js-travel-cancel-nudge-decline');
      if (isDecline) {
        markCancellationDeclined();
        updatePrice();
        updateSummary();
        schedulePersist();
        closeCancelNudge({ continueFlow: true });
        return;
      }
      // X / backdrop — zamknij bez przejścia dalej
      closeCancelNudge({ continueFlow: false });
    });
  });

  root.querySelector('.js-travel-cancel-nudge-add')?.addEventListener('click', (event) => {
    event.preventDefault();
    setBookingCancellation(true);
    updatePrice();
    updateSummary();
    schedulePersist();
    // Zostań na kroku uczestników — użytkownik sam przejdzie dalej (np. „Dalej”).
    closeCancelNudge({ continueFlow: false });
  });

  const tryAdvanceTravelFromParticipants = () => {
    if (!revealInvalidOnCurrentStep()) return;
    const goNext = () => goToStep(TRAVEL_ORDERER_STEP);
    if (needsCancellationNudge()) {
      openCancelNudge(goNext);
      return;
    }
    goNext();
  };

  const syncTravelAddonUi = (card) => {
    const trainingOn = card.dataset.training === '1';
    const discipline = card.dataset.discipline || '';
    card.querySelectorAll('.js-travel-discipline').forEach((btn) => {
      const active = trainingOn && btn.dataset.discipline === discipline;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      const radio = btn.querySelector('input[type="radio"]');
      if (radio) radio.checked = active;
    });
    const rate = normalizeTravelTrainingRate(card.dataset.trainingRate);
    card.querySelectorAll('.js-travel-training-rate').forEach((btn) => {
      const active = trainingOn && normalizeTravelTrainingRate(btn.dataset.rate) === rate;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    const rateBox = card.querySelector('.js-travel-training-rate-box');
    if (rateBox) rateBox.hidden = !trainingAlt.enabled;
    setAddonAddState(card.querySelector('.js-travel-training-toggle'), trainingOn);
    syncTravelTrainingPriceDisplay(card);
  };

  const setBookingCancellation = (on) => {
    const card = root.querySelector('.js-travel-cancel-card');
    const decline = root.querySelector('.js-booking-cancellation-decline');
    setAddonAddState(root.querySelector('.js-booking-cancellation-toggle'), on);
    card?.classList.toggle('is-added', on);
    if (on && decline) decline.checked = false;
  };

  const formatBirthDisplay = (value) => {
    if (!value) return '—';
    const polish = isoBirthToPolish(value);
    if (polish) return polish;
    return value;
  };

  const formatSavedBirthDisplay = (value) => {
    const iso = polishBirthToIso(value);
    if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [year, month, day] = iso.split('-');
      return `${day}-${month}-${year}`;
    }
    return formatBirthDisplay(value);
  };

  const formatAddonAmount = (value) => {
    const amount = Number(value) || 0;
    if (Number.isInteger(amount)) return `${amount} zł`;
    return formatPrice(amount);
  };

  const bindTravelBirthMask = (card) => {
    const input = card.querySelector('[name^="participant-birth-"]');
    if (!input) return null;
    destroyBirthDatePicker(card._birthMask);
    if (card._birthMask?.masked) destroyPolishDateMask(card._birthMask);
    const picker = attachBirthDatePicker(input, {
      maxDate: new Date(),
      onChange: () => {
        setTravelBirthFieldInvalid(card, false);
        syncTravelTrainingPriceDisplay(card);
        updatePrice();
        updateSummary();
        const ordererBox = card.querySelector('.js-travel-is-orderer');
        if (ordererBox?.checked && !canBeTravelOrderer(card)) {
          ordererBox.checked = false;
          setTravelBirthFieldInvalid(
            card,
            true,
            'Osoba zamawiająca musi mieć ukończone 18 lat.',
          );
        }
        if (card.dataset.saved !== '1') {
          refreshTravelParticipantStatus(card);
          schedulePersist();
        } else {
          refreshTravelParticipantStatus(card);
          if (card.dataset.training === '1') {
            renderTravelParticipantSummary(card);
          }
        }
        updateTravelCtas();
      },
    });
    card._birthMask = picker;
    input.addEventListener('blur', () => {
      const result = validateBirthDatePicker(picker, {
        required: true,
        maxDate: new Date(),
        fieldLabel: t('label_birth', 'Data urodzenia'),
      });
      setTravelBirthFieldInvalid(card, !result.valid, result.message || '');
    });
    return picker;
  };

  const setTravelBirthValue = (card, value) => {
    const polish = isoBirthToPolish(value);
    if (!polish) return;
    if (card._birthMask && 'value' in card._birthMask) {
      card._birthMask.value = polish;
      return;
    }
    const input = card.querySelector('[name^="participant-birth-"]');
    if (input) input.value = polish;
  };

  const renderTravelParticipantSummary = (card) => {
    const index = card.dataset.participantIndex || '1';
    const first = readFieldValue(card, `participant-first-name-${index}`);
    const last = readFieldValue(card, `participant-last-name-${index}`);
    const birth = readTravelParticipantBirthValue(card);
    const trainingOn = card.dataset.training === '1';
    const discipline = travelDisciplineLabel(card.dataset.discipline || '', t);
    const trainingAmt = trainingPriceForCard(card);
    const extras = trainingOn ? trainingAmt : 0;
    const rateIsAlt = trainingOn && normalizeTravelTrainingRate(card.dataset.trainingRate) === 'alt';
    const rateBit = rateIsAlt ? escapeHtml(trainingAlt.label) : '';

    const body = card.querySelector('.js-travel-summary-body');
    if (!body) return;
    const trainingValue = trainingOn
      ? (
        [discipline && escapeHtml(discipline), rateBit, `+${escapeHtml(formatAddonAmount(trainingAmt))}`]
          .filter(Boolean)
          .join(' · ')
      )
      : escapeHtml(t('summary_no', 'Nie'));
    const extrasRow = extras > 0
      ? `<div class="travel-participant__saved-total">${escapeHtml(t('summary_addons', 'Dodatki'))}: ${escapeHtml(formatAddonAmount(extras))}</div>`
      : '';
    body.innerHTML = `
      <div class="travel-participant__saved">
        <div class="travel-participant__kv">
          <span>${escapeHtml(t('summary_name', 'Imię i nazwisko'))}</span>
          <b>${escapeHtml(`${first} ${last}`.trim())}</b>
        </div>
        <div class="travel-participant__kv">
          <span>${escapeHtml(t('summary_birth', 'Data urodzenia'))}</span>
          <b>${escapeHtml(formatSavedBirthDisplay(birth))}</b>
        </div>
        <div class="travel-participant__kv">
          <span>${escapeHtml(t('summary_training', 'Szkolenie'))}</span>
          <b>${trainingValue}</b>
        </div>
        ${extrasRow}
      </div>
    `;
  };

  const saveTravelParticipantCard = (card, { silent = false } = {}) => {
    const index = card.dataset.participantIndex || '1';
    const first = card.querySelector(`[name="participant-first-name-${index}"]`);
    const last = card.querySelector(`[name="participant-last-name-${index}"]`);
    const birth = card.querySelector(`[name="participant-birth-${index}"]`);
    let ok = true;
    [first, last].forEach((field) => {
      const fieldOk = Boolean(field?.value?.trim());
      if (!silent) setBookingFieldInvalid(field, !fieldOk);
      if (!fieldOk) ok = false;
    });
    const birthResult = validateBirthDatePicker(card._birthMask || birth, {
      required: true,
      maxDate: new Date(),
      fieldLabel: ui('Data urodzenia'),
    });
    if (!silent) setTravelBirthFieldInvalid(card, !birthResult.valid, birthResult.message || '');
    if (!birthResult.valid) ok = false;
    if (!ok) {
      if (!silent) scrollToFirstInvalid(card);
      return false;
    }

    card.dataset.saved = '1';
    card.classList.add('is-saved');
    card.classList.remove('is-invalid');
    card.querySelector('.js-travel-save')?.classList.remove('is-attention');
    setTravelBirthFieldInvalid(card, false);
    card.querySelector('.js-travel-edit-body')?.setAttribute('hidden', '');
    const summary = card.querySelector('.js-travel-summary-body');
    if (summary) summary.removeAttribute('hidden');
    renderTravelParticipantSummary(card);
    refreshTravelParticipantStatus(card);
    updatePrice();
    updateSummary();
    schedulePersist();
    if (!silent) maybeOfferTrainingNudge(card);
    if (card.querySelector('.js-travel-is-orderer')?.checked) {
      if (!canBeTravelOrderer(card)) {
        const box = card.querySelector('.js-travel-is-orderer');
        if (box) box.checked = false;
        refreshTravelParticipantStatus(card);
        setTravelBirthFieldInvalid(
          card,
          true,
          'Osoba zamawiająca musi mieć ukończone 18 lat.',
        );
      } else {
        syncContactFromOrderer({ force: true });
      }
    }
    updateTravelCtas();
    return true;
  };

  const buildTravelParticipantFieldset = (index) => {
    const card = document.createElement('article');
    card.className = 'travel-participant booking-travel-participant';
    card.dataset.participantIndex = String(index);
    card.dataset.saved = '0';
    card.dataset.training = '0';
    card.dataset.trainingAsked = '0';
    card.dataset.trainingRate = 'standard';
    card.dataset.nnw = '0';
    card.dataset.discipline = '';
    card.innerHTML = `
      <header class="travel-participant__head">
        <div class="travel-participant__head-left">
          <span class="travel-participant__check-icon" aria-hidden="true"></span>
          <h3 class="travel-participant__title">${escapeHtml(t('participant_title', 'Uczestnik nr {index}', { index }))}</h3>
        </div>
        <div class="travel-participant__head-actions">
          <label class="travel-participant__orderer-pill js-travel-orderer-chip">
            <input type="checkbox" class="js-travel-is-orderer" name="participant-is-orderer-${index}" value="1">
            <span>${escapeHtml(t('orderer_checkbox', 'Osoba zamawiająca'))}</span>
          </label>
          <button type="button" class="travel-participant__edit-link js-travel-edit" hidden>✎ ${escapeHtml(t('btn_edit', 'Edytuj dane'))}</button>
          <span class="travel-participant__status js-travel-status">${escapeHtml(t('status_fill', '{index}/{total} - Wypełnij', { index, total: 1 }))}</span>
        </div>
      </header>
      <div class="travel-participant__body js-travel-edit-body">
        <div class="booking-form__row booking-form__row--3 travel-participant__fields">
          <label class="booking-form__field">
            <span class="booking-form__label">${escapeHtml(t('label_first_name', 'Imię'))} <span class="booking-form__req">*</span></span>
            <input type="text" name="participant-first-name-${index}" placeholder="${escapeHtml(t('ph_first_name', 'Imię'))}" required autocomplete="given-name">
          </label>
          <label class="booking-form__field">
            <span class="booking-form__label">${escapeHtml(t('label_last_name', 'Nazwisko'))} <span class="booking-form__req">*</span></span>
            <input type="text" name="participant-last-name-${index}" placeholder="${escapeHtml(t('ph_last_name', 'Nazwisko'))}" required autocomplete="family-name">
          </label>
          <label class="booking-form__field">
            <span class="booking-form__label">${escapeHtml(t('label_birth', 'Data urodzenia'))} <span class="booking-form__req">*</span></span>
            <span class="booking-form__control booking-form__control--date">
              <input
                type="text"
                name="participant-birth-${index}"
                class="js-travel-birth"
                inputmode="numeric"
                placeholder="${escapeHtml(t('ph_birth', 'DD.MM.RRRR'))}"
                autocomplete="bday"
                required
              >
              <img class="booking-form__control-icon js-birth-datepicker-icon" src="${escapeHtml(getBookingAsset('icon-calendar.svg'))}" alt="" width="24" height="24">
            </span>
            <span class="travel-participant__birth-error js-travel-birth-error" role="alert"></span>
          </label>
        </div>
        <div class="travel-participant__addons">
          <p class="travel-participant__addons-title">${escapeHtml(t('addons_title', 'Dodatkowe opcje'))} <span>${escapeHtml(t('addons_optional', '(opcjonalnie)'))}</span></p>
          <div class="travel-participant__addon-grid">
            <div class="travel-addon js-travel-training-box">
              <div class="travel-addon__heading">
                <h4>${escapeHtml(t('training_card_title', 'Szkolenie narciarskie'))}</h4>
                <p>${escapeHtml(t('training_card_desc', 'Wybierz dyscyplinę, jeśli osoba chce wziąć udział w szkoleniu.'))}</p>
              </div>
              ${travelShowDisciplineUi ? `<div class="travel-addon__discipline" role="radiogroup" aria-label="${escapeHtml(t('training_card_title', 'Szkolenie narciarskie'))}">
                ${travelShowSki ? `<button type="button" class="travel-addon__discipline-btn js-travel-discipline" data-discipline="ski" aria-pressed="false">${escapeHtml(t('discipline_ski', 'Narty'))}</button>` : ''}
                ${travelShowSnowboard ? `<button type="button" class="travel-addon__discipline-btn js-travel-discipline" data-discipline="snowboard" aria-pressed="false">${escapeHtml(t('discipline_snowboard', 'Snowboard'))}</button>` : ''}
              </div>` : ''}
              ${trainingAlt.enabled ? `<div class="travel-addon__rates js-travel-training-rate-box" role="radiogroup" aria-label="${escapeHtml(t('training_rate_title', 'Wariant szkolenia'))}">
                <button type="button" class="travel-addon__rate-btn js-travel-training-rate is-active" data-rate="standard" aria-pressed="true">${escapeHtml(t('training_rate_standard', 'Standard'))}</button>
                <button type="button" class="travel-addon__rate-btn js-travel-training-rate" data-rate="alt" aria-pressed="false">${escapeHtml(trainingAlt.label)}</button>
              </div>` : ''}
              <div class="travel-addon__footer">
                <p class="travel-addon__price"><strong class="js-travel-training-price">${Number.isInteger(trainingPrice) ? `${trainingPrice} zł` : formatPrice(trainingPrice)}</strong> <span>${escapeHtml(t('price_per_person', '/ osoba'))}</span></p>
                <button type="button" class="travel-addon__add js-travel-training-toggle" aria-pressed="false">
                  <span class="travel-addon__add-label">${escapeHtml(t('btn_add', 'Dodaj'))}</span>
                  <span class="travel-addon__add-check" aria-hidden="true"></span>
                </button>
              </div>
              <button type="button" class="travel-addon__details" data-addon-details="training">${escapeHtml(t('btn_details', 'Zobacz szczegóły'))}</button>
            </div>
          </div>
        </div>
        <label class="booking-form__field travel-participant__notes">
          <span class="booking-form__label">${escapeHtml(t('notes_label', 'Uwagi'))} <span class="booking-form__optional">${escapeHtml(t('notes_optional', '(opcjonalnie)'))}</span></span>
          <input type="text" name="participant-notes-${index}" placeholder="${escapeHtml(t('notes_placeholder', 'Alergie, ograniczenia ruchowe, wymagania dietetyczne...'))}">
        </label>
        <button type="button" class="travel-participant__save js-travel-save js-travel-save-participant">${escapeHtml(t('btn_save', 'Zapisz dane'))}</button>
      </div>
      <div class="travel-participant__summary js-travel-summary-body" hidden></div>
    `;

    card.addEventListener('input', (event) => {
      const target = event?.target;
      if (target?.classList?.contains('js-travel-birth') || target?.name?.startsWith?.('participant-birth-')) {
        syncTravelTrainingPriceDisplay(card);
        updatePrice();
        updateSummary();
      }
      if (card.dataset.saved === '1') return;
      refreshTravelParticipantStatus(card);
      schedulePersist();
      updateTravelCtas();
    });

    bindTravelBirthMask(card);
    syncTravelTrainingPriceDisplay(card);

    card.querySelector('.js-travel-is-orderer')?.addEventListener('change', (event) => {
      if (event.target.checked) {
        if (!canBeTravelOrderer(card)) {
          event.target.checked = false;
          refreshTravelParticipantStatus(card);
          setTravelBirthFieldInvalid(
            card,
            true,
            'Osoba zamawiająca musi mieć ukończone 18 lat.',
          );
          expandTravelParticipantCard(card);
          scrollToFirstInvalid(card);
          schedulePersist();
          updateTravelCtas();
          return;
        }
        setTravelBirthFieldInvalid(card, false);
        setExclusiveTravelOrderer(card);
        syncContactFromOrderer({ force: true });
      } else {
        refreshTravelParticipantStatus(card);
      }
      schedulePersist();
      updateTravelCtas();
    });

    card.querySelectorAll('.js-travel-discipline').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const discipline = btn.dataset.discipline || defaultTravelTrainingDiscipline(travelDisciplineFlags);
        const isActive = card.dataset.training === '1' && card.dataset.discipline === discipline;
        if (isActive) {
          setTravelTraining(card, false);
        } else {
          card.dataset.discipline = discipline;
          setTravelTraining(card, true);
        }
        if (card.dataset.saved === '1') {
          renderTravelParticipantSummary(card);
        }
        updatePrice();
        updateSummary();
        schedulePersist();
      });
    });

    card.querySelectorAll('.js-travel-training-rate').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        card.dataset.trainingRate = normalizeTravelTrainingRate(btn.dataset.rate);
        if (card.dataset.training !== '1') {
          setTravelTraining(card, true);
        } else {
          syncTravelAddonUi(card);
        }
        if (card.dataset.saved === '1') {
          renderTravelParticipantSummary(card);
        }
        updatePrice();
        updateSummary();
        schedulePersist();
      });
    });

    card.querySelector('.js-travel-training-toggle')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setTravelTraining(card, card.dataset.training !== '1');
      if (card.dataset.saved === '1') {
        renderTravelParticipantSummary(card);
      }
      updatePrice();
      updateSummary();
      schedulePersist();
    });

    card.querySelector('.js-travel-save')?.addEventListener('click', () => {
      saveTravelParticipantCard(card);
    });

    card.addEventListener('click', (event) => {
      if (!event.target.closest('.js-travel-edit')) return;
      expandTravelParticipantCard(card);
      schedulePersist();
      updateTravelCtas();
    });

    return card;
  };

  const syncTravelParticipantForms = () => {
    const form = root.querySelector('.js-booking-participants-form');
    if (!form || bookingType !== 'travel') return;

    const { adults, children } = getCounts();
    const target = Math.max(1, adults + children);
    let current = form.querySelectorAll('.booking-travel-participant').length;

    while (current < target) {
      current += 1;
      form.appendChild(buildTravelParticipantFieldset(current));
    }

    while (current > target) {
      form.lastElementChild?.remove();
      current -= 1;
    }

    const { adults: adultSlots } = getCounts();
    [...form.querySelectorAll('.booking-travel-participant')].forEach((card, index) => {
      card.dataset.guestRole = index < adultSlots ? 'adult' : 'child';
    });

    applyOrdererToFirstParticipant();
    updatePrice();
  };

  const setExclusiveTravelOrderer = (ordererCard) => {
    [...root.querySelectorAll('.booking-travel-participant')].forEach((card) => {
      const box = card.querySelector('.js-travel-is-orderer');
      if (box) box.checked = card === ordererCard;
      refreshTravelParticipantStatus(card);
    });
  };

  const applyOrdererToFirstParticipant = () => {
    if (bookingType !== 'travel') return;
    [...root.querySelectorAll('.booking-travel-participant')].forEach((card) => {
      refreshTravelParticipantStatus(card);
    });
  };

  const copyOrdererToFirstParticipant = () => {
    if (bookingType !== 'travel') return;
    const contactStep = getContactStep(root, bookingType);
    if (!contactStep?.querySelector('[name="is-participant"]')?.checked) return;

    const first = root.querySelector('.booking-travel-participant');
    if (!first) return;

    const index = first.dataset.participantIndex || '1';
    const firstName = readFieldValue(contactStep, 'first-name');
    const lastName = readFieldValue(contactStep, 'last-name');
    const birth = readFieldValue(contactStep, 'birth-date');
    if (firstName) setNamedField(first, `participant-first-name-${index}`, firstName);
    if (lastName) setNamedField(first, `participant-last-name-${index}`, lastName);
    if (birth) setTravelBirthValue(first, polishBirthToIso(birth));

    // Checkbox „jestem uczestnikiem” → tylko pierwsza karta jest zamawiającą.
    setExclusiveTravelOrderer(first);
  };

  const getOrdererAdultMaxDate = () => {
    const max = new Date();
    max.setHours(0, 0, 0, 0);
    max.setFullYear(max.getFullYear() - ORDERER_MIN_AGE);
    return max;
  };

  const bindTravelOrdererBirth = () => {
    const contactStep = getContactStep(root, bookingType);
    const input = root.querySelector('.js-travel-orderer-birth');
    if (!input) return;
    destroyBirthDatePicker(input);
    const syncValidity = () => {
      const hasValue = Boolean(input.value?.trim());
      const result = validateOrdererBirthDate(input._birthDatePicker || input, {
        required: hasValue,
      });
      const showError = hasValue && !result.valid;
      setBookingFieldInvalid(input, showError);
      setOrdererBirthError(
        contactStep || input.closest('.booking-page__panel') || root,
        showError ? (result.message || ORDERER_AGE_MESSAGE()) : '',
      );
      updateTravelCtas();
    };
    attachBirthDatePicker(input, {
      maxDate: getOrdererAdultMaxDate(),
      placeholder: 'DD.MM.RRRR',
      onChange: () => {
        syncValidity();
      },
    });
    input.addEventListener('blur', syncValidity);
    input.addEventListener('input', () => {
      if (!input.value?.trim()) {
        setBookingFieldInvalid(input, false);
        setOrdererBirthError(contactStep || root, '');
      }
      updateTravelCtas();
    });
    const current = isoBirthToPolish(input.value) || input.value;
    if (current && input._birthDatePicker) {
      input._birthDatePicker.value = current;
    }
    syncValidity();
  };

  /** Prefill / sync contact form from the participant marked as orderer. */
  const syncContactFromOrderer = ({ force = false } = {}) => {
    if (bookingType !== 'travel') return;
    const contactStep = getContactStep(root, bookingType);
    if (!contactStep) return;

    const ordererCard = [...root.querySelectorAll('.booking-travel-participant')]
      .find((card) => card.querySelector('.js-travel-is-orderer')?.checked);
    if (!ordererCard) return;

    // Osoba < 18 nie może być zamawiającą — odznacz i nie kopiuj daty.
    if (!canBeTravelOrderer(ordererCard)) {
      const box = ordererCard.querySelector('.js-travel-is-orderer');
      if (box) box.checked = false;
      refreshTravelParticipantStatus(ordererCard);
      setOrdererBirthError(contactStep, '');
      if (force) updateTravelCtas();
      return;
    }

    const index = ordererCard.dataset.participantIndex || '1';
    const first = readFieldValue(ordererCard, `participant-first-name-${index}`);
    const last = readFieldValue(ordererCard, `participant-last-name-${index}`);
    const birthRaw = readFieldValue(ordererCard, `participant-birth-${index}`);
    const birthPolish = isoBirthToPolish(birthRaw) || birthRaw;

    const firstNameEl = contactStep.querySelector('[name="first-name"]');
    const lastNameEl = contactStep.querySelector('[name="last-name"]');
    const birthEl = contactStep.querySelector('[name="birth-date"]');

    if (first && (force || !firstNameEl?.value?.trim())) {
      setNamedField(contactStep, 'first-name', first);
    }
    if (last && (force || !lastNameEl?.value?.trim())) {
      setNamedField(contactStep, 'last-name', last);
    }
    if (birthPolish && isAdultBirthDate(birthPolish) && (force || !birthEl?.value?.trim())) {
      if (birthEl?._birthDatePicker) {
        birthEl._birthDatePicker.value = birthPolish;
      } else if (birthEl) {
        birthEl.value = birthPolish;
      }
      setBookingFieldInvalid(birthEl, false);
      setOrdererBirthError(contactStep, '');
    }

    if (force) updateTravelCtas();
  };

  const prefillContactFromOrderer = () => syncContactFromOrderer({ force: false });

  const syncTravelSidebarCta = (step = currentStep) => {
    const labelsNext = t('cta_next', 'Przejdź dalej');
    const labelsSubmit = t('cta_submit', 'Zamawiam');
    queryBookingAll('.js-booking-sidebar-submit').forEach((btn) => {
      const next = btn.dataset.labelNext || labelsNext;
      const submit = btn.dataset.labelSubmit || labelsSubmit;
      btn.textContent = step >= TRAVEL_REVIEW_STEP ? submit : next;
    });
    updateTravelCtas();
    stickySummary.sync();
  };

  const syncTravelSuccessPageExtras = (bookingRoot, isSuccess) => {
    const host = bookingRoot.closest('.wp-block-hski-booking-travel-form') || bookingRoot;
    const keep = new Set([
      'HEADER', 'FOOTER', 'SCRIPT', 'STYLE', 'NOSCRIPT', 'LINK',
    ]);
    const keepSelector = [
      '.header',
      '.footer',
      '.site-header',
      '.site-footer',
      '#wpadminbar',
      '.skip-link',
    ].join(',');

    const hideNode = (el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el === host || el.contains(bookingRoot) || bookingRoot.contains(el)) return;
      if (keep.has(el.tagName)) return;
      if (el.matches(keepSelector) || el.querySelector(keepSelector)) return;
      el.classList.toggle('is-booking-success-hidden', isSuccess);
    };

    const parent = host.parentElement;
    if (parent) {
      [...parent.children].forEach(hideNode);
    }
  };

  const applyTravelUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const adults = params.get('adults');
    const children = params.get('children') ?? params.get('kids');

    if (adults && adultsSelect) adultsSelect.value = adults;
    if (children != null && children !== '' && childrenSelect) childrenSelect.value = children;

    const adultsVisible = root.querySelector('.js-booking-adults-visible');
    const childrenVisible = root.querySelector('.js-booking-children-visible');
    if (adults && adultsVisible) adultsVisible.value = adults;
    if (children != null && children !== '' && childrenVisible) childrenVisible.value = children;

    const ages = readChildrenAgesFromParams(params);
    if (ages.length) {
      root.dataset.childrenAges = JSON.stringify(ages);
    }

    syncTravelParticipantForms();
    updateSummary();
  };

  const applyLessonUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const lessonType = params.get('lesson_type') || root.dataset.lessonType || 'single';
    const participants = params.get('participants') || root.dataset.lessonParticipants || '1';
    const hours = params.get('hours') || root.dataset.lessonHours || '1';
    const discipline = params.get('discipline') || root.dataset.lessonDiscipline || 'ski';
    const location = params.get('location') || root.dataset.lessonLocation || 'harenda';
    const audience = params.get('audience') || root.dataset.lessonAudience || '';

    root.dataset.lessonType = lessonType;
    root.dataset.lessonParticipants = participants;
    root.dataset.lessonHours = hours;
    root.dataset.lessonDiscipline = discipline;
    root.dataset.lessonLocation = location;
    if (audience) root.dataset.lessonAudience = audience;

    if (lessonType) {
      const radio = root.querySelector(`.js-booking-lesson-type[value="${CSS.escape(lessonType)}"]`);
      if (radio?.type === 'radio') radio.checked = true;
      const typeHidden = root.querySelector('input.js-booking-lesson-type[type="hidden"]');
      if (typeHidden) typeHidden.value = lessonType;
    }
    const participantsSelect = root.querySelector('.js-booking-lesson-participants');
    if (participantsSelect) participantsSelect.value = participants;

    const disciplineSelect = root.querySelector('[name="discipline"]');
    if (disciplineSelect) disciplineSelect.value = discipline;

    const locationSelect = root.querySelector('[name="location"]');
    if (locationSelect) locationSelect.value = location;

    rebuildLessonHoursOptions();
    const hoursSelect = root.querySelector('.js-booking-lesson-hours');
    if (hoursSelect) hoursSelect.value = hours;

    syncLessonParticipantForms();
    syncLessonScheduleVisibility(root);
    updateLessonSummary();
    updateLessonOrdererButton();
  };

  const prefillLessonOrdererFromStudent = () => {
    if (bookingType !== 'lesson') return;
    const ordererCard = [...root.querySelectorAll('.booking-lesson-participant')]
      .find((card) => card.querySelector('.js-lesson-student-orderer')?.checked);
    if (!ordererCard) return;
    const contactStep = getContactStep(root, 'lesson');
    if (!contactStep) return;
    const first = ordererCard.querySelector('[name^="student-first-name-"]')?.value?.trim() || '';
    const last = ordererCard.querySelector('[name^="student-last-name-"]')?.value?.trim() || '';
    const firstInput = contactStep.querySelector('[name="first-name"]');
    const lastInput = contactStep.querySelector('[name="last-name"]');
    if (firstInput && !firstInput.value?.trim() && first) firstInput.value = first;
    if (lastInput && !lastInput.value?.trim() && last) lastInput.value = last;
  };

  const updateLessonOrdererButton = () => {
    if (bookingType !== 'lesson') return;
    const panel = root.querySelector(`[data-booking-step="${LESSON_ORDERER_STEP}"]`);
    const nextBtn = panel?.querySelector('.js-booking-next');
    if (!panel || !nextBtn) return;
    nextBtn.disabled = false;
    const canProceed = validateStep(root, LESSON_ORDERER_STEP, bookingType, { markInvalid: false });
    setBookingCtaMuted(nextBtn, !canProceed);
  };

  const populateTravelSuccess = async () => {
    if (isAdvancedPricing()) {
      try {
        const quote = await fetchTravelQuote();
        if (quote) applyTravelQuote(quote);
      } catch {
        /* keep lastTravelQuote */
      }
    }

    const setText = (selector, value) => {
      const el = root.querySelector(selector);
      if (el) el.textContent = value || '—';
    };

    const resort = root.dataset.resort || '';
    const hotel = root.dataset.hotel || '';
    const { adults, children } = getCounts();
    const guestsLabel = formatTravelGuestsLabel(adults, children);
    const place = root.querySelector('.js-booking-sidebar-place')?.textContent?.trim()
      || root.querySelector('.js-travel-success-place')?.textContent?.trim()
      || '';
    const termLabel = root.querySelector('.js-booking-sidebar-term')?.textContent?.trim()
      || root.dataset.termLabel
      || '';
    const room = root.querySelector('.js-booking-sidebar-room')?.textContent?.trim() || '';

    setText('.js-travel-success-resort', resort);
    setText('.js-travel-success-hotel', hotel);
    if (place) setText('.js-travel-success-place', place);
    if (termLabel) {
      setText('.js-travel-success-term', termLabel.replace(/\s*—\s*/g, ' - '));
    }
    setText('.js-travel-success-guests', guestsLabel);
    const roomRow = root.querySelector('.js-travel-success-room-row');
    if (roomRow) {
      if (room) {
        setText('.js-travel-success-room', room);
        roomRow.hidden = false;
      } else {
        roomRow.hidden = true;
      }
    }

    const quoteRows = travelQuoteCostRows(lastTravelQuote);
    const costsList = root.querySelector('.js-travel-success-costs');
    if (costsList && quoteRows.length) {
      costsList.innerHTML = quoteRows.map((row) => (
        `<li><span>${escapeHtml(row.label)}</span><span>${escapeHtml(row.text)}</span></li>`
      )).join('');
    } else {
      const { trainingCount, cancellationCount } = getTravelAddonCounts();
      const accommodation = (Number(root.dataset.basePrice) || 0) * (adults + children);
      const costs = [{ label: t('success_stay_label', 'Zakwaterowanie'), amount: accommodation }];
      if (cancellationCount) {
        costs.push({
          label: ui('Ubezpieczenie od rezygnacji'),
          amount: cancellationCount * cancellationPrice,
        });
      }
      if (trainingCount) {
        costs.push({
          label: trainingCount > 1 ? `${trainingCount}x Szkolenie narciarskie` : 'Szkolenie narciarskie',
          amount: sumTravelTrainingAmount(),
        });
      }
      if (costsList) {
        costsList.innerHTML = costs.map((row) => (
          `<li><span>${escapeHtml(row.label)}</span><span>${formatPrice(row.amount)}</span></li>`
        )).join('');
      }
    }
    setText(
      '.js-travel-success-total',
      formatQuoteTotal(lastTravelQuote) || formatPrice(calcTravelTotal()),
    );

    const contact = collectContact(root, bookingType);
    const ordererFullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    const ordererLine = root.querySelector('.js-travel-success-orderer-line');
    if (ordererLine) {
      ordererLine.textContent = ordererFullName && contact.email
        ? `${ordererFullName} · ${contact.email}`
        : ordererFullName || contact.email || '—';
    }
    setText('.js-travel-success-first-name', contact.first_name);
    setText('.js-travel-success-last-name', contact.last_name);
    setText('.js-travel-success-phone', formatPhoneWithPrefix(contact.phone, contact.phone_prefix));
    setText('.js-travel-success-email', contact.email);
    setText('.js-travel-success-birth', formatBirthDisplay(contact.birth_date));

    const participantsWrap = root.querySelector('.travel-success__participants-wrap');
    const participantsList = root.querySelector('.js-travel-success-participants');
    const participantsLine = root.querySelector('.js-travel-success-participants-line');
    if (participantsList) {
      const cards = [...root.querySelectorAll('.booking-travel-participant')];
      const names = [];
      participantsList.innerHTML = cards.map((card, idx) => {
        const index = card.dataset.participantIndex || String(idx + 1);
        const first = readFieldValue(card, `participant-first-name-${index}`) || '—';
        const last = readFieldValue(card, `participant-last-name-${index}`) || '—';
        const full = `${first} ${last}`.trim();
        if (full && full !== '—') names.push(full);
        const birth = formatSavedBirthDisplay(readFieldValue(card, `participant-birth-${index}`)) || '—';
        const notes = readFieldValue(card, `participant-notes-${index}`) || 'Brak';
        const trainingOn = card.dataset.training === '1';
        const discipline = travelDisciplineLabel(card.dataset.discipline || '', t);
        const rateIsAlt = trainingOn && normalizeTravelTrainingRate(card.dataset.trainingRate) === 'alt';
        let trainingLabel = `${t('summary_training', 'Szkolenie')}: ${t('summary_no', 'Nie')}`;
        if (trainingOn) {
          const parts = [];
          if (discipline) parts.push(discipline);
          if (rateIsAlt) parts.push(trainingAlt.label);
          trainingLabel = parts.length
            ? `${t('summary_training', 'Szkolenie')}: ${parts.join(' · ')}`
            : t('summary_training', 'Szkolenie');
        }
        const isOrderer = Boolean(card.querySelector('.js-travel-is-orderer')?.checked);
        const title = isOrderer
          ? `${t('participant_title', 'Uczestnik nr {index}', { index })} · ${t('orderer_checkbox', 'Osoba zamawiająca')}`
          : t('participant_title', 'Uczestnik nr {index}', { index });

        return `
          <article class="travel-review-participant">
            <div class="travel-review-participant__head">
              <h3 class="travel-review-participant__title">${escapeHtml(title)}</h3>
              <div class="travel-review-participant__subtitle">
                <span>${escapeHtml(full)}</span>
                ${trainingOn && discipline ? `<span class="course-review-student__discipline">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 19 19 5M6 22 22 6M4 6l13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                  ${escapeHtml(discipline)}
                </span>` : ''}
              </div>
            </div>
            <dl class="lesson-review-orderer__details travel-review-participant__details">
              <div><dt>${escapeHtml(t('label_first_name', 'Imię'))}:</dt><dd>${escapeHtml(first)}</dd></div>
              <div><dt>${escapeHtml(t('label_last_name', 'Nazwisko'))}:</dt><dd>${escapeHtml(last)}</dd></div>
              <div><dt>${escapeHtml(t('review_birth', 'Data urodzenia'))}:</dt><dd>${escapeHtml(birth)}</dd></div>
              <div><dt>${escapeHtml(t('summary_addons', 'Dodatki'))}:</dt><dd>${escapeHtml(trainingLabel)}</dd></div>
              <div><dt>${escapeHtml(t('notes_label', 'Uwagi'))}:</dt><dd>${escapeHtml(notes)}</dd></div>
            </dl>
          </article>
        `;
      }).join('');

      if (participantsLine) {
        participantsLine.textContent = names.length ? names.join(' · ') : '—';
      }
      if (participantsWrap) {
        participantsWrap.hidden = cards.length === 0;
      }
    }

    const participantNotes = [...root.querySelectorAll('.booking-travel-participant')]
      .map((card, index) => readFieldValue(card, `participant-notes-${index + 1}`))
      .filter(Boolean);
    const notes = contact.notes || participantNotes.join('\n') || '—';
    setText('.js-travel-success-notes', notes);
  };

  const updateSummary = () => {
    if (bookingType === 'travel') {
      const { adults, children } = getCounts();
      const guestsLabel = formatGuestsLabel(adults, children);
      if (summaryGuests) summaryGuests.textContent = guestsLabel;
      if (sidebarGuests) sidebarGuests.textContent = guestsLabel;

      const extras = [];
      const { trainingCount, cancellationCount } = getTravelAddonCounts();
      if (trainingCount) extras.push(trainingCount > 1 ? `Szkolenie ×${trainingCount}` : 'Szkolenie');
      if (cancellationCount) extras.push('Ubezp. od rezygnacji');
      if (summaryExtras) summaryExtras.textContent = extras.length ? extras.join(', ') : 'Brak';
    }

    if (bookingType === 'course') {
      const names = [...root.querySelectorAll('.booking-course-participant')].map((card) => {
        const index = card.dataset.studentIndex || '1';
        const first = card.querySelector(`[name="student-first-name-${index}"]`)?.value?.trim();
        const last = card.querySelector(`[name="student-last-name-${index}"]`)?.value?.trim();
        return first && last ? `${first} ${last}` : null;
      }).filter(Boolean);
      if (summaryGuests) summaryGuests.textContent = names.length ? names.join(', ') : '—';
      updateCourseSummary();
    }

    if (bookingType === 'lesson') {
      const names = [...root.querySelectorAll('.booking-lesson-participant')].map((card) => {
        const first = card.querySelector('[name^="student-first-name-"]')?.value?.trim();
        const last = card.querySelector('[name^="student-last-name-"]')?.value?.trim();
        return first && last ? `${first} ${last}` : null;
      }).filter(Boolean);
      if (summaryGuests) summaryGuests.textContent = names.length ? names.join(', ') : '—';
      updateLessonSummary();
    }

    const contact = collectContact(root, bookingType);
    const contactName = contact.first_name && contact.last_name
      ? `${contact.first_name} ${contact.last_name}`
      : '—';
    const contactAddress = [contact.address, contact.postcode, contact.city].filter(Boolean).join(', ');

    if (summaryContact) {
      summaryContact.textContent = contactName;
    }
    if (summaryEmail) summaryEmail.textContent = contact.email || '—';
    if (summaryPhone) summaryPhone.textContent = contact.phone || '—';
    if (summaryAddress) summaryAddress.textContent = contactAddress || '—';
  };

  const lockCompletedBookingHistory = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('step');
    history.replaceState({ bookingComplete: true }, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const goToStep = (step, { historyMode = 'push' } = {}) => {
    step = Math.min(Math.max(1, Number(step) || 1), maxStep);
    if (bookingFinished && step < maxStep) return;
    currentStep = step;
    panels.forEach((panel) => {
      const n = Number(panel.dataset.bookingStep);
      const active = n === step;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
    const progressMax = stepItems.length
      ? Math.max(...stepItems.map((stepItem) => Number(stepItem.dataset.step)))
      : 0;
    const progressOffset = bookingType === 'travel'
      ? Number(root.dataset.progressOffset ?? 0)
      : 0;
    // Travel success (last panel) = progress "Podsumowanie" active, not past the bar.
    let progressStep = step + progressOffset;
    if (bookingType === 'travel' && step >= maxStep && progressMax > 0) {
      progressStep = progressMax;
    }
    stepItems.forEach((item) => {
      const n = Number(item.dataset.step);
      if (progressStep > progressMax) {
        item.classList.remove('is-active');
        item.classList.add('is-done');
        return;
      }
      item.classList.toggle('is-active', n === progressStep);
      item.classList.toggle('is-done', n < progressStep);
    });
    root.querySelectorAll('.booking-progress__connector').forEach((line, index) => {
      line.classList.toggle('is-done', progressStep > progressMax || index < progressStep - 1);
    });

    const statusFraction = root.querySelector('.js-booking-progress-fraction');
    const statusName = root.querySelector('.js-booking-progress-name');
    const currentLabel = root.querySelector('.js-booking-progress-current-label');
    if (statusFraction || statusName || currentLabel) {
      const raw = root.dataset.progressLabels || '';
      let labels = [];
      if (raw.startsWith('[')) {
        try {
          labels = JSON.parse(raw);
        } catch {
          labels = [];
        }
      } else if (raw) {
        labels = raw.split('|').map((part) => part.trim()).filter(Boolean);
      }
      const labelIndex = Math.min(progressStep, progressMax) - 1;
      const label = labels[labelIndex] || '';
      if (statusFraction) {
        statusFraction.textContent = `${Math.min(progressStep, progressMax)}/${progressMax || labels.length || 5}`;
      }
      if (statusName) {
        statusName.textContent = label ? `— ${label}` : '';
      }
      if (currentLabel) {
        currentLabel.textContent = label;
      }
    }

    if (step === 3 && bookingType === 'lesson') {
      updateLessonReview();
    }
    if (step === 3 && bookingType === 'course') {
      updateCourseReview();
    }
    if (step === TRAVEL_REVIEW_STEP && bookingType === 'travel') {
      updateTravelReview();
    }
    if (step === maxStep - 1 && bookingType !== 'lesson' && bookingType !== 'course' && bookingType !== 'travel') {
      updateSummary();
    }
    if (step === maxStep && bookingType === 'travel' && !isTravelThankYouView(root)) {
      populateTravelSuccess();
    }

    if (step === TRAVEL_PARTICIPANTS_STEP && bookingType === 'travel') {
      syncTravelParticipantForms();
      applyOrdererToFirstParticipant();
      updateSummary();
    }
    if (step === TRAVEL_ORDERER_STEP && bookingType === 'travel') {
      prefillContactFromOrderer();
      bindTravelOrdererBirth();
    }
    if (bookingType === 'travel') {
      syncTravelSidebarCta(step);
    }

    const sidebarHint = root.querySelector('.booking-sidebar__hint');
    if (sidebarHint && bookingType === 'lesson') {
      // Hint o „następnym kroku — Dane kursanta” był pod starym ordererem-first; kursanci są teraz pierwszym krokiem.
      sidebarHint.hidden = true;
    }

    const courseSidebar = root.querySelector('.booking-sidebar--course');
    if (courseSidebar && bookingType === 'course') {
      courseSidebar.classList.toggle('is-student-step', step === COURSE_STUDENTS_STEP);
      updateCourseSummary();
    }

    const sidebarSubmit = queryBookingAll('.js-booking-sidebar-submit')[0]
      || root.querySelector('.js-booking-sidebar-submit');
    if (sidebarSubmit && bookingType === 'lesson') {
      sidebarSubmit.hidden = step !== 3;
    }
    if (sidebarSubmit && bookingType === 'course') {
      sidebarSubmit.hidden = step !== 3;
    }
    if (sidebarSubmit && bookingType === 'travel') {
      sidebarSubmit.hidden = step >= maxStep || step < TRAVEL_PARTICIPANTS_STEP;
    }

    const sidebar = sidebarMount || root.querySelector('.booking-page__sidebar');
    const layout = root.querySelector('.booking-page__layout--lesson');
    const isConfirm = (bookingType === 'lesson' || bookingType === 'course') && step === maxStep;

    if (sidebar && bookingType === 'travel') {
      sidebar.hidden = step >= maxStep;
    }

    if (bookingType === 'travel') {
      root.classList.toggle('booking-page--travel-success', step >= maxStep);
      const usp = root.querySelector('.js-travel-success-usp');
      if (usp) usp.hidden = step < maxStep;
      syncTravelSuccessPageExtras(root, step >= maxStep);
      if (step >= maxStep) stickySummary.closeDrawer({ immediate: true });
      stickySummary.sync();
      syncInlineSummarySlot();
      const drawer = root.querySelector('.js-travel-addon-drawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        drawer.hidden = true;
      }
      document.documentElement.classList.remove('booking-addon-drawer-open');
    }

    root.classList.toggle('booking-page--lesson-confirm', isConfirm);
    layout?.classList.toggle('is-confirmation', isConfirm);

    if (sidebar && (bookingType === 'lesson' || bookingType === 'course')) {
      sidebar.hidden = isConfirm;
    }
    if (bookingType === 'lesson' || bookingType === 'course') {
      if (isConfirm) stickySummary.closeDrawer({ immediate: true });
      stickySummary.sync();
      syncInlineSummarySlot();
    }

    if (isConfirm) {
      updateLessonConfirmation();
    }

    if (step === LESSON_STUDENTS_STEP && bookingType === 'lesson') {
      const wrap = root.querySelector('.js-booking-lesson-participants-wrap');
      if (!wrap?.querySelector('.booking-lesson-participant')) {
        syncLessonParticipantForms();
      }
      updateLessonSchedulePanel(root);
      updateLessonStudentsNextButton(root);
    }
    if (step === LESSON_ORDERER_STEP && bookingType === 'lesson') {
      prefillLessonOrdererFromStudent();
      updateLessonOrdererButton();
    }
    if (step === COURSE_ORDERER_STEP && bookingType === 'course') {
      updateCourseOrdererButton();
    }
    if (step === COURSE_STUDENTS_STEP && bookingType === 'course') {
      updateCourseStudentsNextButton(root);
    }
    // Po wyjściu z „Dane zamawiającego” (step 2) → CRM lead (travel / kurs / lekcja).
    if (
      (bookingType === 'travel' || bookingType === 'course' || bookingType === 'lesson')
      && step >= 3
      && step < maxStep
    ) {
      sendBookingLead();
    }
    if (historyMode === 'push') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (step < maxStep) {
      writeStepToUrl(step, historyMode);
    }
    persistDraft({ step });

    if (step >= maxStep) {
      bookingFinished = true;
      root.classList.add('booking-page--complete');
      lockCompletedBookingHistory();
    }

    stepItems.forEach((item) => {
      const progressN = Number(item.dataset.step);
      if (!Number.isFinite(progressN)) return;
      const progressOffset = bookingType === 'travel'
        ? Number(root.dataset.progressOffset ?? 0)
        : 0;
      const panelStep = progressN - progressOffset;
      const clickable = !bookingFinished
        && (panelStep < 1 || (panelStep >= 1 && panelStep < currentStep && panelStep < maxStep));
      item.classList.toggle('is-clickable', clickable);
      if (clickable) item.setAttribute('tabindex', '0');
      else item.removeAttribute('tabindex');
    });
    updateLessonOrdererButton();
    updateCourseOrdererButton();
    if (bookingType === 'travel') updateTravelCtas();
  };

  const openPingwinPackageAgeForBlockingCards = (rootEl) => {
    const cfg = parsePingwinPackageAgeConfig(rootEl);
    if (!resolvePingwinPackageAgeEnabled(rootEl, cfg)) return null;
    const asOf = getCourseConfig(rootEl).date_from || travelTripDate(rootEl) || '';
    for (const card of rootEl.querySelectorAll('.booking-course-participant')) {
      const index = card.dataset.studentIndex || '1';
      const birth = card.querySelector(`[name="student-birth-${index}"]`);
      const birthIso = polishBirthToIso(card._birthMask?.value || birth?.value || '');
      if (!birthIso) continue;
      const pkg = card.querySelector('.js-course-student-package:checked');
      if (!pkg) continue;
      const age = ageFromIsoDate(birthIso, asOf);
      const kind = pingwinPackageKind(pkg.value, pkg.dataset.label || '', cfg);
      const scenario = pingwinPackageAgeScenario(age, kind, cfg);
      if (scenario === 'force_ekonomiczny') {
        card._pkgAgeShownKey = '';
        evaluatePingwinPackageAge(card, { openModal: true });
        return card;
      }
      if (scenario === 'ack_ekonomiczny' && card.dataset.packageAgeAck !== '1') {
        card._pkgAgeShownKey = '';
        evaluatePingwinPackageAge(card, { openModal: true });
        return card;
      }
    }
    return null;
  };

  const bounceToInvalidStep = (step) => {
    goToStep(step, { historyMode: currentStep === step ? 'none' : 'replace' });
    window.requestAnimationFrame(() => {
      if (bookingType === 'lesson' && step === LESSON_STUDENTS_STEP) validateLessonStudentsStep(root);
      else if (bookingType === 'course' && step === COURSE_STUDENTS_STEP) {
        validateCourseStudentsStep(root);
        const blockingCard = openPingwinPackageAgeForBlockingCards(root);
        if (blockingCard) {
          scrollToCourseStudentCard(blockingCard);
          return;
        }
      } else validateStep(root, step, bookingType);
      scrollToFirstInvalid(root.querySelector(`[data-booking-step="${step}"]`) || root);
    });
  };

  const submitBooking = async () => {
    if (isSubmitting) return;

    if (bookingType === 'lesson') {
      if (!validateLessonStudentsStep(root)) {
        bounceToInvalidStep(LESSON_STUDENTS_STEP);
        return;
      }
      if (!validateStep(root, LESSON_ORDERER_STEP, bookingType)) {
        bounceToInvalidStep(LESSON_ORDERER_STEP);
        return;
      }
    } else if (bookingType === 'course') {
      if (!validateCourseStudentsStep(root)) {
        bounceToInvalidStep(COURSE_STUDENTS_STEP);
        return;
      }
      if (!validateStep(root, COURSE_ORDERER_STEP, bookingType)) {
        bounceToInvalidStep(COURSE_ORDERER_STEP);
        return;
      }
    } else if (bookingType === 'travel') {
      if (!validateTravelParticipantsStep(root)) {
        bounceToInvalidStep(TRAVEL_PARTICIPANTS_STEP);
        return;
      }
      if (!validateStep(root, TRAVEL_ORDERER_STEP, bookingType)) {
        bounceToInvalidStep(TRAVEL_ORDERER_STEP);
        return;
      }
      if (isAdvancedPricing()) {
        const freshQuote = await fetchTravelQuote().catch(() => null);
        const blocked = freshQuote?.blocked
          ? (ui(freshQuote.message) || TRAVEL_STAY_AGE_ERROR())
          : travelStayBlockedMessage(freshQuote);
        if (blocked) {
          applyTravelStayBlock(blocked);
          window.alert(blocked);
          return;
        }
        if (!freshQuote) {
          window.alert(TRAVEL_STAY_ZERO_ERROR());
          return;
        }
      } else if (travelStayError) {
        window.alert(travelStayError);
        return;
      }
    } else {
      const contactStep = 3;
      if (!validateStep(root, contactStep, bookingType)) {
        bounceToInvalidStep(contactStep);
        return;
      }
    }

    isSubmitting = true;
    let redirectingToThankYou = false;

    const payload = collectBookingPayload(root, bookingType);
    const { restUrl, restNonce } = getBookingApi();
    const turnstileCfg = getTurnstileConfig();
    if (turnstileCfg.enabled) {
      const mount = root.querySelector('.js-turnstile');
      const token = turnstileWidget?.getToken?.()
        || mount?.querySelector?.('[name="cf-turnstile-response"]')?.value
        || '';
      if (!token) {
        isSubmitting = false;
        turnstileWidget?.reveal?.({ scroll: true });
        if (mount && !turnstileWidget) {
          revealTurnstile(mount, { scroll: true });
        }
        window.alert(i18nUi('cart_turnstile', 'Potwierdź, że nie jesteś botem — zaznacz weryfikację poniżej.'));
        return;
      }
      payload['cf-turnstile-response'] = token;
    }

    const actionButtons = [
      submitBtn,
      ...queryBookingAll('.js-booking-sidebar-submit'),
    ].filter(Boolean);
    actionButtons.forEach((btn) => {
      btn.disabled = true;
      btn.dataset.originalLabel = btn.textContent;
      btn.textContent = t('cta_sending', 'Wysyłanie…');
    });

    if (bookingType === 'course') {
      logCrmDebug('booking → POST', restUrl, {
        submissionId: payload.crm_lead_submission_id,
        course: payload.details?.course,
        course_label: payload.details?.course_label,
        package: payload.details?.package,
        date_from: payload.details?.date_from,
        date_to: payload.details?.date_to,
        students: payload.details?.students?.length,
        note: 'crm_course_id is resolved server-side from cennik (SchoolCoursePricing)',
        payload,
      });
    } else if (bookingType === 'lesson') {
      logCrmDebug('booking → POST', restUrl, {
        submissionId: payload.crm_lead_submission_id,
        lesson_schedule: payload.details?.lesson_schedule,
        students: payload.details?.students?.length,
        payload,
      });
    }

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(restNonce ? { 'X-WP-Nonce': restNonce } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (bookingType === 'course' || bookingType === 'lesson') {
        logCrmDebug('booking ← response', {
          type: bookingType,
          status: response.status,
          ok: response.ok,
          reference: data.reference,
          bookingId: data.id,
          body: data,
        });
      }

      if (!response.ok) {
        throw new Error(ui(data.message) || ui('Nie udało się wysłać rezerwacji.'));
      }

      if (refEl && data.reference) {
        refEl.textContent = data.reference;
      } else if (refEl) {
        const prefix = refEl.dataset.prefix
          || (bookingType === 'lesson' ? 'HSKI-LEKCJA' : bookingType === 'course' ? 'HSKI-KURS' : 'HSKI-2026');
        refEl.textContent = `${prefix}-${Math.floor(1000 + Math.random() * 9000)}`;
      }

      if (draftId) {
        removeDraft(draftId);
        draftId = null;
      }

      if (bookingType === 'travel') {
        await populateTravelSuccess();
        const reference = data.reference || refEl?.textContent || '';
        const snapshot = serializeTravelThankYouSnapshot(root, reference);
        saveTravelThankYouSnapshot(snapshot);
        const thankYouHref = buildTravelThankYouUrl(root, snapshot);
        const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (thankYouHref && thankYouHref !== currentHref) {
          redirectingToThankYou = true;
          window.location.assign(thankYouHref);
          return;
        }
      }

      goToStep(maxStep);
    } catch (error) {
      if (bookingType === 'course' || bookingType === 'lesson') {
        logCrmDebug('booking ← error', error);
      }
      turnstileWidget?.reset?.();
      turnstileWidget?.reveal?.({ scroll: true });
      window.alert(ui(error.message) || ui('Wystąpił błąd podczas wysyłania rezerwacji.'));
    } finally {
      if (redirectingToThankYou) return;
      isSubmitting = false;
      actionButtons.forEach((btn) => {
        btn.disabled = false;
        const fallback = bookingType === 'lesson' || bookingType === 'course'
          ? 'Rezerwuj'
          : (btn.classList.contains('js-booking-sidebar-submit') ? 'Zamawiam' : 'Wyślij rezerwację');
        btn.textContent = btn.dataset.originalLabel || fallback;
      });
      if (bookingType === 'travel') syncTravelSidebarCta(currentStep);
    }
  };

  const revealInvalidOnCurrentStep = () => {
    const ok = bookingType === 'lesson' && currentStep === LESSON_STUDENTS_STEP
      ? validateLessonStudentsStep(root)
      : bookingType === 'course' && currentStep === COURSE_STUDENTS_STEP
        ? validateCourseStudentsStep(root)
        : validateStep(root, currentStep, bookingType);
    if (!ok) {
      let blockingCard = null;
      if (bookingType === 'course' && currentStep === COURSE_STUDENTS_STEP) {
        blockingCard = openPingwinPackageAgeForBlockingCards(root);
      }
      window.requestAnimationFrame(() => {
        if (blockingCard) {
          scrollToCourseStudentCard(blockingCard);
          return;
        }
        scrollToFirstInvalid(root.querySelector(`[data-booking-step="${currentStep}"]`) || root);
      });
    }
    return ok;
  };

  const isCurrentBookingStepComplete = () => {
    if (currentStep >= maxStep) return true;
    if (bookingType === 'lesson' && currentStep === LESSON_STUDENTS_STEP) {
      return validateLessonStudentsStep(root, { markInvalid: false });
    }
    if (bookingType === 'course' && currentStep === COURSE_STUDENTS_STEP) {
      return validateCourseStudentsStep(root, { markInvalid: false });
    }
    if ((bookingType === 'lesson' || bookingType === 'course') && currentStep >= maxStep - 1) {
      return true;
    }
    if (bookingType === 'travel' && currentStep === TRAVEL_REVIEW_STEP) {
      return true;
    }
    return validateStep(root, currentStep, bookingType, { markInvalid: false });
  };

  root.querySelectorAll('.js-booking-next').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (bookingType === 'travel' && currentStep === TRAVEL_PARTICIPANTS_STEP) {
        tryAdvanceTravelFromParticipants();
        return;
      }
      if (!revealInvalidOnCurrentStep()) return;
      if (currentStep < maxStep - 1) goToStep(currentStep + 1);
    });
  });

  root.querySelectorAll('.js-booking-prev').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (bookingFinished) return;
      if (currentStep > 1) goToStep(currentStep - 1);
    });
  });

  const progressOffset = bookingType === 'travel'
    ? Number(root.dataset.progressOffset ?? 0)
    : 0;

  const goToProgressItem = (item) => {
    const progressN = Number(item.dataset.step);
    if (!Number.isFinite(progressN) || !item.classList.contains('is-clickable')) return;
    const panelStep = progressN - progressOffset;
    if (panelStep < 1) {
      const offerUrl = root.dataset.offerUrl || '';
      if (offerUrl) window.location.href = offerUrl;
      return;
    }
    if (panelStep >= 1 && panelStep < maxStep) {
      if (
        bookingType === 'travel'
        && currentStep === TRAVEL_PARTICIPANTS_STEP
        && panelStep > TRAVEL_PARTICIPANTS_STEP
      ) {
        if (!revealInvalidOnCurrentStep()) return;
        if (needsCancellationNudge()) {
          openCancelNudge(() => goToStep(panelStep));
          return;
        }
      }
      goToStep(panelStep);
    }
  };

  stepItems.forEach((item) => {
    item.addEventListener('click', () => goToProgressItem(item));
    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      goToProgressItem(item);
    });
  });

  window.addEventListener('popstate', (event) => {
    if (bookingFinished) {
      lockCompletedBookingHistory();
      if (currentStep < maxStep) goToStep(maxStep, { historyMode: 'none' });
      return;
    }
    const fromState = Number(event.state?.bookingStep);
    const next = Number.isFinite(fromState) && fromState >= 1
      ? Math.min(fromState, lastFormStep())
      : (readStepFromUrl() || 1);
    if (next === currentStep) return;
    goToStep(next, { historyMode: 'none' });
  });

  submitBtn?.addEventListener('click', submitBooking);

  root.querySelectorAll('.js-booking-sidebar-submit').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (bookingType !== 'travel') return;
      if (currentStep >= TRAVEL_REVIEW_STEP) {
        submitBooking();
        return;
      }
      if (currentStep === TRAVEL_PARTICIPANTS_STEP) {
        tryAdvanceTravelFromParticipants();
        return;
      }
      if (!revealInvalidOnCurrentStep()) return;
      goToStep(currentStep + 1);
    });
  });

  const schoolStickyCta = () => {
    const activePanel = root.querySelector('.booking-page__panel.is-active');
    const nextBtn = activePanel?.querySelector('.js-booking-next');
    if (nextBtn && currentStep < maxStep - 1) {
      nextBtn.click();
      return;
    }
    const submit = queryBookingOne('.js-booking-submit') || submitBtn;
    if (submit) submit.click();
    else submitBooking();
  };

  const syncInlineSummarySlot = () => {
    const slot = root.querySelector('.js-sticky-summary-inline-slot');
    if (!(slot instanceof HTMLElement)) return;
    const isSchool = bookingType === 'lesson' || bookingType === 'course';
    // Travel: karta tylko na kroku 1 (jak oferta pod galerią).
    // Lekcja / kurs: karta zawsze na dole layoutu aż do potwierdzenia — inaczej po
    // ukryciu kroku 1 wskakiwała nad aktualny panel („Podsumowanie” na górze).
    const showInline = (isSchool ? currentStep < maxStep : currentStep === 1)
      && !root.classList.contains('booking-page--travel-success')
      && !root.classList.contains('booking-page--lesson-confirm');
    slot.hidden = !showInline;
  };

  const initBookingStickySummary = () => {
    if (!sidebarMount) return;
    const isSchool = bookingType === 'lesson' || bookingType === 'course';
    stickySummary = initStickySummaryBar({
      root,
      sidebar: sidebarMount,
      variant: 'course-pick',
      drawerLabel: t('sticky_drawer', 'Szczegóły rezerwacji'),
      totalLabel: t('sticky_total', 'Razem do zapłaty'),
      detailsLabel: t('sticky_details', 'Szczegóły'),
      keepSidebarInline: true,
      drawerSidebarMode: 'relocate',
      // Travel mobile: karta między privacy a FAQ (nie po całym kroku 1).
      inlineMountAfter: isSchool ? '.booking-page__main' : '.travel-step2__privacy',
      autoOpenAtBottom: false,
      isHidden: () => currentStep >= maxStep
        || root.classList.contains('booking-page--travel-success')
        || root.classList.contains('booking-page--lesson-confirm'),
      getTotalText: () => {
        const el = queryBookingOne('.js-booking-total');
        return el?.textContent.replace(/\s+/g, ' ').trim() || '';
      },
      getPriceCaption: () => t('sticky_caption', 'Razem'),
      getCta: () => {
        const muted = !isCurrentBookingStepComplete();
        if (isSchool) {
          return {
            text: currentStep >= maxStep - 1
              ? t('cta_book', 'Rezerwuj')
              : t('cta_next', 'Przejdź dalej'),
            isButton: true,
            hidden: currentStep >= maxStep,
            muted: currentStep < maxStep - 1 && muted,
          };
        }
        return {
          text: currentStep >= TRAVEL_REVIEW_STEP
            ? t('cta_submit', 'Zamawiam')
            : t('cta_next', 'Przejdź dalej'),
          isButton: true,
          hidden: currentStep >= maxStep,
          muted,
        };
      },
      onCtaClick: (event) => {
        event.preventDefault();
        if (isSchool) {
          schoolStickyCta();
          return;
        }
        queryBookingOne('.js-booking-sidebar-submit')?.click();
      },
    });
    syncInlineSummarySlot();
    const observer = new MutationObserver(() => stickySummary.sync());
    observer.observe(sidebarMount, {
      subtree: true,
      childList: true,
      characterData: true,
    });
  };

  const syncMobileBarVisibility = () => {
    stickySummary.sync();
  };

  const addonDrawer = root.querySelector('.js-travel-addon-drawer');
  const addonDrawerTitle = root.querySelector('.js-travel-addon-drawer-title');
  const addonDrawerLead = root.querySelector('.js-travel-addon-drawer-lead');
  const addonDrawerPrice = root.querySelector('.js-travel-addon-drawer-price');
  const addonDrawerAdd = root.querySelector('.js-travel-addon-drawer-add');
  let openAddonType = '';

  const ADDON_TITLES = {
    cancellation: 'Ubezpieczenie od kosztów rezygnacji',
    training: 'Szkolenie narciarskie/snowboardowe',
    nnw: 'Ubezpieczenie NNW + KL',
  };

  const ADDON_LEADS = {
    cancellation:
      'Chroni przed utratą wpłaty w razie odwołania wyjazdu z przyczyn losowych (choroba, wypadek, sytuacja rodzinna).\nPolisa obejmuje całą rezerwację.',
    training:
      'Szkolenie poprowadzone przez najlepszą polską kadrę instruktorów (Mistrzowie Polski Szkół Narciarskich 2024).',
    nnw: 'Polisa od SIGNAL IDUNA — sprawdzony ubezpieczyciel turystyczny. Dedykowana sportom zimowym.',
  };

  const ADDON_PRICE_LABELS = {
    cancellation: 'CENA ZA CAŁE ZAMÓWIENIE',
    training: 'CENA ZA OSOBĘ',
    nnw: 'CENA ZA OSOBĘ',
  };

  const ADDON_MODAL_SECTIONS = {
    cancellation: [
      {
        title: 'Kiedy zadziała',
        content_type: 'list',
        items: [
          { text: 'Nagła choroba lub wypadek uczestnika lub bliskiego' },
          { text: 'Konieczność hospitalizacji' },
          { text: 'Wypadek komunikacyjny w drodze na wyjazd' },
          { text: 'Wezwanie do sądu lub innej instytucji' },
          { text: 'Utrata pracy' },
        ],
      },
      {
        title: 'Zakres pokrycia ubezpieczenia',
        content_type: 'bullets',
        items: [
          {
            html: true,
            text: 'Polisa zwraca <strong>100% kosztów wyjazdu</strong> minus 10% udziału własnego.',
          },
        ],
      },
    ],
    training: [
      {
        title: 'Co dostajesz',
        content_type: 'list',
        items: [
          { text: '4 dni szkolenia po 2h dziennie' },
          { text: 'Grupy max. 6 osób, dobierane wg poziomu' },
          { text: 'Dyscyplina: narty lub snowboard' },
          { text: 'Język instruktora: PL/EN/DE/RU' },
          { text: 'Certyfikat ukończenia kursu' },
        ],
      },
      {
        title: 'Dla kogo',
        content_type: 'text',
        text: 'Każdy poziom - od początkujących do zaawansowanych. Dzieci od 4 lat (Klub Pingwina).',
      },
      {
        title: 'Co należy mieć',
        content_type: 'list',
        items: [
          { text: 'Własny sprzęt narciarski lub snowboard (lub z wypożyczalni na miejscu)' },
          { text: 'Kask (obowiązkowo dla dzieci)' },
          { text: 'Skipass - w cenie tej oferty' },
        ],
      },
    ],
    nnw: [
      {
        title: 'Co obejmuje',
        content_type: 'list',
        items: [
          { text: 'Koszty leczenia (KL) — do 100 000 EUR' },
          { text: 'Następstwa nieszczęśliwych wypadków (NNW) — do 30 000 PLN' },
          { text: 'Pomoc assistance 24/7' },
          { text: 'Sporty zimowe (narty, snowboard) w zakresie' },
          { text: 'Transport medyczny i ratownictwo na stoku' },
        ],
      },
      {
        title: 'Czego nie obejmuje',
        content_type: 'bullets',
        items: [
          { text: 'Szkód umyślnych' },
          { text: 'Sportów ekstremalnych (heli-skiing itp.)' },
        ],
      },
    ],
  };

  const getTravelAddon = (slug) => (travelAddonsConfig.addons || []).find((item) => item.slug === slug);

  const formatModalPrice = (value) => {
    const amount = Number(value) || 0;
    if (Number.isInteger(amount)) return `${amount} zł`;
    return `${amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
  };

  const addonModalPrice = (type, addon) => {
    const fallback = {
      cancellation: cancellationPrice,
      training: trainingPrice,
      nnw: nnwPrice,
    }[type];
    const amount = Number(addon?.price);
    return Number.isFinite(amount) && amount > 0 ? amount : fallback;
  };

  const renderLeadHtml = (lead) => String(lead || '')
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `<p>${escapeHtml(part)}</p>`)
    .join('');

  const sectionUsesBullets = (section) => {
    if (section.content_type === 'bullets') return true;
    const title = String(section.title || '').toLowerCase();
    return /nie obejmuje|zakres pokrycia/.test(title);
  };

  const renderAddonListItems = (section, asBullets) => {
    const checkSrc = getBookingAsset('icon-check-green.svg');
    return (section.items || [])
      .map((item) => {
        const text = item?.html ? String(item.text || '') : escapeHtml(item?.text || '');
        if (!text) return '';
        if (asBullets) return `<li>${text}</li>`;
        return `<li class="travel-addon-modal__check">
          <span class="travel-addon-modal__check-icon" aria-hidden="true">
            <img src="${escapeHtml(checkSrc)}" alt="" width="16" height="16">
          </span>
          <span class="travel-addon-modal__check-text">${text}</span>
        </li>`;
      })
      .join('');
  };

  const renderAddonModalSection = (section) => {
    if (!section || typeof section !== 'object') return '';
    const asBullets = sectionUsesBullets(section);
    const view = {
      ...section,
      title: section.title ? ui(section.title) : section.title,
      text: section.text ? ui(section.text) : section.text,
      items: Array.isArray(section.items)
        ? section.items.map((item) => (
          item && typeof item === 'object' && item.text
            ? { ...item, text: ui(item.text) }
            : item
        ))
        : section.items,
    };
    const title = view.title
      ? `<h4 class="travel-addon-modal__section-title">${escapeHtml(view.title)}</h4>`
      : '';
    if (view.content_type === 'rich_text' && view.body) {
      return `<section class="travel-addon-modal__section">${title}<div class="travel-addon-modal__section-body">${view.body}</div></section>`;
    }
    if (view.content_type === 'text' && view.text) {
      return `<section class="travel-addon-modal__section">${title}<p class="travel-addon-modal__section-text">${escapeHtml(view.text)}</p></section>`;
    }
    const items = renderAddonListItems(view, asBullets);
    if (!items) return title ? `<section class="travel-addon-modal__section">${title}</section>` : '';
    const listClass = asBullets ? 'travel-addon-modal__bullets' : 'travel-addon-modal__checks';
    return `<section class="travel-addon-modal__section">${title}<ul class="${listClass}">${items}</ul></section>`;
  };

  const openAddonDrawer = (type) => {
    if (!addonDrawer || !type) return;
    openAddonType = type;
    const addon = getTravelAddon(type);
    const fallbacks = ADDON_MODAL_SECTIONS[type] || [];
    const activePanel = root.querySelector(`.js-travel-addon-panel[data-addon="${type}"]`);
    if (addonDrawerTitle) {
      addonDrawerTitle.textContent = ui(
        addon?.modal?.title || activePanel?.dataset.title || ADDON_TITLES[type] || 'Szczegóły',
      );
    }
    if (addonDrawerLead) {
      const lead = addon?.modal?.lead || activePanel?.dataset.lead || ADDON_LEADS[type] || '';
      addonDrawerLead.innerHTML = renderLeadHtml(ui(lead));
    }
    if (addonDrawerPrice) {
      const label = ui(addon?.modal?.price_label || ADDON_PRICE_LABELS[type] || '').trim();
      const amount = addonModalPrice(type, addon);
      if (label && amount != null) {
        addonDrawerPrice.hidden = false;
        addonDrawerPrice.innerHTML = `
          <p class="travel-addon-modal__price-label">${escapeHtml(label)}</p>
          <p class="travel-addon-modal__price-value">${escapeHtml(formatModalPrice(amount))}</p>
        `;
      } else {
        addonDrawerPrice.hidden = true;
        addonDrawerPrice.innerHTML = '';
      }
    }
    const sectionsEl = root.querySelector('.js-travel-addon-drawer-sections');
    if (sectionsEl) {
      const cmsSections = Array.isArray(addon?.modal?.sections) ? addon.modal.sections : [];
      const hasCmsContent = cmsSections.some((section) => (
        (section.items || []).length
        || String(section.text || '').trim()
        || String(section.body || '').trim()
      ));
      const sections = hasCmsContent ? cmsSections : fallbacks;
      sectionsEl.innerHTML = sections.map(renderAddonModalSection).join('');
    }
    if (addonDrawerAdd) {
      const showAdd = type === 'cancellation';
      addonDrawerAdd.hidden = !showAdd;
      if (showAdd) {
        const added = isBookingCancellationOn();
        addonDrawerAdd.textContent = added ? ui('Dodano') : ui('Dodaj do zamówienia');
        addonDrawerAdd.classList.toggle('is-added', added);
      }
    }
    addonDrawer.hidden = false;
    requestAnimationFrame(() => addonDrawer.classList.add('is-open'));
    document.documentElement.classList.add('booking-addon-drawer-open');
  };

  const closeAddonDrawer = () => {
    if (!addonDrawer) return;
    openAddonType = '';
    addonDrawer.classList.remove('is-open');
    addonDrawer.hidden = true;
    document.documentElement.classList.remove('booking-addon-drawer-open');
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (cancelNudgeModal && !cancelNudgeModal.hidden) {
      closeCancelNudge({ continueFlow: false });
      return;
    }
    if (nudgeModal && !nudgeModal.hidden) {
      closeTrainingNudge();
      return;
    }
    if (addonDrawer && !addonDrawer.hidden) {
      closeAddonDrawer();
    }
  });

  root.addEventListener('click', (event) => {
    const detailsBtn = event.target.closest('[data-addon-details]');
    if (!detailsBtn || !root.contains(detailsBtn)) return;
    event.preventDefault();
    openAddonDrawer(detailsBtn.dataset.addonDetails);
  });

  root.querySelectorAll('.js-travel-addon-drawer-close').forEach((el) => {
    el.addEventListener('click', closeAddonDrawer);
  });

  addonDrawerAdd?.addEventListener('click', () => {
    if (openAddonType === 'cancellation') {
      setBookingCancellation(true);
      updatePrice();
      updateSummary();
      schedulePersist();
    }
    closeAddonDrawer();
  });

  initBookingStickySummary();
  syncMobileBarVisibility();
  captureBookingAttributionFromUrl();

  [adultsSelect, childrenSelect].forEach((el) => {
    el?.addEventListener('change', () => {
      if (bookingType === 'travel') syncTravelParticipantForms();
      updatePrice();
      updateSummary();
    });
  });

  root.querySelector('.js-booking-cancellation-toggle')?.addEventListener('click', () => {
    setBookingCancellation(!isBookingCancellationOn());
    updatePrice();
    updateSummary();
    schedulePersist();
  });

  root.querySelector('.js-booking-cancellation-decline')?.addEventListener('change', (event) => {
    if (event.target.checked) {
      setBookingCancellation(false);
      updatePrice();
      updateSummary();
    }
    schedulePersist();
  });

  root.querySelector('.js-booking-step2-privacy')?.addEventListener('change', () => {
    schedulePersist();
  });

  root.querySelectorAll('.js-booking-adults-visible, .js-booking-children-visible').forEach((select) => {
    select.addEventListener('change', () => {
      const target = select.dataset.sync === 'children' ? childrenSelect : adultsSelect;
      if (target) target.value = select.value;
      if (bookingType === 'travel') syncTravelParticipantForms();
      updatePrice();
      updateSummary();
      schedulePersist();
    });
  });

  root.querySelector('.js-booking-is-participant')?.addEventListener('change', () => {
    if (root.querySelector('.js-booking-is-participant')?.checked) {
      copyOrdererToFirstParticipant();
    }
    schedulePersist();
  });

  root.querySelectorAll('.js-booking-lesson-type, .js-booking-lesson-participants, .js-booking-lesson-hours').forEach((input) => {
    input.addEventListener('change', () => {
      syncLessonScheduleVisibility(root);
      if (input.classList.contains('js-booking-lesson-type') || input.classList.contains('js-booking-lesson-participants')) {
        rebuildLessonHoursOptions();
      }
      if (input.classList.contains('js-booking-lesson-participants')) {
        syncLessonParticipantForms();
      }
      updateLessonSchedulePanel(root);
      updateLessonSummary();
    });
  });

  root.querySelector('.js-booking-add-hour')?.addEventListener('click', () => {
    const schedule = root.querySelector('.js-booking-lesson-schedule');
    const slots = schedule?.querySelector('.js-booking-lesson-times');
    if (!slots || slots.querySelectorAll('.js-booking-lesson-time').length >= 3) return;

    const row = document.createElement('label');
    row.className = 'booking-form__field';
    row.innerHTML = `
      <span>Dodatkowa godzina</span>
      <select name="lesson-time-extra" class="js-booking-lesson-time">
        <option value="09:00">09:00</option>
        <option value="11:00">11:00</option>
        <option value="13:00">13:00</option>
        <option value="15:00">15:00</option>
      </select>
    `;
    slots.appendChild(row);
  });

  root.querySelector('.js-booking-ferie-toggle')?.addEventListener('click', (event) => {
    const button = event.currentTarget;
    const panel = button.nextElementSibling;
    const isOpen = button.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(isOpen));
    if (panel) panel.hidden = !isOpen;
  });

  root.querySelector('.js-booking-add-participant')?.addEventListener('click', () => {
    const wrap = root.querySelector('.js-booking-course-participants-wrap');
    if (!wrap || bookingType !== 'course' || participantCount >= 3) return;

    const config = getCourseConfig(root);
    const defaultPackage = resolveDefaultCoursePackage(config);

    participantCount += 1;
    const card = buildCourseStudentCard(participantCount, defaultPackage, coursePackages, getCourseCalendarIcon());
    wrap.appendChild(card);
    bindCourseStudentCard(card);
    updateCourseStudentsCount(root);
    syncCourseStudentRemoveButtons(root);
    updatePrice();
    updateSummary();
    updateCourseSummary();
    updateCourseStudentsNextButton(root);
  });

  root.addEventListener('click', (event) => {
    const addBtn = event.target.closest('.js-booking-add-lesson-participant');
    if (!addBtn || !root.contains(addBtn)) return;
    event.preventDefault();
    if (bookingType !== 'lesson') return;

    const wrap = root.querySelector('.js-booking-lesson-participants-wrap');
    if (!wrap) return;
    const current = wrap.querySelectorAll('.booking-lesson-participant').length;
    if (current >= LESSON_MAX_PARTICIPANTS) return;

    const next = current + 1;
    const peopleInput = root.querySelector('.js-booking-lesson-participants');
    if (peopleInput) peopleInput.value = String(next);
    root.dataset.lessonParticipants = String(next);

    const existingType = wrap.querySelector('.booking-lesson-participant [name^="student-type-"]')?.value
      || defaultLessonStudentType(root);
    wrap.appendChild(buildLessonStudentCard(root, next, { studentType: existingType }));
    reindexLessonStudentCards(wrap);
    syncLessonStudentRemoveButtons(root);
    updateLessonStudentsCount(root);
    updateLessonAddParticipantButton(root);
    updateLessonSchedulePanel(root);
    updateLessonSummary();
    updateLessonStudentsNextButton(root);
    updatePrice();
    schedulePersist();
  });

  root.querySelector('[data-booking-step="1"]')?.addEventListener('input', (event) => {
    clearBookingFieldIfValid(event.target);
    updateSummary();
    if (bookingType === 'lesson') updateLessonStudentsNextButton(root);
    if (bookingType === 'course') {
      updateCourseStudentsNextButton(root);
      updateCourseSummary();
    }
    if (bookingType === 'travel') {
      schedulePersist();
      updateTravelCtas();
    }
  });
  root.querySelector('[data-booking-step="1"]')?.addEventListener('change', (event) => {
    const field = event.target;
    if (field?.matches?.('input[type="checkbox"]') && field.checked) {
      setBookingFieldInvalid(field, false);
    }
    clearBookingFieldIfValid(field);
    if (bookingType === 'lesson') updateLessonStudentsNextButton(root);
    if (bookingType === 'course') {
      updateCourseStudentsNextButton(root);
      updateCourseSummary();
    }
    if (bookingType === 'travel') updateTravelCtas();
  });
  root.querySelector('[data-booking-step="2"]')?.addEventListener('input', (event) => {
    clearBookingFieldIfValid(event.target);
    updateSummary();
    if (bookingType === 'lesson') updateLessonOrdererButton();
    if (bookingType === 'course') {
      updateCourseOrdererButton();
      updateCourseSummary();
    }
    if (bookingType === 'travel') {
      schedulePersist();
      updateTravelCtas();
    }
  });
  root.querySelector('[data-booking-step="2"]')?.addEventListener('change', (event) => {
    const field = event.target;
    if (field?.matches?.('input[type="checkbox"]') && field.checked) {
      setBookingFieldInvalid(field, false);
    }
    clearBookingFieldIfValid(field);
    if (bookingType === 'lesson') updateLessonOrdererButton();
    if (bookingType === 'course') {
      updateCourseOrdererButton();
      updateCourseSummary();
    }
    if (bookingType === 'travel') updateTravelCtas();
  });
  root.querySelector('[data-booking-step="3"]')?.addEventListener('input', updateSummary);

  if (bookingType === 'course') {
    applyCourseUrlParams();
    bindCourseTypeSelect();
    initCourseReview(root);
    initPingwinPackageAgeModal(root);
    updateCourseStudentsNextButton(root);
    updateCourseOrdererButton();
    initBookingLegalModals(root);
  } else if (bookingType === 'lesson') {
    applyLessonUrlParams();
    initLessonSchedule(root);
    initLessonReview(root);
    initLessonMixModal(root);
    initBookingLegalModals(root);
    updateLessonSummary();
    updateLessonStudentsNextButton(root);
    updateLessonAddParticipantButton(root);
    updateLessonOrdererButton();
  } else if (bookingType === 'travel') {
    applyTravelUrlParams();
    initTravelReview(root);
    syncTravelSidebarCta(1);
    updatePrice();
    updateSummary();
  } else {
    updatePrice();
  }

  const turnstileMount = root.querySelector('.js-turnstile');
  if (turnstileMount && getTurnstileConfig().enabled) {
    mountTurnstile(turnstileMount).then((widget) => {
      turnstileWidget = widget;
    }).catch(() => {
      turnstileWidget = null;
    });
  }

  const thankYouParams = new URLSearchParams(window.location.search);
  const thankYouMode = bookingType === 'travel' && isTravelThankYouView(root);
  const thankYouSnapshot = thankYouMode ? readTravelThankYouSnapshot() : null;

  if (thankYouMode) {
    if (thankYouSnapshot) {
      applyTravelThankYouSnapshot(root, thankYouSnapshot);
    } else if (refEl && thankYouParams.get('ref')) {
      refEl.textContent = thankYouParams.get('ref');
    }
    goToStep(maxStep, { historyMode: 'replace' });
    root.addEventListener('input', schedulePersist);
    root.addEventListener('change', schedulePersist);
    return;
  }

  const draftStep = (readStepFromUrl() || readDraftIdFromUrl())
    ? hydrateFromDraft()
    : null;
  if (bookingType === 'travel') {
    bindTravelOrdererBirth();
  }
  initPhonePrefixSelect(root);
  if (draftStep != null) {
    persistDraft();
  }
  syncMobileBarVisibility();

  const urlStep = readStepFromUrl();
  let initialStep = urlStep || 1;
  // Obsada z oferty zmieniła się względem koszyka — wróć do listy uczestników.
  if (bookingType === 'travel' && travelOccupancyChanged) {
    initialStep = Math.min(initialStep, 2);
  }
  goToStep(initialStep, { historyMode: 'replace' });

  root.addEventListener('input', schedulePersist);
  root.addEventListener('change', schedulePersist);
}

export default function initBooking() {
  document.querySelectorAll('.js-booking').forEach(initBookingRoot);
}
